import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, RefreshCw, Upload } from "lucide-react";
import { getApiBaseUrl } from "../lib/apiClient";
import { getVatRate } from "../lib/settingsApi";
import { useOptionalNotifications } from "../context/NotificationContext";
import EditableCell from "../components/EditableCell";
import { Modal } from "../components/Modal";
import { ModuleInfoButton } from "../components/ModuleInfoButton";
import LoadingState from "../components/LoadingState";
import Button from "../components/ui/Button";
import Pagination from "../components/ui/Pagination";
import SearchInput from "../components/ui/SearchInput";
import { EmptyState } from "../components/ui/States";
import CatalogueSyncBanner, { type ConfirmPackingAlert } from "../features/vendorInvoices/CatalogueSyncBanner";
import {
  listVendorInvoices,
  // V5: a link to one invoice loads that record itself (the list page may not hold it).
  getVendorInvoiceById,
  patchVendorInvoice,
  reprocessVendorInvoice,
  markVendorInvoiceVerified,
  confirmVendorInvoiceInventory,
  deleteVendorInvoice,
  uploadVendorInvoiceSingle,
  uploadVendorInvoicePages,
  reconcileVendorInvoice,
  fetchVendorInvoiceAttachmentUrl,
  assignVendorInvoiceVessel,
  clearVendorInvoiceAssignment,
  listVesselStickerMappings,
  suggestStickerColorForVessel,
  splitVendorInvoiceByVessel,
  getVendorInvoiceVesselSplit,
  clearVendorInvoiceVesselSplit,
  allocateVendorInvoiceToVessel,
  vesselSendMovedStock,
  vesselSendState,
  vesselSendLabel,
  VendorInvoice,
  VendorInvoiceStatus,
  VesselStickerMapping,
  VendorInvoiceSplitSpec,
  SplitLineAllocation,
  VesselAllocationResult,
  vesselAssignmentErrorMessage,
  type VendorInvoiceSortKey,
  type SortDir,
  type ConfirmInventoryResult,
  // The change card: what Confirm & sync will change in the Master Catalogue,
  // from a dry run of the sync itself.
  getVendorInvoiceSyncPreview,
  type SyncPreview,
  // USC-BUG-0021 — the Remove button on a line of an invoice under review.
  removeVendorInvoiceLine,
} from "../lib/vendorInvoiceApi";
import SyncPreviewPanel from "../features/vendorInvoices/SyncPreviewPanel";
import { categoryChoicesToSend, startingCategoryChoices } from "../features/vendorInvoices/categoryChoices";
import DuplicateInvoiceComparison from "../features/vendorInvoices/DuplicateInvoiceComparison";
// V5: which invoice the address names, and the address an open invoice has.
import { invoiceIdFromLocation, invoicePath } from "../features/vendorInvoices/invoiceLink";
import { notAttachedMessage } from "../features/vendorInvoices/poAttachNote";
// B6 — the client order a vendor invoice's goods were bought for.
import { ConfirmedOrderSearchSelect } from "../components/ConfirmedOrderSearchSelect";
import { COLOR_SWATCH } from "../components/StickerColorDot";
import { fetchConfirmedOrderSummary, type ConfirmedOrderDto } from "../lib/confirmedOrderApi";
// One owner for how an order and its client are LABELLED, shared with the three
// list screens that show the same order at different stages.
import { orderRef, clientDisplayName } from "../utils/orderIdentity";
import {
  getLineItemFlags,
  type GateIssue,
  flagCellClass,
  countFlags,
  collectFlaggedCells,
  isEmptyValue,
  reviewInvoice,
  pageContextOf,
  arithmeticIssues,
  rowBadge,
  nonArithmeticIssues,
  totalsNote,
  totalsEvidenceNote,
  uncheckedTotals,
  TOTAL_LABELS,
  worstFlag,
  describeLineTotalConflict,
  REQUIRED_LINE_CELL_KEYS,
  MONEY_REQUIRED_CELL_KEYS,
  CellFlag,
} from "../utils/lineItemFlags";
// The legend was three hand-written swatches whose Tailwind strings restated
// `flagCellClass`'s. Shared now, and painted by that same function, so the
// quotation grid cannot drift from this one — or either from the cells.
import { CellHighlightLegend, HighlightSwatch } from "../components/CellHighlightLegend";
import { normalizeUnit, repairUnitOcr, isKnownUnit, unitFromSizeText, canonicalUnit, isDimensionUnit } from "../utils/unitVocabulary";
import { packFromWords, parseUnitSize } from "../utils/packConversion";
import { cleanItemName, splitItemName } from "../utils/itemName";
import { deriveDueDate, formatDdMmYyyy, parseInvoiceDate } from "../utils/paymentTerms";
import { planZeroTaxRepairs } from "../utils/lineTax";
import { formatCurrency } from "../utils/priceUtils";
import {
  TAX_LINE_FIELDS,
  PACK_LINE_FIELDS,
  packMathTotalUnits,
  impliedPackCount,
  impliedUnitSize,
  // Whether a line's Unit Size ("20ct*1.5oz") still owes an answer — the same
  // call the confirm gate refuses on (prod USC-BUG-0030).
  lineUnitSize,
  statedTotals,
  // The Pack Unit a line starts with, from the printed cell — the processor's
  // rule, for the lines `backfillSplitFields` heals.
  seedPackUnit,
  // D65 — the Units / Case guard: which lines lock the cell at 1, and which
  // ones still owe a proof or a person's confirmation before their stated
  // count is trusted. Same functions the server's Confirm & sync gate reads
  // through `missingStockCells`.
  linePackUnit,
  packCountLocked,
  packCountGuardIssue,
  // The owner's number reader (thousands separators, accounting parens,
  // currency marks) — used only where the Units / Case lock has to agree with
  // the server's `packCountGuardIssue`/`missingStockCells`, which read a
  // stored count through this, not the page's bare-`Number` `toNum` below.
  num,
  // D83 (prod vendor-invoice 3723225809, Blue Waters): the Discount cell's
  // display is the line's total discount in money, not the printed per-case
  // or percent figure `discount` stores.
  lineDiscountAmount,
  // For the "as printed" hint: a PERCENT-proven line's raw `discount` is a
  // rate, not money — the hint has to say "10% as printed", never format it
  // as currency.
  computeLineMath,
} from "../utils/invoiceLineMath";
// USC-BUG-0030 — the question a "20ct*1.5oz" Unit Size puts to the reviewer.
import UnitSizeReadingPrompt from "../features/vendorInvoices/UnitSizeReadingPrompt";
import UploadPageList from "../features/vendorInvoices/UploadPageList";
import PaymentOnReceiptFields from "../features/vendorInvoices/PaymentOnReceiptFields";
import PaymentOnReceiptCard from "../features/vendorInvoices/PaymentOnReceiptCard";
import SplitInvoiceQuestion from "../features/vendorInvoices/SplitInvoiceQuestion";
import { splitQuestionOpen } from "../utils/splitQuestion";
import DestinationCard from "../features/vendorInvoices/DestinationCard";
import DestinationPicker from "../features/vendorInvoices/DestinationPicker";
import { DEFAULT_DESTINATION, destinationErrorMessage, destinationLabel, destinationOf, destinationWording, isExpenseInvoice, type VendorInvoiceDestination } from "../lib/vendorInvoiceDestinationApi";
import VesselCancelSendButton from "../features/vendorInvoices/VesselCancelSendButton";
import PaymentTermsFlag from "../features/vendorInvoices/PaymentTermsFlag";
import { checkPaymentDraft, EMPTY_PAYMENT_DRAFT, paymentAnswerDialog, type PaymentDraft } from "../features/vendorInvoices/paymentOnReceiptForm";
import { addPickedPages, movePage, pageForUpload, type UploadPage } from "../features/vendorInvoices/uploadPages";
// USC-BUG-0028 — "are these fixed 2 KG packs?", on a catch-weight line.
import FixedPackWeightPrompt, { pendingFixedPackWeightQuestion } from "../features/vendorInvoices/FixedPackWeightPrompt";
// D65 — "is this Units / Case right?", on a pack of one with no size, or a
// count that matches a size printed on the same line.
import PackCountConfirmPrompt from "../features/vendorInvoices/PackCountConfirmPrompt";
import { validatePackUnit } from "../features/vendorInvoices/validatePackUnit";
import { validateUnitSize } from "../utils/validateUnitSize";
// VI-7 (D29) — correct a stocked line's packaging, previewed, for Master Catalogue editors.
import PackagingCorrectionDialog from "../features/vendorInvoices/PackagingCorrectionDialog";
// D107.3 — the ONE supplier dropdown; it also creates a vendor that is not on the list yet.
import { VendorSelect, NO_VENDOR, type VendorSelection } from "../components/VendorSelect";
import { ActionDialog } from "../components/ActionDialog";
import { VendorItemMemoryPanel, memorySearchForLine } from "../components/VendorItemMemoryPanel";
import PackingAlertNote from "../features/vendorInvoices/PackingAlertNote";
// D44 VR-5: a person's quantity correction before stock-in, and the lock after it.
import CorrectedLineNote from "../features/vendorInvoices/CorrectedLineNote";
import { LockedCellValue, StockedQuantityLockNote } from "../features/vendorInvoices/StockedQuantityLock";
import { ProcessingIssueNote, processingIssueText } from "../features/vendorInvoices/processingIssue";
import {
  STOCKED_QUANTITY_LOCK_TITLE,
  isQuantityEditPath,
  isQuantityLocked,
  lineEditOps,
  lineRemovalQuestion,
  newVendorReturnPath,
  printedFiguresOf,
  printedLineFlagFor,
  printedTotalOf,
  refusalOffersVendorReturn,
} from "../features/vendorInvoices/quantityCorrection";
// The existing per-action gate (client/src/hooks/usePermission.ts), built for
// exactly this: hiding individual controls rather than whole routes.
import { usePermission } from "../hooks/usePermission";
import { describeApiError, type DialogContent, type DialogItem } from "../utils/errorPresentation";
import { useConfirm, type ConfirmOptions } from "../components/ui/ConfirmProvider";
import { opensInBrowser } from "../utils/attachmentPreview";

/**
 * VI-6 (D30) — the edits that move an invoice's money: price, tax, pack size,
 * line totals and the stated totals. On an invoice already in Inventory they
 * stay allowed, but stock does not follow them while the bill and the Master
 * Catalogue price do, so Save asks first.
 *
 * QUANTITY is no longer among them: D44 #20 (VR-5) reverses D30 for it. After
 * stock-in a quantity cannot be saved at all (the grid locks the cell and the
 * server refuses it), so asking "Save anyway?" about one would be a question
 * with no true answer.
 *
 * The same fields the server's `touchesArithmetic` and pack math read: the two
 * shared lists plus the line-total spellings and `extracted.totals.*`.
 */
const MONEY_LINE_FIELD_PATH = new RegExp(
  "^extracted\\.lineItems\\.\\d+\\.(" +
    Array.from(new Set([...TAX_LINE_FIELDS, ...PACK_LINE_FIELDS, "discount", "lineTotal", "total", "amount"])).join("|") +
    ")$"
);

export function stockedMoneyEditPaths(
  invoice: { inventoryConfirmedAt?: string | null } | null | undefined,
  ops: { path: string }[]
): string[] {
  if (!invoice?.inventoryConfirmedAt) return [];
  return ops
    .map((op) => op.path)
    .filter((p) => !isQuantityEditPath(p))
    .filter((p) => MONEY_LINE_FIELD_PATH.test(p) || p.startsWith("extracted.totals."));
}

/**
 * The line-item edits whose counterpart the SERVER derives, so the grid has to
 * reload to show what it computed — the other half of the rate/amount pair, and
 * the invoice tax total that follows the lines.
 *
 * Derived from the shared field list rather than spelled out here. This used to
 * be a hand-copied alternation of the server's list, and it was the last copy of
 * it left; widening the server list without also widening this one stops the
 * grid refreshing the very field the widening was for, silently, with the
 * reviewer left reading a stale figure.
 */
const DERIVED_LINE_FIELD_PATH = new RegExp(
  "^extracted\\.lineItems\\.\\d+\\.(" +
    Array.from(new Set([...TAX_LINE_FIELDS, ...PACK_LINE_FIELDS])).join("|") +
    ")$"
);

/**
 * Does a save need a reload to show what the server derived from it? The
 * reasons are commented where it is called; extracted so they can be tested.
 * `derivedPriceChanged`: on a line whose Unit Price was worked out from its
 * printed total, a Line Total edit makes the server work the price out again
 * (vendorInvoice.service `linkDerivedUnitPrice`) — and `lineTotal` is in
 * neither shared field list, so only the server can say so.
 */
export function saveNeedsReload(
  ops: { path: string }[],
  touchesProfiledLine: boolean,
  saved?: {
    memoryApplied?: boolean;
    unitSizeQuestionsMayHaveChanged?: boolean;
    derivedPriceChanged?: boolean;
  } | null
): boolean {
  const derivesSomething = ops.some(
    ({ path }) =>
      DERIVED_LINE_FIELD_PATH.test(path) || /^extracted\.invoice\.(invoiceDate|paymentTerms)$/.test(path)
  );
  return (
    derivesSomething ||
    touchesProfiledLine ||
    !!saved?.memoryApplied ||
    !!saved?.unitSizeQuestionsMayHaveChanged ||
    !!saved?.derivedPriceChanged
  );
}

/**
 * Some builds of the API client threw the whole response body as the error
 * message, so `{"success":false,"message":"..."}` could land in a dialog verbatim.
 * Pull the human sentence out rather than showing the envelope.
 */
function unwrapErrorMessage(raw: unknown): string {
  const text = String(raw ?? "").trim();
  if (!text) return "Could not delete this invoice. Please try again.";
  if (!text.startsWith("{") && !text.startsWith("[")) return text;
  try {
    const parsed = JSON.parse(text);
    const msg = parsed?.message ?? parsed?.error?.message ?? parsed?.error;
    return typeof msg === "string" && msg.trim() ? msg.trim() : text;
  } catch {
    return text;
  }
}

// D1 — one destination vessel in the split modal.
//
// `id` is a stable handle rather than an array position. Removing a vessel used
// to be impossible because every line's allocation was keyed on the position;
// with an id, a removed row simply leaves allocations nothing points at.
//
// `allocationId` is the server's id for a SAVED row. Null until the split has
// been saved, which is what gates the per-row "Send to inventory" button: there
// is nothing for the warehouse to act on until the plan is written down.
type SplitRow = {
  id: string;
  vessel: string;
  color: string;
  label: string;
  allocationId: string | null;
  sentAt: string | null;
  // D113.1 — the latest send's line counts; null = legacy stamp or never sent.
  // Read only through rowSendState, never compared here.
  sentLines: number | null;
  linesToSend: number | null;
};

/** D113.1 — a modal row's send state, decided by the one owner (vesselSendState). */
function rowSendState(r: SplitRow) {
  return vesselSendState({
    inventorySentAt: r.sentAt,
    inventorySentLines: r.sentLines,
    inventoryLinesToSend: r.linesToSend,
  });
}

// How one line is divided. "whole" is the original D1 behaviour — the line goes
// to a single vessel untouched, and nothing is recalculated.
type SplitBasis = "whole" | "packs" | "units";

// Row tints for vessels that have no sticker colour yet. Taken from
// COLOR_SWATCH so a row that later gets a real sticker stays in the same
// family, and so this file has exactly one palette in it.
const SPLIT_FALLBACK_TINTS = [
  COLOR_SWATCH.blue,
  COLOR_SWATCH.green,
  COLOR_SWATCH.purple,
  COLOR_SWATCH.orange,
  COLOR_SWATCH.teal,
  COLOR_SWATCH.pink,
  COLOR_SWATCH.yellow,
  COLOR_SWATCH.brown,
];

// Case weights are a catch-weight concern: meat, poultry and seafood, and only
// a handful of lines even on those invoices. As a permanent column it charges
// every reviewer of every invoice 190px for a cell they will never fill in.
// Hidden until the feature is designed properly — nothing behind it is
// switched off. The extractor still reads the grid, the processor still stamps
// caseWeights/caseWeightCheck onto the line, validation still raises its
// (warn-only) flags, and the Stock Received panel further down the page still
// edits the weights of goods already received. What is gone is the up-front
// column in the line-item grid.
const SHOW_CASE_WEIGHTS_COLUMN = false;

function splitRowTint(row: SplitRow, idx: number): string {
  const c = (row.color || "").toLowerCase();
  return COLOR_SWATCH[c] || SPLIT_FALLBACK_TINTS[idx % SPLIT_FALLBACK_TINTS.length];
}

function toNum(v: any): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * D65: why the Units / Case cell is locked at 1 on this row, or undefined
 * when it is not.
 *
 * Locked exactly where `packCountLocked` says the pack count is one by
 * definition (a PCS, a KG...) AND the stored value is blank or already 1 —
 * the second half is what keeps this from hiding an error: a PCS somehow
 * carrying a stored 24 is impossible on its face (D65 rule 1) and has to stay
 * EDITABLE so the reviewer can clear it, not sit behind a lock showing "1"
 * while the real, wrong figure is invisible underneath.
 *
 * What the sentence calls the unit is the vocabulary's own classification, the
 * one the Pack Unit flag reads (`slotTypeIssues` -> `isDimensionUnit`): a weight,
 * volume or length unit is a MEASURE ("one LBS is one LBS"), a piece or a bottle
 * is ONE ITEM. This used to say "one item" for both, so loose produce billed in
 * LBS read "a measure, not a container" on its Pack Unit cell and "A LBS is one
 * item" on the cell beside it (lane 5, V2).
 */
export function packCountLockTitle(row: any): string | undefined {
  if (!packCountLocked(row)) return undefined;
  // `num`, not the page's `toNum`: a stored "1,000" the server's own
  // `packCountGuardIssue`/`missingStockCells` reads (and refuses) as 1000
  // must not read as null here — that locked the cell over a stored count
  // the reviewer then had no way to clear (D65 rule 1).
  const stored = num(row?.splitPackCount);
  if (stored !== null && stored !== 1) return undefined;
  const unit = linePackUnit(row) || "This unit";
  const kind = isDimensionUnit(unit) ? "a measure" : "one item";
  return (
    `A ${unit} is ${kind}, so Units / Case is 1. If the invoice sold these in packs, ` +
    `change the Pack Unit to CASE or BOX first.`
  );
}

/**
 * How much of a line there is, counted in the basis being split by.
 *
 * Mirrors lineTotalInBasis in src/utils/vendorInvoiceSplit.js — the
 * remainder shown while typing has to agree with the sum the server checks, or
 * the form reads as complete and the request is rejected.
 */
function lineTotalForBasis(li: any, basis: SplitBasis): number | null {
  if (basis === "units") return toNum(li?.splitItemUnits) ?? toNum(li?.itemUnits);
  return toNum(li?.splitQuantity) ?? toNum(li?.qty) ?? toNum(li?.quantity);
}

/**
 * Turn the split form's state into the payload the server takes.
 *
 * Pure, and at module scope, because two callers need the SAME answer: the form
 * builds it to send, and the loader builds it to record what "saved" looks like.
 * If those two ever disagreed, a freshly-loaded split would read as unsaved and
 * its "Send to inventory" buttons would stay disabled with nothing to fix.
 */
function buildSplitSpecs(
  lineItems: any[],
  rows: SplitRow[],
  basis: Record<number, SplitBasis>,
  whole: Record<number, string>,
  qty: Record<string, string>
): VendorInvoiceSplitSpec[] {
  return rows.map((r, i) => {
    const lineIndices: number[] = [];
    const lines: SplitLineAllocation[] = [];
    lineItems.forEach((_, idx) => {
      const b = basis[idx] || "whole";
      if (b === "whole") {
        if (whole[idx] === r.id) lineIndices.push(idx);
        return;
      }
      const q = toNum(qty[`${idx}:${r.id}`] ?? "");
      if (q !== null && q > 0) lines.push({ index: idx, quantity: q, basis: b });
    });
    const vessel = r.vessel.trim();
    return {
      vesselName: vessel,
      stickerColor: r.color.trim() || undefined,
      splitLabel: r.label.trim() || vessel || `Vessel ${i + 1}`,
      ...(lineIndices.length ? { lineIndices } : {}),
      ...(lines.length ? { lines } : {}),
    };
  });
}

// Module 1 — Vessel + sticker pill (clickable, opens assign modal)
function VesselPill({
  name,
  color,
  title,
  onClick,
}: {
  name: string;
  color?: string | null;
  title: string;
  onClick?: () => void;
}) {
  const c = (color || "").toLowerCase();
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-slate-200 bg-white text-xs text-slate-700 hover:bg-slate-50 max-w-full"
      title={title}
    >
      <span
        className="inline-block h-2.5 w-2.5 rounded-full border border-slate-300 shrink-0"
        style={{ backgroundColor: COLOR_SWATCH[c] || "#ccc" }}
      />
      <span className="font-medium truncate max-w-[150px]">{name}</span>
    </button>
  );
}

/**
 * Which vessels this invoice's goods go to.
 *
 * A split invoice has several, and this is the only place they show: the
 * invoice is never cloned, so there is no second row to read them off. Clicking
 * one reopens the split, because that is where a destination is changed.
 */
export function VesselCell({
  invoice,
  onAssign,
  onSplit,
}: {
  invoice: VendorInvoice;
  onAssign?: () => void;
  onSplit?: () => void;
}) {
  const allocations = Array.isArray(invoice.vesselAllocations) ? invoice.vesselAllocations : [];

  if (allocations.length) {
    // D113.1 — "sent" counts only vessels with every line out; a vessel with
    // some lines unsent is reported as partly sent, not folded into either.
    const states = allocations.map((a) => vesselSendState(a));
    const sent = states.filter((s) => s.kind === "sent").length;
    const partly = states.filter((s) => s.kind === "partly").length;
    return (
      <div className="flex flex-wrap items-center gap-1">
        {allocations.map((a, i) => (
          <VesselPill
            key={a._id}
            name={a.vesselName}
            color={a.stickerColor}
            title={
              `${a.vesselName}${a.label ? ` — ${a.label}` : ""} · ` +
              `${a.lines?.length ?? 0} line${(a.lines?.length ?? 0) === 1 ? "" : "s"} · ` +
              vesselSendLabel(states[i]) +
              " (click to edit the split)"
            }
            onClick={onSplit}
          />
        ))}
        <span
          className="text-xs text-slate-400 whitespace-nowrap"
          title="Split across vessels. This is still one invoice and one payable."
        >
          split{sent > 0 ? ` · ${sent}/${allocations.length} sent` : ""}
          {partly > 0 ? ` · ${partly} partly sent` : ""}
        </span>
      </div>
    );
  }

  const v = invoice.assignment?.vesselName || "";
  if (!v) {
    return (
      <button
        onClick={onAssign}
        className="text-xs text-slate-400 hover:text-primary-500 underline-offset-2 hover:underline"
        title="Click to assign this invoice to a vessel"
      >
        + Assign vessel
      </button>
    );
  }
  return (
    <VesselPill
      name={v}
      color={invoice.assignment?.stickerColor}
      title={`Vessel: ${v} · Sticker: ${invoice.assignment?.stickerColor || "none"} (click to change)`}
      onClick={onAssign}
    />
  );
}

function StatusPill({ status, label }: { status: VendorInvoiceStatus; label?: string }) {
  const cls =
    status === "done"
      ? "bg-cyan-50 text-cyan-700 border-cyan-200"
    : status === "failed"
      ? "bg-amber-50 text-amber-800 border-amber-200"
    : status === "processing"
      ? "bg-amber-50 text-amber-800 border-amber-200"
    : status === "pending"
      ? "bg-sky-50 text-sky-800 border-sky-200"
    : status === "skipped"
      ? "bg-slate-50 text-slate-600 border-slate-200"
      : "bg-slate-100 text-slate-700 border-slate-200";

  const text = label || String(status || "").replace(/^\w/, (c) => c.toUpperCase());
  // Issue 2: use rounded-md + whitespace-nowrap so multi-word labels never wrap
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-md border whitespace-nowrap ${cls}`}>
      {text}
    </span>
  );
}

/**
 * Columns where the useful first click is "biggest / newest first" — a date or a
 * number. Text columns start at A–Z instead.
 *
 * Status is the exception among the words (lane 5, V6): nearly every row reads Done, which is
 * the first word A–Z, so its first click put the Done rows first, the order the list was
 * already in, and nothing seemed to happen. Reversed, the rows that are NOT Done (Skipped,
 * Processing, Pending, Failed) come first, which is what anyone clicking Status is after.
 */
const DESC_FIRST_SORTS: VendorInvoiceSortKey[] = ["createdAt", "updatedAt", "total", "items", "confidence", "status"];

/**
 * A sortable column heading.
 *
 * Sorting is applied by the server across the whole result set, not just the
 * page in hand, so changing it goes back to page 1 (see onSort).
 */
function SortHeader({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  className = "",
}: {
  label: string;
  sortKey: VendorInvoiceSortKey;
  activeKey: VendorInvoiceSortKey;
  dir: SortDir;
  onSort: (key: VendorInvoiceSortKey) => void;
  className?: string;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={`px-3 py-2 ${className}`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-slate-900"
        title={
          active
            ? `Sorted by ${label} (${dir === "asc" ? "ascending" : "descending"}) — click to reverse`
            : `Sort by ${label}`
        }
      >
        <span className={active ? "text-slate-900 font-semibold" : ""}>{label}</span>
        <span className={`text-xs leading-none ${active ? "text-primary-500" : "text-slate-300"}`}>
          {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

// The API origin comes from lib/apiClient's getApiBaseUrl — one resolver for
// the whole client. This file carried its own copy of it until 2026-08-21,
// which is exactly the "new API base-URL strategy" CLAUDE.md forbids: two
// resolvers drift, and the one that drifts is the one nobody tests.
function buildAbsoluteFileUrl(fileUrl: string): string {
  const raw = String(fileUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = getApiBaseUrl();
  const clean = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${clean}`;
}


// ? Confidence pill (AI confidence score)
// (unused) ConfidencePill removed to avoid lint warnings

// ── Entry validation ────────────────────────────────────────────────────────
// Flags describe values that are already stored; these refuse to store the ones
// that cannot be right in the first place. Nothing here judges whether a figure
// AGREES with the rest of the invoice — that is the flags' job, and blocking it
// would stop ops recording an invoice that genuinely disagrees with itself.

function nonNegativeNumber(label: string) {
  return (value: any): string | null => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return `${label} must be a number`;
    if (n < 0) return `${label} cannot be negative`;
    return null;
  };
}



function validateTaxRate(value: any): string | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return "Tax Rate must be a number";
  if (n < 0) return "Tax Rate cannot be negative";
  // 12.5 is a rate; 125 is the tax AMOUNT typed into the rate column, which is
  // how a line ends up charging more tax than it does goods.
  if (n > 100) return `${n}% is not a tax rate — did you mean to enter this in Tax Amount?`;
  return null;
}


/**
 * Quantity x Units per Case = Total Units, applied when either factor is edited.
 *
 * Mirrors `linkPackMath` in services/vendorInvoice.service.js, which is the
 * authority — it runs for every writer, including the API and the reprocess
 * path. This exists so the cell beside the one just edited updates now rather
 * than at the next refetch; the server would otherwise be silently correcting a
 * total the user can still see the old value of.
 *
 * Returns just the edited field when the other factor is missing. Total Units
 * cannot be computed from one number, and clearing it because we cannot compute
 * it would delete the figure inventory actually receives.
 */
export function packMathWrites(row: any, key: string, value: any): { key: string; value: any }[] {
  const edit = [{ key, value }];
  const statedCount = toNum(key === "splitPackCount" ? value : row?.splitPackCount);
  // The line's own unit word, where it states no count — a pound holds one
  // pound. Same owner as the server's `linkPackMath` reads, so the cell this
  // echoes lands on the figure the save is about to write.
  const packCount = statedCount ?? impliedPackCount(row);
  const qty = toNum(key === "splitQuantity" ? value : row?.splitQuantity ?? row?.qty);

  // CLEARING one of the two factors clears the product with it. Bailing out and
  // leaving the old Total Units behind produced a row reading Quantity = blank,
  // Units / Case = 24, Total Units = 240 — and Total Units is not a display
  // figure, it is what vendorInvoiceInventorySync receives into stock. A line
  // stating no quantity would still have stocked in 240 units.
  //
  // Distinct from the "other factor is missing" case below: there the user has
  // not stated the second number, so there is nothing to recompute and nothing
  // to invalidate. Here they have just taken one away.
  //
  // Clearing UNITS / CASE is the one that does not always empty the total.
  // On a loose line the unit word still answers 1, so the product falls back to
  // the quantity rather than to nothing — `packMathTotalUnits` returns null for
  // every other line and the cell empties exactly as it did before.
  const clearedAFactor =
    (key === "splitQuantity" || key === "splitPackCount") && isEmptyValue(value);
  if (clearedAFactor) {
    const total = key === "splitPackCount" ? packMathTotalUnits(qty, packCount) : null;
    return [...edit, { key: "splitItemUnits", value: total }];
  }

  // The owner's arithmetic, not a second spelling of it — this cell has to
  // land on the figure `linkPackMath` is about to write, or the grid shows a
  // number the save then silently corrects.
  const total = packMathTotalUnits(qty, packCount);
  if (total === null) return edit;

  return [
    ...edit,
    { key: "splitItemUnits", value: total },
    // Only a pack count the user typed is theirs. Re-deriving the total after a
    // QUANTITY edit does not change where the per-case figure came from.
    ...(key === "splitPackCount" ? [{ key: "splitPackCountSource", value: "manual" }] : []),
  ];
}

/**
 * Total Units for a LEGACY line `backfillSplitFields` is healing, through the
 * owner of BUSINESS_RULES#qty-times-units-per-case (`packMathTotalUnits`).
 *
 * TECH_DEBT #90, Q9 (owner, 2026-10-12): this used to be a fourth spelling,
 * `Math.round(packCount * qty)`, which turned 10.6 KG x 24 into 254 and wrote a
 * NEGATIVE Total Units for a supplier credit. The owner's answer is the owner's
 * arithmetic: fractions kept (254.4), and a credit line (negative quantity)
 * gets a blank Total Units, as the processor does server-side (D114.6).
 *
 * What this returns is PERSISTED: `backfillSplitFields` sends it through
 * `patchVendorInvoice`, and the server's `linkPackMath` steps aside when
 * `splitItemUnits` arrives with the pack count or quantity. "" means no figure.
 */
export function legacyBackfillTotalUnits(
  qty: number | null,
  packCount: number | null
): number | "" {
  return packCount ? packMathTotalUnits(qty, packCount) ?? "" : "";
}

/**
 * May `backfillSplitFields` fill this cell? Only a blank NOBODY MADE.
 *
 * The backfill runs on every open in edit mode (`needsBackfill` stays true on
 * any line sold loose), and it filled every empty pack cell from the PRINTED
 * cells: the Quantity from `qty`, the Pack Unit from `uom`. So a reviewer who
 * cleared a Quantity, a Pack Unit or a Units / Case found it back on the next
 * open, typed over by what the paper was misread as — and Total Units, which
 * stock receives, re-derived from it. The packing note already kept this rule
 * ("a blank a person MADE is not a blank to fill"); every cell now does.
 * `manuallyEditedPaths` is the invoice's own record of which cells a person
 * has typed into, the list the grid paints blue from.
 */
export function mayBackfillCell(li: any, field: string, path: string, editedPaths: Set<string>): boolean {
  return isEmptyValue(li?.[field]) && !editedPaths.has(path);
}

/**
 * The one stamp a backfilled Item UOM carries. Read out of a size the row filled, it is the row's
 * (never learned until typed); else the implied "1 LBS" reading's; else a document read (D177).
 */
export function backfillItemUowStamp(
  li: any,
  splitItemUow: string,
  unitUom: string,
  sizeTextUnit: string,
  impliedSize: { unitSize: number; unitUom: string } | null
): "splitItemUowFromCatalogueRow" | "splitItemUowFromUnitWord" | null {
  if (!unitUom && sizeTextUnit && li?.splitUnitSizeFromCatalogueRow === true) return "splitItemUowFromCatalogueRow";
  return impliedSize && splitItemUow === impliedSize.unitUom ? "splitItemUowFromUnitWord" : null;
}

/**
 * The quantity `backfillSplitFields` derives Total Units from (TECH_DEBT #319).
 *
 * `splitQuantity` FIRST — the cell the grid's Quantity column writes, so on a
 * corrected line it is the live figure and `qty` the extractor's misread (the
 * order `invoiceVat.QUANTITY_KEYS` and `invoiceLineMath.lineQuantity` keep).
 * It was missing from this list, so Total Units was derived from the printed
 * quantity beside the reviewed one. And through `toNum`, not a bare `Number`:
 * `Number(null)` is 0, so a stored `qty: null` became a Quantity and a Total
 * Units of 0, written down and read into stock (invariant 8).
 */
export function backfillQuantity(li: any): number | null {
  const candidates = [
    li?.splitQuantity,
    li?.qty,
    li?.quantity,
    li?.qtyOrdered,
    li?.orderedQty,
    li?.qty_ordered,
    li?.qty_total,
    li?.quantityOrdered,
    li?.quantity_ordered,
    li?.unitQuantity,
  ];
  for (const c of candidates) {
    const n = toNum(c);
    if (n !== null) return n;
  }
  return null;
}

/**
 * The Pack Unit a legacy line is seeded with, and the count its printed unit
 * states — the processor's rule, `invoiceLineMath.seedPackUnit`, rather than
 * the printed cell as typed ("CS10PK" is CASE and 10, as on a fresh extraction).
 * The count is offered only while the Pack Unit is still being seeded from that
 * cell: once a Pack Unit is on the line — or a person has emptied it
 * (`mayFill` false, see `mayBackfillCell`) — the printed one is not evidence.
 */
export function backfillPackUnit(
  li: any,
  caseBagUow: string,
  descriptionPackCount: number | null,
  mayFill: boolean,
  // The size the description states ("2 KG"), so a "KG 5" cell that disagrees
  // stays raw here exactly as it does in the processor (D100).
  descriptionUnitSize: string | null = null
): { splitUow: any; unitWordPackCount: number | null; unitSize?: string } {
  if (!mayFill) return { splitUow: li?.splitUow ?? "", unitWordPackCount: null };
  return seedPackUnit(li?.uow || li?.uom || li?.unit || caseBagUow, descriptionPackCount, descriptionUnitSize);
}

/**
 * The Unit Size a legacy line is backfilled with, in the processor's order:
 * the size the description states; else the size a "KG 5" unit cell states
 * (D100, from `backfillPackUnit`); else, when the pack unit is itself a measure,
 * one of it — "1 LBS" on a line billed by the pound, the reading that lets
 * `weightTierFromSize` state a per-kg cost (`impliedUnitSize`, D18). The last is
 * returned too, because only it carries the `...FromUnitWord` stamps: a size the
 * supplier printed is a document read. A stored value, or a blank a person made
 * (`mayFill` false), is kept.
 */
export function backfillUnitSize(
  stored: any,
  mayFill: boolean,
  unitSize: number | null,
  unitUom: string,
  cellSize: string | undefined,
  splitUow: any
): { splitUnitSize: any; impliedSize: { unitSize: number; unitUom: string } | null } {
  const describedNone = unitSize === null && !unitUom;
  const cellSizeOnly = describedNone && cellSize ? cellSize : null;
  const impliedSize = describedNone && !cellSizeOnly ? impliedUnitSize({ splitUow }) : null;
  const splitUnitSize = !isEmptyValue(stored)
    ? stored
    : !mayFill
      ? ""
      : unitSize
        ? `${unitSize} ${unitUom}`
        : cellSizeOnly
          ? cellSizeOnly
          : impliedSize
            ? `${impliedSize.unitSize} ${impliedSize.unitUom}`
            : "";
  return { splitUnitSize, impliedSize };
}

/**
 * Units / Case `backfillSplitFields` may derive onto a blank cell by dividing
 * Total Units by Quantity — or null when the line's resolved Pack Unit is
 * `packCountLocked` (D65 rule 1).
 *
 * A PCS, BTL, KG... holds one item by definition and the grid cell locks to
 * say so, but a legacy line dividing Total Units by Quantity lands on that
 * same shape by construction whenever the two agree — Total Units ==
 * Quantity divides to exactly 1 — and on a real-looking count above 1
 * whenever they don't (Total Units 48, Quantity 2 -> 24). Writing either into
 * `splitPackCount` fabricates a stored count D18 / TECH_DEBT #72 forbid: a
 * stored 1 is learned by vendor item memory as a fact about the product the
 * invoice never stated, and a stored >1 is refused outright at Confirm &
 * sync — leaving a reviewer unable to clear a cell the backfill itself wrote,
 * on a line nobody touched.
 *
 * Takes the LINE, not just the resolved `splitUow` string, because
 * `packCountLocked` reads through `linePackUnit`'s own fallback
 * (`splitUow || uow || uom || unit`, `invoiceLineMath.js`). A reviewer who
 * empties Pack Unit leaves `splitUow` blank — `mayBackfillCell` then refuses
 * to refill it, so the resolved `splitUow` this function is handed stays
 * `""` — but the PRINTED unit is still sitting in `li.uow`. Checking the lock
 * against `{ splitUow }` alone hid that printed unit from `packCountLocked`
 * entirely: a blank never locks, whatever the paper said, so a PCS line
 * (Total Units 48, Quantity 2) still derived the now-refused 24. Passing
 * `{ ...li, splitUow }` lets `linePackUnit` fall through to `li.uow` exactly
 * as every other reader of the lock already does.
 */
export function backfillDerivedPackCount(
  li: any,
  splitUow: string,
  statedItemUnits: any,
  splitQuantity: any
): number | null {
  if (packCountLocked({ ...li, splitUow })) return null;
  const units = toNum(statedItemUnits);
  const q = toNum(splitQuantity);
  if (units === null || q === null || q <= 0 || units <= 0) return null;
  const per = units / q;
  return Number.isInteger(per) ? per : null;
}

/**
 * Invoice Number and PO Number have no length limit anywhere — `extracted.invoice`
 * is `mongoose.Schema.Types.Mixed` server-side, so nothing stopped a mistaken
 * paste (a whole line of the document, a product description) from being saved
 * as "the invoice number". Matches the server's own cap
 * (`vendorInvoice.service.js`'s `MAX_HEADER_TEXT_FIELD_LENGTH`) so a value this
 * rejects here is the same one the server would refuse — the point is telling
 * the reviewer BEFORE the request round-trips, not a stricter rule.
 */
export const HEADER_TEXT_FIELD_MAX_LENGTH = 100;

/**
 * Mirrors the server's own gate (`invoiceFileFilter` in
 * `src/routes/vendorInvoice.routes.js`): a PDF or any image, nothing else.
 * That check only ran after the file had already been posted, so picking a
 * `.docx` or a stray screenshot's sibling `.zip` looked fine right up until
 * "Upload & Parse" came back with an error — the `accept` attribute on the
 * file input only filters the OS picker's default view, it doesn't stop
 * "All Files" plus a bad pick, and nothing re-checked the file client-side
 * before sending it. Same rule, checked the moment a file is chosen instead
 * of after the round trip.
 */
export function isAcceptedInvoiceUploadFile(file: { type?: string } | null | undefined): boolean {
  const mime = String(file?.type || "").toLowerCase();
  return mime.includes("pdf") || mime.startsWith("image/");
}

/**
 * VI-4: why the extractor read this invoice without its page layout (over the
 * page attach limit, or the layout could not be sent), or null when it had it.
 * `meta.documentAttached` is false only when V2 tried and did not attach.
 */
export function textOnlyReadNote(inv: { meta?: any } | null | undefined): string | null {
  return inv?.meta?.documentAttached === false && typeof inv.meta.documentNote === "string"
    ? inv.meta.documentNote
    : null;
}

/**
 * Invoice Date and Due Date were plain text cells with nothing checking what
 * got typed into them: "2026-02-30" saved without complaint, and because
 * `deriveDueDate` (correctly) refuses to guess a due date from a date it
 * cannot parse, Due Date then silently stopped following Invoice Date — with
 * nothing on screen explaining why it looked stuck. Rejecting the unparseable
 * date HERE, at the point a person types it, is what stops that chain from
 * starting: `parseInvoiceDate` is the same owner `deriveDueDate` itself reads
 * (`utils/paymentTerms.ts`), so a date this accepts is one the derivation can
 * actually use.
 */
export function validateHeaderDateText(value: any): string | null {
  const s = value === null || value === undefined ? "" : String(value).trim();
  if (!s) return null; // blank is allowed — this is a format check, not a required-field one
  if (!parseInvoiceDate(s)) {
    return `"${s}" isn't a date this system can read — try dd/mm/yyyy, or spell out the month.`;
  }
  return null;
}

/**
 * Item 29 (QA retest): the Invoice Date / Due Date header cells are plain
 * `type: "text"` EditableCells (see `validateHeaderDateText` above for why
 * they are not `type: "date"`), so they showed back whatever was typed or
 * extracted VERBATIM — "5/10/2026" stayed "5/10/2026" — while every other
 * date on this same screen, and the catalogue banner reading the same field,
 * reads dd/mm/yyyy. `formatDdMmYyyy` is the owner this page already imports
 * for that (`utils/paymentTerms.ts`; it is the engine `dateFormat.ts`'s own
 * `formatDate` calls for a printed, day-first date like this one, so the two
 * are not a second, diverging formatter). A value that does not parse — free
 * text like "see attached" the reviewer has not corrected yet — is shown
 * exactly as stored rather than silently blanked.
 */
export function invoiceHeaderDateDisplay(value: any): string | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const raw = String(value);
  return formatDdMmYyyy(raw) || raw;
}

/**
 * A vendor-invoice amount on screen. Was hard-coded to USD, so every invoice —
 * TT$, EUR, anything — rendered as US dollars; it reads the currency the
 * processor resolved for that invoice, and when it could not be resolved shows
 * the bare number rather than implying one. The label and grouping are the
 * owner's, `formatCurrency` (BUSINESS_RULES#quotation-screen-money-labels:
 * "US$", never the bare "$" `toLocaleString` printed) — with `ceil: false`,
 * because this is what the supplier printed: a printed 10.004 reads 10.00.
 */
export function formatInvoiceMoney(value: any, currencyCode?: string | null): string {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const code = String(currencyCode || "").trim().toUpperCase();
  return formatCurrency(num, /^[A-Z]{3}$/.test(code) ? code : null, { ceil: false });
}

/**
 * The Line Items grid's Discount cell (D83, prod vendor-invoice 3723225809,
 * Blue Waters): `discount` is stored exactly as printed (see
 * `lineDiscountAmount`'s own header comment for why), so the cell SHOWS the
 * line's total discount in money instead, whenever the printed lineTotal
 * proves one and it differs from the raw stored figure. An unproven line, or
 * one whose `discount` was already a whole-line amount, shows the stored
 * value unchanged — nothing to translate.
 *
 * Editing still writes `discount` itself: this is the cell's DISPLAY only,
 * the same "shown value is derived" split the Expiry cell's `display` uses.
 */
export function discountCellDisplay(row: any): any {
  const total = lineDiscountAmount(row);
  return total !== null && total !== num(row?.discount) ? total : row?.discount;
}

/**
 * The Discount cell's hover text — what the invoice actually printed, the
 * same "shown value is derived, hover to check what it was derived from"
 * convention as the Expiry cell's `cellTitle`. undefined (no tooltip) when
 * the shown value already IS the printed figure.
 *
 * A PERCENT-proven line's raw `discount` is a RATE, not money (D83 redesign,
 * item C) — "10" means 10%, and formatting it as currency ("TT$10.00 as
 * printed") states a figure the invoice never printed. `computeLineMath`'s
 * own `provenReading` says which meaning was proven, so this asks it rather
 * than re-deriving the same reading a second way.
 */
export function discountCellTitle(row: any, currencyCode?: string | null): string | undefined {
  const total = lineDiscountAmount(row);
  if (total === null || total === num(row?.discount)) return undefined;
  if (computeLineMath(row).provenReading === "percent") return `${num(row?.discount)}% as printed`;
  return `${formatInvoiceMoney(row?.discount, currencyCode)} as printed`;
}

/** Shared by the Invoice Number and PO number rows below. */
export function validateHeaderTextLength(value: any): string | null {
  const s = value === null || value === undefined ? "" : String(value);
  if (s.length > HEADER_TEXT_FIELD_MAX_LENGTH) {
    return `Too long (${s.length} characters, ${HEADER_TEXT_FIELD_MAX_LENGTH} max).`;
  }
  return null;
}

function KVTable(props: {
  title: string;
  rows: {
    label: string;
    path: string;
    value: any;
    type?: "text" | "number";
    hint?: string;
    /** "warn" paints the hint amber: something to check against the paper. */
    hintTone?: "warn";
    /**
     * A second, neutral line under the value: what the vendor printed for a
     * total a quantity correction moved (D44 #55). Separate from `hint`, which
     * already says where the figure came from.
     */
    note?: string | null;
    /** Rejects a value before it is saved; return an error message to block it. */
    validate?: (value: any, raw: string) => string | null;
    /**
     * What the cell SHOWS while not being edited, when that differs from the
     * stored `value` — see EditableCell's own `display` prop. Item 29 (QA
     * retest): Invoice Date / Due Date are plain `type: "text"` cells (their
     * own `validateHeaderDateText` reads the lenient, invoice-printed-date
     * parser, not the strict typed-date one `type: "date"` would force), so
     * they echoed whatever was typed verbatim instead of the house dd/mm/yyyy
     * every other date on screen uses.
     */
    display?: string;
  }[];
  onSave: (path: string, value: any) => Promise<void> | void;
  /** Review highlight for a header field, keyed by the row's path. */
  fieldFlag?: (path: string) => CellFlag | undefined;
  /** View-only mode: cells render as plain text, never editable. */
  readOnly?: boolean;
}) {
  const { title, rows, onSave, fieldFlag, readOnly } = props;
  if (!rows.length) return null;

  return (
    <div className="rounded-xl bg-white border border-slate-200 overflow-hidden min-w-0 max-w-full">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/60 text-[13px] text-slate-800 font-semibold">
        {title}
      </div>
      {/* Label column is a percentage, not a fixed width, so the card stays readable
          at any column width the auto-fit grid gives it. */}
      <table className="w-full text-[13px] table-fixed">
        <tbody>
          {rows.map((r) => {
            const flag = fieldFlag?.(r.path);
            const tooltip = [flag?.reason, r.hint].filter(Boolean).join("\n");
            return (
              <tr key={r.path} className="border-t border-slate-100">
                <td className="w-[40%] px-3 py-1.5 text-slate-500 align-top break-words">{r.label}</td>
                <td className={`px-1.5 py-1.5 min-w-0 ${flagCellClass(flag)}`} title={tooltip || undefined}>
                  {/* Header amounts are deliberately NOT range-checked: a
                      credit from a supplier legitimately carries negative
                      totals. EditableCell still refuses anything that is not a
                      number at all. */}
                  <EditableCell
                    value={r.value}
                    type={r.type}
                    display={r.display}
                    onSave={(v) => onSave(r.path, v)}
                    validate={r.validate}
                    readOnly={readOnly}
                    hideActions
                  />
                  {r.hint && (
                    <div className={`px-2 pt-0.5 text-xs ${r.hintTone === "warn" ? "text-amber-700" : "text-slate-400"}`}>
                      {r.hint}
                    </div>
                  )}
                  {r.note && <div className="px-2 pt-0.5 text-xs text-slate-500">{r.note}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type SimpleTableColumn = {
  header: string;
  key: string;
  /**
   * "check" is a plain boolean toggle rather than a click-to-edit cell: there is
   * no draft to commit and nothing to mistype, so it saves on the click.
   */
  type?: "text" | "number" | "date" | "check";
  /** Width/min-width override, e.g. "min-w-[220px]". Falls back to a compact default. */
  widthClass?: string;
  /** Pins the column while the table scrolls sideways, e.g. "left-0" / "left-14". */
  stickyClass?: string;
  /** Explains the column in the header tooltip. */
  help?: string;
  /**
   * A short rule printed UNDER the header, not hidden behind a hover.
   *
   * Reserved for the handful of columns where the rule is not guessable from
   * the name and getting it wrong is expensive — Unit Price is the price of a
   * PACK, and the master catalogue is built from it, so a per-item price typed
   * there prices every future quotation off a figure that is out by the pack
   * size. Ops were being told this verbally and in a tooltip nobody hovers.
   *
   * Keep these to three or four words. A note on every column is a note on no
   * column.
   */
  headerNote?: string;
  /**
   * Tints the header, marking columns that have to be read together.
   *
   * Unit Price and Units / Case are one statement in two cells: the price is
   * per pack, and this is what a pack holds. Their agreement is what the
   * catalogue's per-unit cost is derived from, and they sit either side of
   * columns that have nothing to do with each other.
   */
  headerAccent?: boolean;
  /**
   * A unit written after the value when the cell is being read — "%" on the VAT
   * rate. Display only: it never reaches the input or the stored value. See
   * EditableCell's `suffix`.
   */
  suffix?: string;
  /** Rejects a value before it is saved; return an error message to block it. */
  validate?: (value: any, raw: string) => string | null;
  /**
   * One visible column, more than one stored field.
   *
   * Unit Size and Unit were separate columns showing "500 ML" next to "ML" —
   * the same fact twice, and editable in two places that could disagree. They
   * are now one column: this maps what the user typed onto every field it
   * actually determines, so the stored unit can never drift from the size.
   */
  writes?: (value: any, row: any) => { key: string; value: any }[];
  /**
   * What the cell shows, when the stored value is not what a person edits.
   *
   * The case weights of a catch-weight line are stored as an array of numbers —
   * nine boxes of ribeye — and typed as a list. The counterpart of `writes`:
   * that maps typing onto storage, this maps storage onto the cell.
   */
  display?: (row: any) => any;
  /**
   * What EDITING seeds from and what the no-op check compares against, when
   * that is not simply the row's own `key` field — D83's own redesign (prod
   * vendor-invoice 3723225809, Blue Waters): `display` used to seed editing
   * too, wherever a column set it, so a DERIVED figure with no relation to
   * what is typed (the Discount cell's total, 156.00, over a stored per-unit
   * 6.50) got compared against the stored value as if it were an edit and
   * saved back over it on a plain click-then-blur.
   *
   * Absent, editing seeds from `row?.[key]` — the stored field itself — which
   * is correct for every column whose `display` is a pure VIEW transform of
   * that SAME field (a locked cell's "1", a joined case-weights list). Only a
   * column whose `display` shows a FALLBACK the reviewer is meant to confirm
   * by editing — the Expiry cell's parsed-off-the-invoice date, still unsaved
   * — restates that same function here, so a click-then-blur still confirms
   * and writes it (USC-BUG-0057, predates and is unrelated to D83).
   */
  editSeed?: (row: any) => any;
  /**
   * What is actually stored, when `display` shows a fallback in its place — it
   * reads the fallback's OWN field off the row, which `storedRows` cannot guess.
   * Overrides the table's `storedRows` for this column. See EditableCell's
   * `stored`.
   */
  stored?: (row: any) => any;
  /**
   * Hover text for the cell, where the cell shows something derived and the
   * thing it was derived FROM is worth being able to check.
   *
   * A per-cell review flag outranks it: what is wrong with a value matters more
   * than where it came from.
   */
  cellTitle?: (row: any) => string | undefined;
  /**
   * Shown but not editable, with this as the reason on hover — the quantity
   * cells of an invoice already in stock (D44 #20, VR-5). Unlike the table's
   * `readOnly`, it says why, so the lock is not mistaken for a broken cell.
   */
  locked?: string;
  /**
   * The same lock, decided per ROW rather than once for the whole column —
   * Units / Case on a PCS or a KG line (D65): a unit whose pack count is one
   * by definition (`packCountLocked`) locks that row's cell and shows "1"
   * (via `display`) without ever writing it, while a BOX or a CASE two rows
   * down keeps editing normally. `locked` above cannot express this — it is
   * evaluated once, outside the row loop — so this is a second, per-row
   * answer to the same question, and a column may use either but need not
   * use both. Returning undefined leaves the cell exactly as `locked` would.
   *
   * UNLOCKS ITSELF the moment the stored value is something the lock cannot
   * defend — a PCS somehow carrying a stored 24 — because that state is an
   * ERROR the reviewer has to be able to clear, not a fact to keep protecting.
   */
  lockedFor?: (row: any) => string | undefined;
};

export function SimpleTable(props: {
  title: string;
  columns: SimpleTableColumn[];
  basePath: string;
  rows: any[];
  /**
   * The same rows as last LOADED, before any staged-but-unsaved edit was laid
   * over them (USC-BUG-0045). `rows` is the preview: a column with `writes`
   * stages a derived value into a sibling cell — Units / Case derives Total
   * Units — so that cell shows a figure nobody has saved. Without this, typing
   * that figure to confirm it read as "nothing changed" and was dropped. Omit
   * it and every cell compares against what it shows, as before.
   */
  storedRows?: any[];
  onSave: (path: string, value: any) => Promise<void> | void;
  /** Batch save, required by columns that write more than one field. */
  onSaveMany?: (ops: { path: string; value: any }[]) => Promise<void> | void;
  /**
   * Last check before a value is written: return a question to put to the user,
   * or null to save silently. Declining leaves the cell as it was.
   */
  confirmCell?: (row: any, key: string, value: any) => ConfirmOptions | null;
  scrollClass?: string;
  /**
   * Per-cell review highlight. Returns the flag for one cell, or undefined to
   * leave it alone. Lives on the table rather than on EditableCell so callers
   * that do not review anything stay untouched.
   */
  cellFlag?: (row: any, key: string, idx: number) => CellFlag | undefined;
  /** Rendered under the title, e.g. the highlight legend. */
  subheader?: React.ReactNode;
  /** View-only mode: cells render as plain text, never editable. */
  readOnly?: boolean;
  /**
   * Columns a line cannot be left without, marked with a star in the header.
   *
   * Passed in rather than declared per column so it can come from the same list
   * the red "this is empty" flag is built from — see REQUIRED_LINE_CELL_KEYS.
   * A star that does not match the flag is worse than no star: it either
   * demands something nothing checks, or stays silent on something that turns
   * red the moment the reviewer moves on.
   */
  requiredKeys?: ReadonlySet<string>;
  /**
   * One trailing cell per row, for something about the whole LINE rather than
   * one of its cells: what the item memory holds for that product (MC-6).
   * After the columns, so no column's path, flag or pinning moves.
   */
  rowAction?: (row: any, idx: number) => React.ReactNode;
  /**
   * Puts a Remove button on each row (prod USC-BUG-0021). Absent means no
   * button: the caller decides who may remove a line, and when.
   */
  onRemoveRow?: (row: any, idx: number) => void;
  /**
   * A question that belongs to a row but to no single cell, drawn on a line of
   * its own under that row (prod USC-BUG-0030: how to read a "20ct*1.5oz" Unit
   * Size). Return null for a row with nothing to ask; the row renders as before.
   */
  rowNote?: (row: any, idx: number) => React.ReactNode;
}) {
  const { title, columns, basePath, rows, storedRows, onSave, onSaveMany, confirmCell, scrollClass, cellFlag, subheader, readOnly, requiredKeys, rowAction, onRemoveRow, rowNote } = props;
  const confirm = useConfirm();
  if (!rows?.length) return null;

  return (
    <div className="rounded-xl bg-white border border-slate-200 overflow-hidden min-w-0 max-w-full flex flex-col">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/60 text-[13px] text-slate-800 font-semibold flex items-center justify-between gap-2 shrink-0">
        <span>{title}</span>
        <span className="text-xs font-normal text-slate-400">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
      </div>
      {subheader}

      {/* Issue 4 + 5: scrollable container with sticky header cells inside.
          Sticky lives on the th (not the thead) so pinned columns can stack both axes. */}
      {/* No `min-w-max` on the table. `min-width: max-content` sizes the table
          to its longest UNWRAPPED line, so however a cell is styled it never
          needs to wrap — the grid just grew sideways and long descriptions were
          only readable by scrolling. Each column's own `min-w-[…]` is the floor
          instead: the table still overflows into this scroller when the floors
          exceed the width, but any spare width now goes into wrapping. */}
      <div className={`flex-1 min-h-0 overflow-x-auto overflow-y-auto ${scrollClass || ""}`}>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-slate-700">
              {columns.map((c) => {
                const isRequired = !!requiredKeys?.has(c.key);
                return (
                <th
                  key={c.key}
                  title={
                    // The required sentence even when the column has no help
                    // text of its own — a star with no explanation is a riddle.
                    [c.help, isRequired ? "Required — a line cannot be received without it." : null]
                      .filter(Boolean)
                      .join("\n\n") || undefined
                  }
                  // Dividers are inset shadows, not borders: collapsed-table borders
                  // drop out from sticky cells in Chrome/Safari.
                  //
                  // The accent tint is on the header only, never the cells: it
                  // marks a pair of columns that have to agree, and tinting 40
                  // rows of data would compete with the per-cell review flags,
                  // which are the colours that mean something is wrong.
                  className={`sticky top-0 bg-[#F9FAF9] text-left px-2.5 py-1.5 whitespace-nowrap ${
                    c.stickyClass
                      ? `${c.stickyClass} z-30 shadow-[inset_-1px_0_0_#D7DBD9,inset_0_-1px_0_#D7DBD9]`
                      : "z-20 border-r border-[#D7DBD9] shadow-[inset_0_-1px_0_#D7DBD9]"
                  } ${c.widthClass || ""}`}
                >
                  <span>{c.header}</span>
                  {/* Not merely decorative: this is the only thing on screen
                      that says which cells a reviewer may not leave blank
                      before they have left one blank. */}
                  {isRequired && (
                    <span
                      role="img"
                      aria-label="Required"
                      title="Required"
                      className="ml-0.5 text-rose-500 font-bold align-top text-xs"
                    >
                      *
                    </span>
                  )}
                  {c.headerNote && (
                    <span
                      className={`block font-normal text-xs leading-tight ${
                        c.headerAccent ? "text-amber-700" : "text-slate-400"
                      }`}
                    >
                      {c.headerNote}
                    </span>
                  )}
                </th>
                );
              })}
              {rowAction && (
                <th className="sticky top-0 z-20 bg-[#F9FAF9] px-2.5 py-1.5 shadow-[inset_0_-1px_0_#D7DBD9]">
                  <span className="sr-only">Line actions</span>
                </th>
              )}
              {onRemoveRow && (
                <th className="sticky top-0 z-20 bg-slate-50 px-2.5 py-1.5 shadow-[inset_0_-1px_0_#e2e8f0]">
                  <span className="sr-only">Remove line</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const note = rowNote ? rowNote(row, idx) : null;
              return (
              <React.Fragment key={idx}>
              <tr className="border-t border-slate-100">
                {columns.map((c) => {
                  const p = `${basePath}.${idx}.${c.key}`;
                  const flag = cellFlag?.(row, c.key, idx);
                  const save = async (v: any) => {
                    const question = confirmCell?.(row, c.key, v);
                    // Declining closes the cell with its original value: the
                    // edit simply did not happen. Asked in the app: a native
                    // window.confirm froze the tab here (VI-6 Chrome retest).
                    if (question && !(await confirm(question))) return;

                    const extra = c.writes?.(v, row);
                    if (extra && onSaveMany) {
                      await onSaveMany(
                        extra.map((w) => ({ path: `${basePath}.${idx}.${w.key}`, value: w.value }))
                      );
                      return;
                    }
                    await onSave(p, v);
                  };
                  // The lock first, then anything the review has to say about
                  // the value. `lockedFor` is asked per row — a column-wide
                  // `locked` never varies by row, so a row the per-row answer
                  // does not lock still falls through to it (D65: most
                  // columns use only one of the two).
                  const rowLock = c.lockedFor?.(row) ?? c.locked;
                  const lockTitle = rowLock ? [rowLock, flag?.reason].filter(Boolean).join("\n\n") : undefined;
                  return (
                    <td
                      key={c.key}
                      title={lockTitle || flag?.reason || c.cellTitle?.(row)}
                      className={`px-2.5 py-1 align-top ${c.widthClass || "min-w-[92px]"} ${
                        c.stickyClass ? `${c.stickyClass} sticky z-10 bg-white shadow-[inset_-1px_0_0_#D7DBD9]` : "border-r border-[#D7DBD9]"
                      } ${flagCellClass(flag)}`}
                    >
                      {c.type === "check" ? (
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 accent-primary-500 disabled:opacity-50"
                          checked={!!(c.display ? c.display(row) : row?.[c.key])}
                          disabled={readOnly || !!rowLock}
                          title={rowLock}
                          onChange={(e) => save(e.target.checked)}
                        />
                      ) : lockTitle ? (
                        <LockedCellValue value={c.display ? c.display(row) : row?.[c.key]} title={lockTitle} />
                      ) : (
                        <EditableCell
                          value={c.editSeed ? c.editSeed(row) : row?.[c.key]}
                          display={c.display ? c.display(row) : undefined}
                          hint={c.cellTitle?.(row)}
                          stored={
                            c.stored
                              ? c.stored(row)
                              : storedRows
                              ? storedRows[idx]?.[c.key] ?? null
                              : undefined
                          }
                          type={c.type}
                          onSave={save}
                          validate={c.validate}
                          readOnly={readOnly}
                          suffix={c.suffix}
                          hideActions
                        />
                      )}
                    </td>
                  );
                })}
                {rowAction && <td className="px-2.5 py-1 align-top">{rowAction(row, idx)}</td>}
                {onRemoveRow && (
                  <td className="px-2 py-1 align-top">
                    <button
                      type="button"
                      onClick={() => onRemoveRow(row, idx)}
                      className="mt-0.5 text-xs text-slate-400 hover:text-rose-600 whitespace-nowrap"
                      title={`Remove line ${row?.lineNo ?? idx + 1} from this invoice`}
                      aria-label={`Remove line ${row?.lineNo ?? idx + 1}`}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
              {note ? (
                <tr>
                  <td colSpan={columns.length + (rowAction ? 1 : 0) + (onRemoveRow ? 1 : 0)} className="px-2.5 pb-2">
                    {note}
                  </td>
                </tr>
              ) : null}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Applies a batch of dotted-path ops to a cloned copy of an invoice, creating
 * any missing intermediate objects along the way. Shared by the optimistic
 * update after a real save and by the live preview of not-yet-saved edits.
 */
function applyOpsToInvoice<T>(inv: T, ops: { path: string; value: any }[]): T {
  const next: any = structuredClone(inv);
  for (const { path, value } of ops) {
    const parts = path.split(".");
    let cur: any = next;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      if (cur[k] === undefined || cur[k] === null) cur[k] = {};
      cur = cur[k];
    }
    cur[parts[parts.length - 1]] = value;
  }
  return next;
}

/**
 * The optimistic update after a REAL save (not the live preview of unsaved
 * edits — see `previewInv`). Applies the ops, folds the touched paths into
 * `manuallyEditedPaths` the same way the server does, and clears
 * `verifiedAt`/`verifiedBy` on every save — mirroring `patchVendorInvoice`'s
 * unconditional reset (CR-14: "✓ Verified" means a human checked THESE
 * figures, and any edit makes that statement false).
 *
 * The server's reset was already unconditional; this function existing is the
 * fix. Before it, the client only learned the badge had lapsed on the next
 * full reload — which `savePaths` only triggers for a handful of fields with
 * a derived counterpart (invoice date, payment terms, pack math, ...). An
 * edit to, say, Vendor Name or PO Number left "✓ Verified" showing on screen
 * for an invoice the server had already un-verified.
 */
export function applyOptimisticInvoicePatch<T extends { manuallyEditedPaths?: string[] }>(
  inv: T,
  ops: { path: string; value: any }[],
  manual: boolean
): T {
  const next: any = applyOpsToInvoice(inv, ops);
  if (manual) {
    const touched = ops.map((o) => o.path).filter((p) => p.startsWith("extracted."));
    next.manuallyEditedPaths = Array.from(new Set([...((inv as any).manuallyEditedPaths || []), ...touched]));
  }
  next.verifiedAt = null;
  next.verifiedBy = null;
  return next;
}

/**
 * D86: whether a registered vendor is linked to this invoice, read the way the server reads it
 * (the invoice's vendorId, or the reconciliation's match). Client pre-check only; the owner is
 * the server's resolveInvoiceVendorId(doc, { allowNameGuess: false }).
 */
function hasLinkedVendor(inv: any): boolean {
  return !!(inv?.vendorId || inv?.vendorReconciliation?.matchedVendorId);
}

export function VendorInvoicesPage() {
  // Whether this user may change or forget the item memory: Master Catalogue
  // rights, the screen the routes gate those two actions on since MC-6 (D29).
  // Read here so the memory panel can hide the cells and the Forget button a
  // user would only get a 403 from.
  const catalogueRights = usePermission("products");
  const vendorRights = usePermission("vendors");
  // D110.3: creating a category from a sync card is the Settings → Item Categories act.
  const settingsPermission = usePermission("settings");
  // Which write actions this user has on invoices (raising a vendor return).
  const invoicePerms = usePermission("invoices");
  // The in-app confirmation (never window.confirm): Save on a stocked invoice's
  // money cells asks first (VI-6, D30).
  const confirm = useConfirm();
  // "Raise a vendor return" (D44 #53) opens a new return on the invoice.
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<VendorInvoiceStatus | "all">("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [tag] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // Newest invoice first by default. The list used to come back ordered by
  // `updatedAt`, so editing an old invoice pushed it above invoices uploaded
  // after it and the top of the list stopped meaning "just arrived".
  const [sort, setSort] = useState<VendorInvoiceSortKey>("createdAt");
  const [dir, setDir] = useState<SortDir>("desc");
  // What the rows on screen are ACTUALLY ordered by: the order of the last list that loaded. `sort` /
  // `dir` are what the last click asked for, and a request that fails leaves the old rows where they
  // were, so the column headings draw this and not those. "Total Price ▼" over rows no Total Price
  // request ever returned is a claim nothing backs (QA 2026-10-08). The request keeps the asked order,
  // so Refresh retries what was clicked and the arrow follows once it has loaded.
  const [loadedOrder, setLoadedOrder] = useState<{ sort: VendorInvoiceSortKey; dir: SortDir }>({ sort, dir });

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<VendorInvoice[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<Record<string, number> | null>(null);

  // D71: a row that was pending/processing on one 2.5s poll and is gone on the
  // next was auto-deleted after its first processing run failed (see the poll
  // effect below) — the user should not have to wait up to 30s for the
  // notification bell to say so on its own. `null` outside the provider (page
  // tests render this page without one), which is exactly when this feature
  // is a no-op.
  const notifications = useOptionalNotifications();
  const [failureNotice, setFailureNotice] = useState<{ invoiceId: string; message: string } | null>(null);
  const [vanishedIds, setVanishedIds] = useState<string[]>([]);

  // The one Trinidad VAT rate (owner decision 2026-09-24), admin-set in
  // Settings — read here ONLY for the two help texts below that used to state
  // "12.5%" as a literal. Null while it has not loaded / has never been set;
  // both texts fall back to rate-neutral wording rather than guess.
  const [vatRatePercent, setVatRatePercent] = useState<number | null>(null);
  useEffect(() => {
    getVatRate()
      .then((r) => setVatRatePercent(r.ratePercent))
      .catch((e) => {
        console.error("Error fetching VAT rate:", e);
        setVatRatePercent(null);
      });
  }, []);

  const [openId, setOpenId] = useState<string | null>(null);
  // The last Confirm & sync run on this screen (QA USC-BUG-0061): its packing
  // alerts, and that the catalogue result for it is still to come.
  const [confirmBanner, setConfirmBanner] = useState<{
    invoiceId: string;
    packingAlerts: ConfirmPackingAlert[];
  } | null>(null);
  // Edit: full editing. View (Open): read-only, no data entry at all.
  const [openMode, setOpenMode] = useState<"view" | "edit">("view");
  // Staged, unsaved cell edits for the currently open invoice, keyed by dotted
  // path (e.g. "extracted.lineItems.2.unitPrice"). Nothing here has hit the
  // server — that only happens when the top-level Save button is clicked.
  const [pendingEdits, setPendingEdits] = useState<Record<string, any>>({});
  const [savingPending, setSavingPending] = useState(false);
  // Ref, not state: two clicks in the same tick read the same stale `false` off
  // `savingPending`, so the disabled prop alone does not close the double-submit
  // window. `savingPending` stays as the "Saving…" label; this is the guard.
  const savingPendingRef = useRef(false);
  // Why the last save failed. A rejected PATCH used to produce no visible
  // change at all, so a reviewer could not tell a saved correction from a lost
  // one.
  const [saveError, setSaveError] = useState<string | null>(null);
  // D44 #53: the save refusal a vendor return answers (a quantity edit on a
  // stocked invoice), and the invoice it was for. Tied to the message it came
  // with, so a later, different error in the same banner never offers it.
  const [saveErrorReturn, setSaveErrorReturn] = useState<{ message: string; invoiceId: string } | null>(null);
  // D109.4: why a Reprocess click was refused (still processing, already in
  // Inventory, worker down). Nothing was being saved, so it is NOT `saveError`
  // ("Not saved." prefix, survives every invoice). Tied to the invoice and the
  // status it was refused at: shown in that invoice's open panel, or on its list
  // row when it is not the open one; gone when another opens (requestOpen), that
  // invoice's status moves (effect below), or the next Reprocess is clicked.
  const [reprocessNotice, setReprocessNotice] = useState<{
    message: string;
    invoiceId: string;
    status: string | undefined;
  } | null>(null);
  // Invoices whose one-time backfills have already been scheduled this mount,
  // keyed "split:<id>" / "due:<id>". A ref rather than state: it must not cause
  // a render, and it is read during one.
  const backfilledRef = useRef<Set<string>>(new Set());
  // The open invoice as we last saw it, so a background refetch that no longer
  // returns it cannot unmount the editor out from under a half-finished review.
  const openInvoiceRef = useRef<VendorInvoice | null>(null);
  // D95: after a destination save the OPEN invoice is patched at once - it may have left the
  // filtered list, so a reload alone would keep showing (and wording) the old destination.
  const applySavedDestination = async (id: string, destination: VendorInvoiceDestination) => {
    setItems((prev) => prev.map((i) => (i._id === id ? { ...i, destination } : i)));
    if (openInvoiceRef.current?._id === id) openInvoiceRef.current = { ...openInvoiceRef.current, destination };
    // A list request already in flight was fetched before the save and would carry the old
    // destination over the patch above. `load` supersedes it (latest wins), so it cannot.
    await load();
  };
  const pendingOps = useMemo(
    () => Object.entries(pendingEdits).map(([path, value]) => ({ path, value })),
    [pendingEdits]
  );
  // UC5 — Confirm & sync to Inventory is a separate, one-way action from Save.
  const [confirmingInventory, setConfirmingInventory] = useState(false);
  // The change card (Preet, 2026-09-10): the invoice whose catalogue changes are
  // being reviewed, and the plan behind them. `syncExcluded` is the switches —
  // lines that will NOT reach the Master Catalogue. Stock is unaffected either
  // way; every line is still received.
  const [syncPreviewFor, setSyncPreviewFor] = useState<string | null>(null);
  const [syncPreview, setSyncPreview] = useState<SyncPreview | null>(null);
  const [syncPreviewLoading, setSyncPreviewLoading] = useState(false);
  const [syncPreviewError, setSyncPreviewError] = useState<string | null>(null);
  const [syncPreviewStale, setSyncPreviewStale] = useState(false);
  const [syncExcluded, setSyncExcluded] = useState<number[]>([]);
  // D110.3: the category picked for each new row, lineIndex -> key ('' = none).
  // Starts on the AI's suggestion every time a preview loads.
  const [syncCategories, setSyncCategories] = useState<Record<number, string>>({});
  /**
   * Which cells the SERVER refused this invoice for, from the last failed
   * Confirm & sync — `{ invoiceId, byLine: Map<lineIndex, GateIssue> }`.
   *
   * Held against an invoice id rather than globally: the refusal describes ONE
   * invoice's figures, and painting it on whatever row happens to be open next
   * is worse than not painting it at all. A line's marks clear when a save
   * touches that line (it describes the values that were on screen when the
   * gate ran), and all of them when the next confirm starts. NOT on a reload:
   * the processing poll reloads every 2.5 s and would wipe them unseen.
   */
  const [gateIssues, setGateIssues] = useState<{
    invoiceId: string;
    byLine: Map<number, GateIssue>;
  } | null>(null);
  // "These flagged figures are right" — the answer to the grid's amber cells.

  // Every outcome this page has to put in front of a person — a refusal, a
  // precondition, a server failure, a one-way action asking first, a summary of
  // what moved — goes through one dialog. `window.alert` could show the words
  // but not the structure: a list of eight offending lines arrived as one
  // paragraph, and "the database is down" looked identical to "line 3 is wrong".
  // `id` forces a remount so a dialog replaced in place (confirm → result)
  // starts with its disclosure closed and its buttons re-enabled.
  type PageDialog = DialogContent & {
    id: number;
    onConfirm?: () => void | Promise<void>;
    confirmLabel?: string;
    cancelLabel?: string;
    busyLabel?: string;
  };
  const [dialog, setDialog] = useState<PageDialog | null>(null);
  const dialogSeq = useRef(0);
  const showDialog = useCallback((d: Omit<PageDialog, "id">) => {
    dialogSeq.current += 1;
    setDialog({ ...d, id: dialogSeq.current });
  }, []);
  /** Report a thrown API error in the dialog, told apart by what actually failed. */
  const showApiError = useCallback(
    (err: any, action: string, fallback?: string) => {
      dialogSeq.current += 1;
      setDialog({ ...describeApiError(err, { action, fallback }), id: dialogSeq.current });
    },
    []
  );

  function confirmDiscardPending(): boolean {
    if (!pendingOps.length) return true;
    return window.confirm("You have unsaved changes on this invoice. Discard them?");
  }

  /** Opens an invoice in the given mode, closing it on a second click of the same button. */
  function requestOpen(id: string, mode: "view" | "edit") {
    if (openId === id && openMode === mode) {
      if (!confirmDiscardPending()) return;
      setOpenId(null);
      setPendingEdits({});
      return;
    }
    if (openId && openId !== id && !confirmDiscardPending()) return;
    setReprocessNotice(null);
    setOpenId(id);
    setOpenMode(mode);
    setPendingEdits({});
  }

  // A LINK TO ONE INVOICE (lane 5, V5): `/vendor-invoices/<id>`, or the copilot's `?selected=<id>`. An
  // invoice used to open only from a list row's own button and be read back out of the list's own
  // `items`, so an address that named one opened the list and nothing (the route did not even exist), and
  // a page that did not hold the invoice (it is on page 3, or filtered out) could not have opened it.
  // The page now loads the record the address names ITSELF and opens it in edit mode (opening writes
  // nothing; only Save does). The copy goes into `items` (a row at the top, so Edit is a click away once it is closed) and
  // into `openInvoiceRef`, which keeps it on screen when the list reloads a page that does not hold it.
  // An invoice this page already has open (it asked for this very address itself, below) is not a new link.
  const linkedId = invoiceIdFromLocation(location);
  useEffect(() => {
    if (!linkedId || openId === linkedId) return;
    // An invoice with unsaved edits is not replaced by a link without asking, as a row click does not.
    if (openId && !confirmDiscardPending()) {
      navigate(invoicePath(openId), { replace: true });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const record = (await getVendorInvoiceById(linkedId)).data;
        if (cancelled) return;
        openInvoiceRef.current = record;
        setItems((prev) => (prev.some((i) => i._id === record._id) ? prev : [record, ...prev]));
        // Opens as an Edit, the same as the row's Edit button (owner, 2026-10-20): a link from Home
        // used to land view-only and the reviewer had to close it and click Edit.
        setOpenMode("edit");
        setPendingEdits({});
        setOpenId(record._id);
      } catch {
        // Not swallowed: said in a dialog, and the dead id leaves the address so a reload is not the same
        // dead end. No server sentence is shown (it is a 500 for a missing id).
        if (cancelled) return;
        showDialog({
          tone: "system",
          title: "Invoice not opened",
          summary:
            "That invoice could not be opened. The link may be wrong, the invoice may have been deleted, " +
            "or the server could not be reached.",
          confirmLabel: "Close",
        });
        navigate(invoicePath(null), { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Keyed on the address alone: reading `openId` and the helpers fresh each time it changes is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedId]);

  // ...and the address follows the invoice that is open, so the link a person copies from the bar is the
  // same link. Not before anything has been opened: a link in the bar is still being read then, and
  // stripping it here would cancel the very load above.
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (openId) hasOpenedRef.current = true;
    else if (!hasOpenedRef.current) return;
    const want = invoicePath(openId);
    if (location.pathname !== want) navigate(want, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [vendorMatchOpen, setVendorMatchOpen] = useState(false);
  const [vendorMatchSuppressedId, setVendorMatchSuppressedId] = useState<string | null>(null);
  const [vendorMatchSaving, setVendorMatchSaving] = useState(false);
  const [vendorMatchName, setVendorMatchName] = useState("");
  // Opt-in: also update the matched vendor's phone/address/credit-days from this
  // invoice. Off by default; confirming a match shouldn't silently overwrite
  // vendor master data unless asked to.
  const [vendorSyncDetails, setVendorSyncDetails] = useState(false);
  // The supplier picked in the shared dropdown, inside the match modal (D107.3). It lists the
  // WHOLE vendor register, so a supplier the matcher scored below its 0.55 candidate floor can
  // still be linked (TECH_DEBT #281: otherwise the only route was to create a duplicate), and a
  // supplier that is not on the list yet is created from the dropdown itself.
  const [vendorPick, setVendorPick] = useState<VendorSelection>(NO_VENDOR);

  // ? Upload modal state
  const [uploadOpen, setUploadOpen] = useState(false);
  // VI-4: the pages of ONE invoice, in the order they will be joined. One page
  // goes through upload-single exactly as before; two or more through upload-pages.
  const [uploadPages, setUploadPages] = useState<UploadPage[]>([]);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const [uploadFolderTag, setUploadFolderTag] = useState("");
  // "Has this invoice already been paid?" (D49). Empty until a person picks: the
  // upload refuses a file with no answer, and there is deliberately no default.
  const [uploadPayment, setUploadPayment] = useState<PaymentDraft>(EMPTY_PAYMENT_DRAFT);
  const [uploadPaidDateValid, setUploadPaidDateValid] = useState(true);
  const [uploadDestination, setUploadDestination] = useState<VendorInvoiceDestination>(DEFAULT_DESTINATION);
  const [uploadCaptureMode, setUploadCaptureMode] = useState<"auto" | "camera">("auto");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [reprocessId, setReprocessId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBlockedByStock, setDeleteBlockedByStock] = useState(false);

  // Module 1 — vessel assignment state
  const [assignOpenFor, setAssignOpenFor] = useState<string | null>(null);
  const [assignVesselName, setAssignVesselName] = useState("");
  const [assignColor, setAssignColor] = useState("");
  // Read inside the debounced colour-suggestion callback below instead of
  // `assignColor` itself. That effect's deps are `[assignVesselName,
  // assignOpenFor]` on purpose (re-suggesting on every colour click would
  // fight a reviewer's own pick), so its closure only refreshes when the
  // VESSEL NAME changes — a colour picked manually in the 300ms between a
  // keystroke and the debounced lookup landing was invisible to that stale
  // closure's `!assignColor` check, and the suggestion overwrote the pick the
  // reviewer had just made. The ref always reads the latest value.
  const assignColorRef = useRef(assignColor);
  useEffect(() => {
    assignColorRef.current = assignColor;
  }, [assignColor]);
  const [assignBusy, setAssignBusy] = useState(false);
  // B6 — the client order these goods were bought for. The whole order, not just
  // its id, because the modal has to NAME it (vessel, client, ref) and show the
  // client PO number the link exists to reach.
  const [assignOrder, setAssignOrder] = useState<ConfirmedOrderDto | null>(null);
  const [assignOrderLoading, setAssignOrderLoading] = useState(false);
  const [vesselMappings, setVesselMappings] = useState<VesselStickerMapping[]>([]);
  const [allowedColors, setAllowedColors] = useState<string[]>([]);
  const [vesselFilter, setVesselFilter] = useState("");
  const [destinationFilter, setDestinationFilter] = useState<"" | "united" | "expense">("");

  // D1 — Split modal state. Any number of vessels, not the original A/B pair.
  const [splitOpenFor, setSplitOpenFor] = useState<string | null>(null);
  // Which supplier's item memory is open. Held as the vendor rather than the
  // invoice: the memory belongs to the supplier, and the same panel is reached
  // from any of their invoices. `search` is set when it was opened from a line.
  const [memoryFor, setMemoryFor] = useState<{ id: string; name: string; search?: string } | null>(null);
  // VI-7 (D29): the stocked line whose packaging is being corrected.
  const [packagingFor, setPackagingFor] = useState<
    { invoiceId: string; invoiceNumber: string | null; lineIndex: number; line: any } | null
  >(null);
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);
  const [splitLoading, setSplitLoading] = useState(false);
  // Set when a SAVED split no longer describes the invoice — a line was deleted,
  // or a quantity edited below what was promised to a vessel.
  const [splitStale, setSplitStale] = useState<string | null>(null);
  // Monotonic, so a removed row's id is never handed out again and stale
  // allocations keyed on it can never re-attach to a new vessel.
  const splitRowSeq = useRef(0);
  const [splitRows, setSplitRows] = useState<SplitRow[]>([]);
  // line index -> how that line is divided
  const [splitBasis, setSplitBasis] = useState<Record<number, SplitBasis>>({});
  // line index -> row id, for lines going to one vessel untouched
  const [splitWhole, setSplitWhole] = useState<Record<number, string>>({});
  // `${lineIndex}:${rowId}` -> typed quantity, for lines shared by quantity
  const [splitQty, setSplitQty] = useState<Record<string, string>>({});
  // The spec as last saved, serialised. Compared against what is on screen to
  // decide whether "Send to inventory" is acting on the plan the user can see
  // — releasing goods against an edit that was never saved is the one mistake
  // this screen must not allow.
  const [splitSavedSpec, setSplitSavedSpec] = useState<string | null>(null);
  // rowId -> outcome of that vessel's release
  const [allocBusyRow, setAllocBusyRow] = useState<string | null>(null);
  const [allocByRow, setAllocByRow] = useState<Record<string, VesselAllocationResult>>({});
  const [allocErrByRow, setAllocErrByRow] = useState<Record<string, string>>({});

  const blankSplitRow = (): SplitRow => {
    splitRowSeq.current += 1;
    return {
      id: `sv${splitRowSeq.current}`,
      vessel: "",
      color: "",
      label: "",
      allocationId: null,
      sentAt: null,
      sentLines: null,
      linesToSend: null,
    };
  };

  // Load the invoice's existing split when the modal opens, so reopening shows
  // what is actually recorded rather than a blank form that would silently
  // replace it on save.
  useEffect(() => {
    if (!splitOpenFor) return;
    let cancelled = false;

    setSplitBasis({});
    setSplitWhole({});
    setSplitQty({});
    // Item 32 (QA retest): the dialog used to open on two FULLY blank rows even
    // when a vessel is already assigned to the invoice (the single-vessel
    // `assignment`, separate from a formal multi-vessel split below) — a
    // reviewer who had already named the ship retyped it here. The first row
    // defaults to that assignment; a saved split (below) still overwrites both
    // rows outright the moment it loads, so this is only what shows before —
    // or in place of — a real split record.
    const assignment = (items.find((x) => x._id === splitOpenFor) as any)?.assignment;
    setSplitRows(() => {
      const first = blankSplitRow();
      const second = blankSplitRow();
      if (assignment?.vesselName) {
        first.vessel = assignment.vesselName;
        first.color = String(assignment.stickerColor || "").toLowerCase();
      }
      return [first, second];
    });
    setSplitSavedSpec(null);
    setSplitError(null);
    setSplitStale(null);
    setAllocByRow({});
    setAllocErrByRow({});
    setSplitLoading(true);

    getVendorInvoiceVesselSplit(splitOpenFor)
      .then((res) => {
        if (cancelled) return;
        const allocations = res?.data?.allocations || [];
        setSplitStale(res?.data?.error || null);
        if (!allocations.length) return;

        const rows: SplitRow[] = [];
        const basis: Record<number, SplitBasis> = {};
        const whole: Record<number, string> = {};
        const qty: Record<string, string> = {};

        for (const a of allocations) {
          const row = blankSplitRow();
          row.vessel = a.vesselName || "";
          row.color = (a.stickerColor || "").toLowerCase();
          row.label = a.label || "";
          row.allocationId = a._id;
          row.sentAt = a.inventorySentAt || null;
          row.sentLines = a.inventorySentLines ?? null;
          row.linesToSend = a.inventoryLinesToSend ?? null;
          rows.push(row);

          for (const l of a.spec || []) {
            if (l.basis === "whole") {
              basis[l.index] = "whole";
              whole[l.index] = row.id;
            } else {
              basis[l.index] = l.basis;
              qty[`${l.index}:${row.id}`] = String(l.quantity ?? "");
            }
          }
        }

        setSplitRows(rows);
        setSplitBasis(basis);
        setSplitWhole(whole);
        setSplitQty(qty);

        // A split that was just loaded IS saved. Without this the form would
        // read as dirty on open and every "Send to inventory" button would be
        // disabled, with no edit to undo.
        const lineItems = (items.find((x) => x._id === splitOpenFor) as any)?.extracted?.lineItems;
        setSplitSavedSpec(
          JSON.stringify(buildSplitSpecs(Array.isArray(lineItems) ? lineItems : [], rows, basis, whole, qty))
        );
      })
      .catch((e: any) => {
        if (!cancelled) setSplitError(e?.message || "Could not load this invoice's split");
      })
      .finally(() => {
        if (!cancelled) setSplitLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // blankSplitRow only touches a ref, so it is stable enough not to re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitOpenFor]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  /**
   * Every invoice re-checked against its own lines, once per data change.
   *
   * The checks are cheap individually, but the list renders on every keystroke
   * in the search box, and both the row badge and the detail panel want the
   * same answer — so it is worked out once here and shared rather than
   * recomputed a few thousand times a second.
   */
  const invoiceReviews = useMemo(() => {
    const byId = new Map<string, { review: ReturnType<typeof reviewInvoice>; lineErrors: number }>();
    for (const inv of items) {
      const ex: any = inv.extracted || {};
      byId.set(inv._id, {
        // The upload date is what the invoice date's year is checked against (D34).
        review: reviewInvoice(ex, { uploadedAt: inv.createdAt, documentNote: textOnlyReadNote(inv), ...pageContextOf(inv), stocks: !isExpenseInvoice(inv) }),
        lineErrors: countFlags(Array.isArray(ex.lineItems) ? ex.lineItems : [], ex.validation, { stocks: !isExpenseInvoice(inv) }).errors,
      });
    }
    return byId;
  }, [items]);

  const emptyReview = useMemo(() => ({ review: reviewInvoice({}), lineErrors: 0 }), []);

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  // True while the NEWEST list request is still out (only the background poll asks).
  const loadingRef = useRef(false);
  // Only the newest request may write to state: every request that starts takes the next number.
  const loadSeqRef = useRef(0);
  // Why the list could not be loaded, shown above it; null while it can.
  const [loadError, setLoadError] = useState<string | null>(null);

  // Returns the freshly loaded rows (undefined when this call was skipped or
  // superseded by a newer one) — the poll effect below needs them to notice a
  // row vanishing between two ticks, which `items` state alone cannot give it
  // without the same stale-closure trap `loadRef` exists to avoid.
  //
  // LATEST WINS (lane 5, V6). This used to be single-flight: a reload asked for while a request
  // was already out (the 2.5 s poll's, the previous search's) was parked until that request came
  // back, and the OLD answer was painted first. On a list that answers slowly, clearing the search
  // or clicking a column sat behind whatever was in flight — "does nothing" — and behind a request
  // that never came back, for ever. A person's action now starts its request at once and is the
  // only one allowed to write the list. Only the BACKGROUND poll waits its turn: started while a
  // request is out it would supersede it, and on a list that answers slower than the poll interval
  // no answer would ever land.
  const load = useCallback(
    async (opts?: { background?: boolean }): Promise<VendorInvoice[] | undefined> => {
      const background = opts?.background === true;
      if (background && loadingRef.current) return undefined;

      const seq = (loadSeqRef.current += 1);
      loadingRef.current = true;
      // Only a person's action shows busy: a poll tick every 2.5 s must not flash it.
      if (!background) setLoading(true);

      let loaded: VendorInvoice[] | undefined;
      try {
        const res = await listVendorInvoices({ page, limit, status, q: debouncedQ, tag, vessel: vesselFilter, destination: destinationFilter, sort, dir });
        if (seq !== loadSeqRef.current) return undefined; // a newer request has started: its answer is the one to show
        loaded = res.items || [];
        setItems(loaded);
        setLoadedOrder({ sort, dir });
        setTotal(res.total || 0);
        setStats(res.stats || null);
        setLoadError(null);
      } catch (err) {
        // Said on screen, never only in the console: a failed read left the rows as they were and nothing
        // to say why. Only the status code, no server prose. Rethrown for callers that await it and word
        // their own refusal.
        if (seq === loadSeqRef.current) {
          setLoadError(
            typeof (err as any)?.status === "number"
              ? `The server answered with an error (HTTP ${(err as any).status}).`
              : "The server could not be reached."
          );
        }
        throw err;
      } finally {
        if (seq === loadSeqRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
      return loaded;
    },
    [page, limit, status, debouncedQ, tag, vesselFilter, destinationFilter, sort, dir]
  );

  // The newest `load`, for callers that outlive the render that created them.
  // The polling interval below is created once per `hasActive` flip, so calling
  // `load` directly captured that render's closure — including its `page`,
  // `status` and `debouncedQ`. Paging to 2 while an invoice was processing
  // silently repainted page 1 underneath the reader within 2.5 seconds, while
  // the pager still read "Items 26-50 of 120".
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    void load().catch(() => {
      // Not swallowed: `loadError` shows it above the list. Nobody awaits an effect.
    });
  }, [load]);

  /**
   * Click a column: same column reverses, a new column starts in the direction
   * that column is usually read in. Always back to page 1 — the row you were
   * looking at on page 3 is somewhere else entirely under a new order.
   *
   * "Same column" is the one the headings draw. That is the order last asked for,
   * except after a load that failed: the order asked for was never shown, the
   * headings still draw the last list that loaded, and "click to reverse" on that
   * heading has to reverse THAT. Clicking the very order that failed asks for what
   * was asked before, which changes nothing `load` watches, so it is asked by hand.
   */
  function onSort(key: VendorInvoiceSortKey) {
    const from = loadError ? loadedOrder : { sort, dir };
    const nextDir: SortDir =
      key === from.sort ? (from.dir === "asc" ? "desc" : "asc") : DESC_FIRST_SORTS.includes(key) ? "desc" : "asc";
    if (key === sort && nextDir === dir && page === 1) {
      void load().catch(() => {
        // Not swallowed: `loadError` shows it above the list.
      });
      return;
    }
    setPage(1);
    setSort(key);
    setDir(nextDir);
  }

  // Module 1: load vessel→colour mappings (once)
  useEffect(() => {
    let cancelled = false;
    listVesselStickerMappings()
      .then((res) => {
        if (cancelled) return;
        const data = (res as any)?.data || {};
        setVesselMappings(Array.isArray(data.mappings) ? data.mappings : []);
        setAllowedColors(Array.isArray(data.allowedColors) ? data.allowedColors : []);
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  // Pre-fill the Assign modal from the invoice's current assignment, once per open.
  //
  // This used to run in the render body as
  //   if (assignOpenFor && !assignVesselName && currentVessel) setTimeout(...)
  // which re-evaluated on EVERY render. Clearing the field to type a different
  // vessel made the condition true again, so the old name was written straight
  // back over what was being typed. Keyed on `assignOpenFor` it fires once, when
  // the modal opens, and never fights the user afterwards.
  useEffect(() => {
    if (!assignOpenFor) return;
    const inv = items.find((i) => i._id === assignOpenFor);
    setAssignVesselName(inv?.assignment?.vesselName || "");
    setAssignColor((inv?.assignment?.stickerColor || "").toLowerCase());

    // B6 — name the order it is already linked to. The row carries the id but
    // not the label, so this is one brief read when the modal opens (not per
    // row: the list would be a lookup per invoice for a string most rows never
    // show). Cancelled on close so a slow reply cannot land in a later modal.
    const linkedId = inv?.assignment?.confirmedOrderId || null;
    setAssignOrder(null);
    if (!linkedId) return;
    let cancelled = false;
    setAssignOrderLoading(true);
    fetchConfirmedOrderSummary(String(linkedId))
      .then((order) => { if (!cancelled) setAssignOrder(order); })
      .catch(() => {
        // The order could not be read — deleted, or no permission on that
        // screen. The modal says so rather than silently showing "not linked",
        // which would invite somebody to overwrite a link that is really there.
        if (!cancelled) setAssignOrder(null);
      })
      .finally(() => { if (!cancelled) setAssignOrderLoading(false); });
    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
    // `items` is deliberately not a dependency: the list refreshes on a poll, and
    // re-running this on a refresh would discard in-progress typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignOpenFor]);

  // When user types a vessel name in the Assign modal, suggest the existing colour if any
  useEffect(() => {
    if (!assignOpenFor || !assignVesselName) return;
    let cancelled = false;
    const t = setTimeout(() => {
      suggestStickerColorForVessel(assignVesselName)
        .then((res) => {
          if (cancelled) return;
          const suggested = (res as any)?.data?.stickerColor;
          if (suggested && !assignColorRef.current) setAssignColor(suggested);
        })
        .catch(() => { /* ignore */ });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignVesselName, assignOpenFor]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 450);
    return () => clearTimeout(t);
  }, [q]);

  // ? Stable polling for pending/processing (no interval churn)
  const hasActive = useMemo(
    () => items.some((x) => x.status === "pending" || x.status === "processing"),
    [items]
  );

  useEffect(() => {
    if (!hasActive) return;

    // The pending/processing rows as of the LAST tick (seeded from the render
    // that started polling) — reassigned after every tick, never read from
    // React state, so this stays correct across ticks without the stale-page/
    // status-filter closure trap `loadRef` exists for. A row missing from
    // `after` that this remembers as active did not get paged, filtered or
    // sorted away — only `load()`'s own filters do that, and every poll tick
    // asks with the SAME ones — so it was deleted out from under the list.
    let previouslyActive = items.filter((x) => x.status === "pending" || x.status === "processing");

    // loadRef, not load: the interval is created once and must always call the
    // CURRENT load, not the one from the render that started polling.
    const interval = setInterval(() => {
      void (async () => {
        // `background`: this tick waits for a request already out instead of superseding it. A failure is
        // shown by `loadError`, so it is not rethrown into the interval.
        const after = await loadRef.current({ background: true }).catch(() => undefined);
        if (!after) return; // skipped, superseded or failed — nothing to compare
        const afterIds = new Set(after.map((x) => x._id));
        const vanished = previouslyActive.filter((x) => !afterIds.has(x._id));
        previouslyActive = after.filter((x) => x.status === "pending" || x.status === "processing");
        if (!vanished.length) return;

        // D71: a fresh upload's failed first processing run deletes its own
        // row (BUSINESS_RULES — vendor-invoice upload failure). A vanished row
        // is only a CANDIDATE: under a Pending/Processing status filter a
        // SUCCESSFUL run leaves the list too. Nudge the bell (the backend
        // writes the failure notification before it deletes), and the effect
        // below shows the banner only once a matching failure notification is
        // actually there.
        void notifications?.refresh();
        setVanishedIds((prev) => [...prev, ...vanished.map((x) => x._id)]);
      })();
    }, 2500);

    return () => clearInterval(interval);
    // `items` deliberately not a dependency, same reason as `loadRef` above:
    // this reads it once to seed the FIRST tick's comparison and reassigns its
    // own local copy after every tick since. `notifications` (the context
    // value) is also excluded on purpose: it is a NEW object every time the
    // bell's own 30s poll or a refresh() ticks, and depending on it would
    // restart this interval — and reset `previouslyActive` from current
    // `items` — on that unrelated schedule instead of only on `hasActive`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActive]);

  // Shows the backend's own reason (describeProcessingFailure's message) for a
  // vanished row once its failure notification lands; a row that vanished
  // for any other reason never gets one, so never gets a banner.
  useEffect(() => {
    if (!vanishedIds.length || !notifications?.notifications?.length) return;
    const match = notifications.notifications.find(
      (n) => n.type === "vendor_invoice_failed" && vanishedIds.includes(String(n.metadata?.invoiceId || ""))
    );
    if (!match) return;
    const invoiceId = String(match.metadata?.invoiceId);
    setFailureNotice({ invoiceId, message: match.message });
    setVanishedIds((prev) => prev.filter((id) => id !== invoiceId));
  }, [notifications?.notifications, vanishedIds]);

  /**
   * Save one edited field.
   *
   * This used to recompute taxAmount and lineTotal here and send them as extra
   * patches. Two things were wrong with that:
   *
   *   - It gated on `line.taxRateDefaulted`, read from the copy in state — which
   *     still says "defaulted" at the moment you type a rate, because the save
   *     has not happened yet. So entering a VAT rate sent `taxAmount: null`,
   *     wiping the very value it was meant to fill in.
   *   - It set `lineTotal = base + tax`. On these invoices the printed extended
   *     price is tax-EXCLUSIVE, so that silently inflated a figure the document
   *     actually stated.
   *
   * Deriving one tax field from the other now happens in patchVendorInvoice, so
   * there is a single rule, it applies to every client, and it cannot disagree
   * with itself. Here we send the edit and re-read what the server made of it.
   */
  async function savePaths(invoiceId: string, ops: { path: string; value: any }[], manual: boolean = true) {
    if (!ops.length) return;

    // Optimistic: show the typed value immediately. Anything derived from it
    // comes back from the server, so it is not guessed at twice.
    //
    // Also fold the touched paths into manuallyEditedPaths right here, the same
    // way the server's patchVendorInvoice does — otherwise the blue highlight,
    // which reads manuallyEditedPaths off the invoice, vanished the instant
    // pendingEdits was cleared, and only came back on a full reload (which only
    // fires for the handful of fields with a derived counterpart).
    //
    // The pre-edit rows are kept so all of that optimism can be UNDONE. Without
    // it a failed PATCH left the grid showing values the server had rejected —
    // and now a blue highlight asserting they were saved — with no banner and no
    // error, until the next background refresh silently reverted them.
    let rollback: VendorInvoice[] | null = null;
    setItems((prev) => {
      rollback = prev;
      return prev.map((it) =>
        it._id !== invoiceId ? it : applyOptimisticInvoicePatch(it, ops, manual)
      );
    });

    let saved: Awaited<ReturnType<typeof patchVendorInvoice>> | null = null;
    try {
      saved = await patchVendorInvoice(invoiceId, ops, { manual });
    } catch (e: any) {
      if (rollback) setItems(rollback);
      // The server's own sentence, e.g. VENDOR_INVOICE_QUANTITY_BELOW_SPLIT
      // naming the vessel, or the stocked-invoice lock (D44 #20, #56).
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "Could not save those changes. They have been put back the way they were.";
      setSaveError(message);
      setSaveErrorReturn(refusalOffersVendorReturn(e) ? { message, invoiceId } : null);
      throw e;
    }

    // The gate's verdict described the values that were on screen when it
    // ran (see the comment on `gateIssues`); a successful save just changed
    // some of them. Only the LINES this save touched lose their marks — a
    // refusal naming three lines must not lose lines 2 and 3 when line 1 is
    // fixed (review, 2026-09-25) — and only on THIS invoice. Nothing clears it
    // on `load()`: the 2.5 s processing poll goes through it, and wiped the
    // cells to fix before the reviewer could see them.
    const savedLines = new Set<number>();
    for (const op of ops) {
      const m = /^extracted\.lineItems\.(\d+)\./.exec(op.path);
      if (m) savedLines.add(Number(m[1]));
    }
    if (savedLines.size) {
      setGateIssues((prev) => {
        if (!prev || prev.invoiceId !== invoiceId) return prev;
        const byLine = new Map(prev.byLine);
        savedLines.forEach((i) => byLine.delete(i));
        return byLine.size ? { invoiceId, byLine } : null;
      });
    }

    // Some edits have a counterpart the server just computed — the other half of
    // the rate/amount pair, the invoice's tax total, which now follows the
    // lines, and the due date, which follows the invoice date and terms. Reload
    // so those show rather than staying stale until the next refresh.
    // A line the vendor item memory had a hand in needs the same reload, for a
    // different reason: the server RETRACTS the memory's stamps on any cell a
    // reviewer types over (see linkProfileStamps), and those stamps are what put
    // the "we changed this" / "we disagree" note on the cell. Without the reload
    // the note survives the correction that answered it, and the grid goes on
    // explaining a value nobody is looking at any more.
    //
    // Keyed on the row carrying a profileMatch rather than on a list of learnable
    // field names: a match is present exactly when the memory wrote or annotated
    // something on that line, so there is no second copy of the server's field
    // list over here to fall out of step with it.
    // From `items`, NOT from `rollback`. `rollback` is assigned as a side
    // effect inside the setItems updater above, and React decides when to run
    // that — twice under StrictMode, and not necessarily before this line. When
    // it has not run, `rollback` is null, this reads as "no profiled line", and
    // the reload the block exists for is skipped: the retracted annotation then
    // stays on the cell the reviewer just corrected. `items` is the pre-edit
    // render's value in this closure, which is exactly the snapshot wanted.
    const editedRows = (items.find((it: any) => it._id === invoiceId) as any)?.extracted
      ?.lineItems;
    const touchesProfiledLine = ops.some(({ path }) => {
      const parts = path.split(".");
      if (parts[0] !== "extracted" || parts[1] !== "lineItems" || parts.length < 4) return false;
      return !!editedRows?.[Number(parts[2])]?.profileMatch;
    });

    // The first reason is an edit with a server-derived counterpart
    // (`DERIVED_LINE_FIELD_PATH`, the invoice date and terms).
    // The third reason, and the one `touchesProfiledLine` cannot see. Naming a
    // product is what RESOLVES it to a memory — that is the whole point of the
    // `settled` identity rung — so on a handwritten supplier's invoice the row
    // carries no `profileMatch` at the moment of the edit, and the pack unit,
    // units per case and unit size the server has just filled in from an
    // earlier invoice would sit unseen until the next background refresh
    // (Lawrence Marketing 25622 / 26129). The server says whether it wrote
    // anything; nothing here has to guess which fields could have made it.
    // The fourth: which lines are asked "are these fixed 2 KG packs?" is worked
    // out on the server (prod USC-BUG-0028), and an edit to a Unit Size or its
    // reading can change it. The server says so, the way it says `memoryApplied`.
    // The fifth: a Line Total edit on a line whose price was worked out from
    // its printed total makes the server work the price out again
    // (`derivedPriceChanged`).
    if (saveNeedsReload(ops, touchesProfiledLine, saved)) {
      await load();
    }
  }

  /**
   * D44 #53: open a new vendor return on this invoice — from the stocked grid's
   * note, or from a refusal that a vendor return answers.
   */
  function raiseVendorReturn(invoiceId: string) {
    if (!confirmDiscardPending()) return;
    setPendingEdits({});
    setDialog(null);
    navigate(newVendorReturnPath(invoiceId));
  }

  /**
   * Remove one line from the open invoice (prod USC-BUG-0021). The server
   * refuses anything past Confirm & sync, an invoice split across vessels or
   * matched to a PO, and a row that no longer matches what this screen showed.
   *
   * Removal does not move the totals (D44 #57): it is for a line that is not
   * goods. The question says so, and points goods that never arrived at a
   * quantity of 0, which does (#54). Asked in the app, like every other
   * question on this page.
   */
  async function removeLine(invoiceId: string, row: any, idx: number) {
    const lineLabel = `line ${row?.lineNo ?? idx + 1}`;
    if (pendingOps.length) {
      showDialog({
        tone: "blocked",
        title: `Save your ${pendingOps.length} unsaved edit${pendingOps.length === 1 ? "" : "s"} first`,
        summary: "Removing a line reads the saved invoice.",
        confirmLabel: "Got it",
      });
      return;
    }
    if (!(await confirm(lineRemovalQuestion(row, idx)))) return;
    try {
      await removeVendorInvoiceLine(invoiceId, idx, {
        lineNo: row?.lineNo ?? null,
        description: row?.description ?? null,
        itemName: row?.itemName ?? null,
      });
    } catch (err: any) {
      // LINE_REMOVAL_AFTER_CONFIRM: stocked since this screen loaded. The
      // refusal explains; the button is the way forward (#53).
      if (refusalOffersVendorReturn(err) && invoicePerms.canCreate) {
        showDialog({
          ...describeApiError(err, { action: `Remove ${lineLabel}` }),
          tone: "blocked",
          confirmLabel: "Raise a vendor return",
          cancelLabel: "Close",
          onConfirm: () => raiseVendorReturn(invoiceId),
        });
        return;
      }
      showApiError(err, `remove ${lineLabel}`);
      return;
    }
    // Everything this screen holds by line position described the invoice as it
    // was before the removal.
    setGateIssues(null);
    setSyncPreview(null);
    setSyncExcluded([]);
    await load();
  }

  /** Stages an edit locally without touching the server — the top Save button flushes these. */
  function stageEdit(path: string, value: any) {
    setPendingEdits((prev) => ({ ...prev, [path]: value }));
  }

  function stageEdits(ops: { path: string; value: any }[]) {
    setPendingEdits((prev) => {
      const next = { ...prev };
      for (const { path, value } of ops) next[path] = value;
      return next;
    });
  }

  /** Flushes every staged edit for the open invoice in one batched PATCH. */
  async function commitPendingEdits(invoiceId: string) {
    const sent = pendingOps;
    if (!sent.length) return;

    // Two fast clicks on Save sent the same edits twice (USC-BUG-0045). The
    // button's `disabled` prop is fed by `savingPending`, which is React state
    // and so only reaches the DOM on the next render — both handlers had
    // already read the same `false`. The ref is checked and set synchronously,
    // before any await, the way QuotationDetailPage's `confirmingOrderRef`
    // closes the same window around "Confirm order". Guarded here rather than
    // in the button so every future caller of this function is covered too.
    if (savingPendingRef.current) return;
    savingPendingRef.current = true;

    setSaveError(null);
    try {
      await savePaths(invoiceId, sent);
    } finally {
      savingPendingRef.current = false;
    }

    // Clear only what was actually SENT. The grid stays editable while a save
    // is in flight, and a blanket `setPendingEdits({})` discarded anything typed
    // during those seconds — never transmitted, and gone from the badge count
    // too, so the next refresh quietly reverted those cells to the server's
    // values with nothing having reported a problem.
    const sentPaths = new Set(sent.map((op) => op.path));
    setPendingEdits((prev) => {
      const next: Record<string, any> = {};
      for (const [path, value] of Object.entries(prev)) {
        if (!sentPaths.has(path)) next[path] = value;
      }
      return next;
    });
  }

  function StatCard(props: { label: string; value: number | undefined; onClick?: () => void }) {
    const { label, value, onClick } = props;
    return (
      <button
        onClick={onClick}
        className="text-left rounded-xl bg-white border border-slate-200 p-4 hover:bg-slate-50"
      >
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl text-slate-900 font-semibold">{value ?? "-"}</div>
      </button>
    );
  }

  function resetUploadModal() {
    setUploadPages([]);
    setUploadFolderTag("");
    setUploadPayment(EMPTY_PAYMENT_DRAFT);
    setUploadDestination(DEFAULT_DESTINATION);
    setUploadCaptureMode("auto");
    setUploadBusy(false);
    setUploadMsg(null);
  }

  async function doUploadSingle() {
    if (!uploadPages.length) {
      setUploadMsg("Please select a PDF or image file first.");
      return;
    }
    // Belt and suspenders: the file input's onChange already refuses a bad
    // type at selection time, but this button is the last stop before the
    // request goes out, so it checks again rather than trusting that nothing
    // else could have added a page.
    const badPage = uploadPages.find((p) => !isAcceptedInvoiceUploadFile(p.file));
    if (badPage) {
      setUploadMsg(
        `"${badPage.file.name}" is not a PDF or an image (${badPage.file.type || "unrecognised type"}). Choose a PDF, JPG, PNG or WEBP file.`
      );
      return;
    }

    // The same rule the server applies, asked first so the person is told here what
    // is missing. A part-paid amount is typed on the review screen, once the
    // invoice has been read, so it is not asked for at upload.
    const payment = checkPaymentDraft(uploadPayment, { includeAmount: false, dateRefused: !uploadPaidDateValid });
    if (!payment.ok) {
      setUploadMsg(payment.message);
      return;
    }

    const controller = new AbortController();
    uploadAbortRef.current = controller;
    setUploadBusy(true);
    setUploadMsg(null);
    const uploadLabel = uploadPages.length === 1 ? uploadPages[0].file.name : `${uploadPages.length} files`;

    try {
      let result: Awaited<ReturnType<typeof uploadVendorInvoiceSingle>>;
      if (uploadPages.length === 1) {
        result = await uploadVendorInvoiceSingle({
          file: uploadPages[0].file,
          folderTag: uploadFolderTag || undefined,
          payment: payment.input,
          destination: uploadDestination,
          signal: controller.signal,
        });
      } else {
        // VI-4: photos are turned upright, shrunk and made JPEG here; the server
        // joins the pages into one PDF in this order.
        const prepared: File[] = [];
        for (const page of uploadPages) {
          if (controller.signal.aborted) throw new DOMException("Upload cancelled", "AbortError");
          prepared.push(await pageForUpload(page.file));
        }
        result = await uploadVendorInvoicePages({
          pages: prepared,
          folderTag: uploadFolderTag || undefined,
          payment: payment.input,
          destination: uploadDestination,
          signal: controller.signal,
        });
      }
      const { item: created, isDuplicate } = result;

      setUploadMsg(
        isDuplicate
          ? `This invoice has already been uploaded (as "${created?.source?.fileName || uploadLabel}"). The duplicate file was discarded; nothing was reprocessed.`
          : `Uploaded: ${created?.source?.fileName || uploadLabel}. Status: ${String(
              created?.status || "pending"
            ).toUpperCase()}`
      );

      if (!isDuplicate) {
        await load();

        if (created?._id) {
          setOpenId(created._id);
          setOpenMode("edit");
          setPendingEdits({});
        }

        setTimeout(() => {
          setUploadOpen(false);
          resetUploadModal();
        }, 700);
      }
      // Duplicate: leave the modal open with the message visible. It's easy to miss
      // at a glance, so it stays up until the user explicitly closes it (Cancel/×/Escape)
      // instead of vanishing on its own before it's been read.
    } catch (e: any) {
      setUploadMsg(
        e?.name === "AbortError"
          ? "Upload cancelled. If the invoice still appears in the list, it had already reached the server before you cancelled."
          : destinationErrorMessage(e, e?.message || "Upload failed.")
      );
    } finally {
      uploadAbortRef.current = null;
      setUploadBusy(false);
    }
  }

  async function doReprocess(id: string) {
    if (!id) return;
    setReprocessId(id);
    setSaveError(null);
    setReprocessNotice(null);
    try {
      await reprocessVendorInvoice(id);
      await load();
    } catch (e: any) {
      // Was uncaught: a rejected reprocess (rate limit, worker down, no
      // permission, and now the server's "already processing" / "already in
      // Inventory" refusals) cleared the spinner, never reloaded, and left the
      // status pill reading "Failed" with no explanation — so the button looked
      // simply dead and got clicked again.
      setReprocessNotice({
        message: e?.response?.data?.message || e?.message || "Could not reprocess this invoice.",
        invoiceId: id,
        status: items.find((it) => it._id === id)?.status,
      });
    } finally {
      setReprocessId(null);
    }
  }

  async function doDelete(id: string, opts?: { force?: boolean }) {
    if (!id) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteVendorInvoice(id, opts);
      setDeleteId(null);
      setDeleteBlockedByStock(false);
      setOpenId((cur) => {
        if (cur !== id) return cur;
        setPendingEdits({});
        return null;
      });
      await load();
    } catch (e: any) {
      // Deleting returns the stock this invoice added, and the server refuses
      // when that stock has already been transferred or issued. Without this
      // catch the rejection went nowhere and the button just looked broken.
      const raw =
        e?.response?.data?.message ||
        e?.message ||
        "Could not delete this invoice. Please try again.";
      // Older builds surfaced the whole response body as the message, so a raw
      // JSON envelope could reach the screen. Unwrap it rather than show braces.
      setDeleteError(unwrapErrorMessage(raw));
      // Offer the override only for the one failure it applies to.
      setDeleteBlockedByStock(/cannot be returned/i.test(String(raw)));
    } finally {
      setDeleteBusy(false);
    }
  }

  const openInvoice = useMemo(
    () => items.find((it) => it._id === openId) || null,
    [items, openId]
  );

  // D109.4: a reprocess refusal described the invoice as it was (e.g. still
  // processing); once its status moves the sentence is stale, so it goes.
  useEffect(() => {
    if (!reprocessNotice) return;
    const current = items.find((it) => it._id === reprocessNotice.invoiceId);
    if (current && current.status !== reprocessNotice.status) setReprocessNotice(null);
  }, [items, reprocessNotice]);

  const currentVendorMatch = useMemo(() => {
    if (!openInvoice?.vendorReconciliation) return null;
    return openInvoice.vendorReconciliation;
  }, [openInvoice]);

  // D86: Confirm & sync refuses an invoice no vendor is linked to. The way out is
  // the vendor-match popup (D107.3: pick the supplier from the list, or create it there).
  const vendorNotLinkedDialog = (invoice?: VendorInvoice | null) => ({
    tone: "blocked" as const,
    title: "Link a vendor first",
    summary: "This invoice is not linked to a vendor in the vendor list, so its bill would belong to nobody.",
    hint: "Pick the supplier from the list, or create it there. Nothing was added to stock.",
    confirmLabel: "Link a vendor",
    cancelLabel: "Cancel",
    onConfirm: () => openVendorMatch(invoice),
  });

  // The vendor-match popup (Link this invoice to a vendor), from any of its entry points.
  const openVendorMatch = (invoice?: VendorInvoice | null) => {
    resetVendorMatchForm(invoice);
    setVendorMatchSuppressedId(null);
    setVendorMatchOpen(true);
  };

  const resetVendorMatchForm = (invoice?: VendorInvoice | null) => {
    const extracted = invoice?.vendorReconciliation?.extractedVendor || invoice?.extracted?.vendor || {};
    const best = invoice?.vendorReconciliation?.bestCandidate || null;
    setVendorMatchName(String(extracted?.name || extracted?.vendorName || best?.vendorName || "").trim());
    setVendorSyncDetails(false);
    setVendorPick(NO_VENDOR);
  };

  useEffect(() => {
    if (!openId) {
      setVendorMatchOpen(false);
      setVendorMatchSuppressedId(null);
      return;
    }

    if (vendorMatchSuppressedId === openId) return;

    const invoice = items.find((it) => it._id === openId);
    if (!invoice) return;

    const state = invoice?.vendorReconciliation?.state;
    if (state === "suggested" && !vendorMatchOpen) {
      resetVendorMatchForm(invoice);
      setVendorMatchOpen(true);
    }
  }, [items, openId, vendorMatchOpen, vendorMatchSuppressedId]);

  const closeVendorMatch = () => {
    setVendorMatchOpen(false);
    setVendorMatchSuppressedId(openId);
  };

  const resolveVendorMatch = async (action: "same_existing", vendorId?: string) => {
    if (!openId) return;
    setVendorMatchSaving(true);
    try {
      const res: any = await reconcileVendorInvoice(openId, {
        action,
        vendorId,
        syncVendorDetails: vendorSyncDetails,
      });
      await load();
      setVendorMatchOpen(false);
      setVendorMatchSuppressedId(null);

      const sync = res?.data?.vendorSync;
      if (vendorSyncDetails) {
        if (sync?.error) {
          // Half-done, and worth saying which half: the match itself stuck.
          showDialog({
            tone: "system",
            title: "Vendor matched, but their details were not updated",
            summary:
              "The invoice is linked to the supplier. Copying this invoice's contact details onto " +
              "the supplier record failed, so their master data is unchanged.",
            hint: "Edit the supplier directly if those details need updating.",
            details: String(sync.error),
            confirmLabel: "Close",
          });
        } else if (sync?.applied && sync.changes && Object.keys(sync.changes).length) {
          showDialog({
            tone: "success",
            title: "Vendor matched and details updated",
            itemsHeading: "Updated from this invoice",
            items: Object.entries(sync.changes).map(([field, ch]: [string, any]) => ({
              label: field,
              text: `${ch.from || "(empty)"} → ${ch.to}`,
            })),
            confirmLabel: "Done",
          });
        }
      }
      // D35 (VI-1): say that the printed name was learned, so the reviewer knows
      // the next invoice with the same letterhead will find this supplier.
      if (!vendorSyncDetails && res?.data?.aliasLearned) {
        showDialog({
          tone: "success",
          title: "Vendor saved",
          summary:
            `This invoice is filed under ${res.data.vendorName || "the chosen supplier"}. ` +
            `Invoices printing "${vendorMatchName || res.data.aliasLearned}" will be matched to this supplier from now on.`,
          confirmLabel: "Done",
        });
      }
    } catch (e: any) {
      showApiError(e, "Vendor match", "The supplier could not be reconciled.");
    } finally {
      setVendorMatchSaving(false);
    }
  };

  /**
   * UC5 — run the sync itself, once the user has agreed to it in the dialog.
   *
   * Both endings replace the confirmation dialog in place rather than closing
   * it: a one-way action that reports nothing back leaves the reviewer guessing
   * whether stock moved, and the failure path has to say plainly that it did
   * not — the server releases its claim on any error, so the invoice stays
   * confirmable and the right next step is to fix and retry.
   */
  /**
   * Load the change card for an invoice, and say whether it can be shown.
   *
   * `false` means fall back to the plain confirmation dialog, which is the
   * decided behaviour for a reviewer without Products view: the cards show
   * catalogue costs, so that route asks for both permissions and answers 403
   * to anyone holding only the first (Preet, 2026-09-10).
   */
  const openSyncPreview = async (invoiceId: string, opts: { stale?: boolean } = {}) => {
    setSyncPreviewFor(invoiceId);
    setSyncPreviewLoading(true);
    setSyncPreviewError(null);
    setSyncPreviewStale(!!opts.stale);
    try {
      const res: any = await getVendorInvoiceSyncPreview(invoiceId);
      const data: SyncPreview = res?.data;
      if (!data) throw new Error("The preview came back empty.");
      setSyncPreview(data);
      // Whatever an earlier confirm stored is where the switches start.
      setSyncExcluded(Array.isArray(data.excludedLines) ? data.excludedLines : []);
      setSyncCategories(startingCategoryChoices(data));
      return true;
    } catch (e: any) {
      const status = Number(e?.status ?? e?.statusCode ?? 0);
      if (status === 403) {
        setSyncPreviewFor(null);
        setSyncPreview(null);
        return false;
      }
      // Anything else is worth saying out loud, in the panel rather than as a
      // dialog over it: the reviewer can close it and confirm the old way.
      // The server's reason, not a generic sentence: "VAT rate is not set in
      // Settings" is something the reviewer can act on (browser QA, 2026-09-25).
      setSyncPreview(null);
      setSyncPreviewError(
        describeApiError(e, {
          action: "Loading the catalogue preview",
          fallback: "The preview of the catalogue changes could not be loaded.",
        }).summary || "The preview of the catalogue changes could not be loaded."
      );
      return true;
    } finally {
      setSyncPreviewLoading(false);
    }
  };

  const closeSyncPreview = () => {
    setSyncPreviewFor(null);
    setSyncPreview(null);
    setSyncPreviewError(null);
    setSyncPreviewStale(false);
  };

  const runInventorySync = async (
    invoiceId: string,
    allowDuplicate = false,
    autoConfirmPaths: string[] = []
  ) => {
    setConfirmingInventory(true);
    // The previous refusal described the figures as they were then. Whatever
    // this attempt decides, the old verdict stops being true the moment it
    // starts — leaving it up would mark cells the gate may no longer object to.
    setGateIssues(null);
    try {
      // Pressing this button IS the reviewer vouching for the invoice: they are
      // moving goods on it. So the amber cells it still carries are recorded as
      // checked here, and the separate "Confirm checked values" button is gone.
      //
      // This is not cosmetic. `recordFromConfirmedInvoice` runs post-commit
      // inside the sync below and reads `reviewConfirmedPaths` to decide each
      // field's weight, and `confirmed` is the bar the memory
      // demands before it will fill or suggest anything (`findProfileVatRate`,
      // and the auto-correct in `applyProfileToLine`). Without this the only
      // route above `seen` was somebody retyping a value the AI got right, so
      // the memory learned from mistakes and never from successes.
      //
      // ONE REQUEST. The paths travel WITH the sync rather than in a
      // confirm-review call before it, and the server records them in the same
      // write that claims the invoice. The two-request version had a state with
      // no good exit: the confirm fails, the sync succeeds, and the reviewer's
      // confirmations are gone with no second button left to retry from. The
      // only choices there were to swallow the error or to refuse a stock-in
      // over provenance, and this has neither.
      const res: any = await confirmVendorInvoiceInventory(invoiceId, {
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
        ...(autoConfirmPaths.length ? { confirmPaths: autoConfirmPaths } : {}),
        // The change card's switches, and the rows those cards were drawn from.
        // Sent on the retries too (the override dialogs call this again), so
        // "Confirm anyway" cannot quietly sync a line somebody switched off, and
        // a row that moved in the meantime is still refused.
        ...(syncPreviewFor === invoiceId && syncPreview
          ? {
              excludedLines: syncExcluded,
              previewedRows: syncPreview.previewedRows,
              // D110.3: the category of each included new row, as picked on its card.
              categoryChoices: categoryChoicesToSend(syncPreview, syncCategories, syncExcluded),
            }
          : {}),
      });

      // OUTSIDE THE TRY THAT REPORTS A SYNC FAILURE, deliberately. The confirm
      // above is irreversible: by the time it resolves, stock has moved. Refreshing
      // the list is housekeeping, and it used to sit inside the same try, so a
      // failed refresh reported "the sync could not be completed, so nothing was
      // added to stock" over a stock-in that had already committed, and invited a
      // retry the server would then refuse as a duplicate.
      let refreshFailed: string | null = null;
      try {
        await load();
      } catch (refreshErr: any) {
        refreshFailed = String(refreshErr?.message || refreshErr || "unknown error");
        console.warn(`[VendorInvoices] refresh after a successful sync failed for ${invoiceId}:`, refreshErr);
      }

      // The cards described a change that has now happened, so the panel closes
      // with the sync it was reviewing.
      closeSyncPreview();
      // And the invoice closes with it (2026-09-25): its work is done, and the
      // catalogue / packing result follows to the top of the list page.
      // Nothing is lost - Confirm & sync refuses while edits are unsaved.
      setOpenId(null);
      setPendingEdits({});

      const data: ConfirmInventoryResult = res?.data || {};
      const items: DialogItem[] = [];
      // D116.3: the stock went in, but no bill was raised: another invoice of this supplier and number
      // already has one. Said first, because it is the one line here that changes what is owed.
      if (data.billSkipped) {
        items.push({
          label: "Bill",
          text: "No bill was raised: this invoice duplicates another invoice of the same supplier, whose bill already stands.",
        });
      }
      // D171.3: tagged to an order with one PO from this supplier, so it attached itself to that PO.
      if (data.attachedPo) items.push({ label: "Purchase order", text: `Attached to ${data.attachedPo.poNumber}` });
      // D185.5: or why it did not, when that is the person's to settle.
      const poNote = notAttachedMessage(data.notAttached);
      if (poNote) items.push({ label: "Purchase order", text: poNote });
      // From the confirm's own answer, not from the list (which a filter may have dropped the row from).
      const wording = destinationWording({ destination: data.destination });
      if (wording.stocks && data.stockIn) {
        const created = Number(data.stockIn.created || 0);
        const skipped = Number(data.stockIn.skipped || 0);
        items.push({
          label: "Stock",
          text: `${created} stock-in movement${created === 1 ? "" : "s"} created`,
          note: skipped ? `${skipped} line(s) skipped` : undefined,
        });
      }
      if (wording.stocks && data.inventory) {
        items.push({
          label: "Inventory",
          text: `${Number(data.inventory.upserted || 0)} item(s) added or updated`,
          note: data.inventory.skipped ? `${data.inventory.skipped} skipped` : undefined,
        });
      }
      if (wording.stocks && data.catalogue) {
        items.push({
          label: "Catalogue",
          text: `${Number(data.catalogue.upserted || 0)} Master Catalogue entr${
            Number(data.catalogue.upserted || 0) === 1 ? "y" : "ies"
          } added or updated`,
          note: data.catalogue.skipped ? `${data.catalogue.skipped} skipped` : undefined,
        });
      }

      // QA USC-BUG-0061: the packing check is reported on EVERY confirm, and the
      // same list stays at the top of the invoice with the catalogue result.
      const packingAlerts: ConfirmPackingAlert[] = Array.isArray(data.packingAlerts) ? data.packingAlerts : [];
      // A company expense has no packing check and no catalogue sync to wait for.
      if (wording.stocks) {
        items.push({
          label: "Packing",
          text: packingAlerts.length
            ? `${packingAlerts.length} packing alert${packingAlerts.length === 1 ? "" : "s"} to check`
            : "No packing alerts",
          note: packingAlerts.length
            ? packingAlerts.map((a) => `Line ${a.lineNo}: ${a.message}`).join(" ")
            : undefined,
        });
        setConfirmBanner({ invoiceId, packingAlerts });
      }

      if (refreshFailed) {
        items.push({
          label: "This screen",
          text: "Could not be refreshed, so the list below may still show the old status.",
          note: refreshFailed,
        });
      }

      showDialog({
        tone: "success",
        title: wording.doneTitle,
        summary: wording.doneSummary,
        itemsHeading: items.length ? "What changed" : undefined,
        items,
        hint: refreshFailed
          ? "The sync itself succeeded. Reload the page to see the current state; do not confirm again."
          : wording.doneHint,
        confirmLabel: "Done",
      });
    } catch (e: any) {
      // One refusal out of all of them is answerable rather than fatal: an
      // invoice sharing its number and vendor with one already stocked in. The
      // server has done nothing, and the reviewer is the only one who can say
      // whether it is the same delivery or a vendor reusing a number — so offer
      // the override instead of making them find another route to it.
      // Switch on the server's stable code (apiClient attaches it from the
      // envelope); the prose regex stays only as a fallback for deploy skew
      // while an older backend without codes is still live.
      const raw = String(e?.response?.data?.message || e?.message || "");
      const isStockedDuplicate =
        e?.code === "DUPLICATE_ALREADY_STOCKED" ||
        /already been stocked into Inventory/i.test(raw);
      if (!allowDuplicate && isStockedDuplicate) {
        showDialog({
          ...describeApiError(e, { action: "Confirm & sync to Inventory" }),
          // "confirm", not "error": the server's refusal is a question for the
          // reviewer, and the dialog's job here is to ask it, not to close it off.
          tone: "confirm",
          confirmLabel: "CONFIRM ANYWAY",
          cancelLabel: "CANCEL",
          busyLabel: "Confirming...",
          // THE PATHS GO WITH THE RETRY. They used to be left off because the
          // confirmation was a separate request that had already landed by the
          // time the sync refused. It is not separate any more: it rides with
          // this call and is written by the claim, and the duplicate refusal
          // happens BEFORE the claim, so nothing was recorded. Dropping them
          // here would stock the invoice on an override and lose the reviewer's
          // confirmations, which is the failure this change removed one layer up.
          onConfirm: () => runInventorySync(invoiceId, true, autoConfirmPaths),
        });
        return;
      }
      // A pack-code unit ("C-12", "BALE-48", a bare "24") is NOT answerable any
      // more. It used to offer CONFIRM ANYWAY, and on prod that stored the junk
      // as the catalogue row's unit (USC-BUG-0022, 0026). Owner's decision,
      // 2026-09-14: no override. UNIT_NOT_RECOGNISED falls through to the
      // ordinary refusal below, which marks the offending cells.
      //
      // The gate named the lines and the cells. Keep them so the grid can mark
      // the offending cells instead of leaving the reviewer to find "Line 7"
      // by counting — the payload has always been sent, and until the transport
      // was fixed it was being dropped at the client boundary.
      // A catalogue row one of the cards was drawn from moved while the
      // reviewer was reading. Nothing was written; reload the cards rather than
      // confirm against a picture that is out of date.
      if (e?.code === "SYNC_PREVIEW_STALE") {
        await openSyncPreview(invoiceId, { stale: true });
        return;
      }
      const lines = Array.isArray(e?.validationErrors) ? e.validationErrors : [];
      const byLine = new Map<number, GateIssue>();
      for (const l of lines) {
        if (typeof l?.index !== "number" || !Array.isArray(l?.cells) || !l.cells.length) continue;
        byLine.set(l.index, {
          cells: l.cells,
          reason: String(e?.message || "This line was refused by Confirm & sync."),
        });
      }
      setGateIssues(byLine.size ? { invoiceId, byLine } : null);

      // D86: no vendor linked. Same dialog as the button's own check.
      if (e?.code === "VENDOR_NOT_LINKED") {
        showDialog(vendorNotLinkedDialog(items.find((x) => x._id === invoiceId)));
        return;
      }

      // D110.3: a category pick the server could not use (deleted meanwhile).
      // The cards are reloaded so the list is current; nothing was written.
      if (e?.code === "CATEGORY_CHOICE_INVALID") {
        showDialog({
          tone: "blocked" as const,
          title: "Check the categories",
          summary: "One of the categories picked for a new item is no longer in the list.",
          hint: "The cards were reloaded. Pick the categories again and confirm. Nothing was added to stock.",
        });
        await openSyncPreview(invoiceId);
        return;
      }

      // D49: the "already paid?" answer is what stopped it. Worded from the code, not the server's sentence.
      const answerDialog = paymentAnswerDialog(e?.code);
      if (answerDialog) {
        showDialog(answerDialog);
        return;
      }

      showApiError(
        e,
        "Confirm & sync to Inventory",
        "The sync could not be completed, so nothing was added to stock."
      );
    } finally {
      setConfirmingInventory(false);
    }
  };

  // The attachment route is signature-gated (it used to be public — any
  // guessed ObjectId downloaded the PDF), so the URL is minted on click by an
  // authenticated endpoint and opened immediately.
  // Only the id is read, so a duplicate twin (which need not be on this page of
  // the list) can open its document too (VI-5).
  const openInvoiceAttachment = async (invoice: Pick<VendorInvoice, "_id"> | null, inline: boolean) => {
    if (!invoice) return;
    try {
      const signedPath = await fetchVendorInvoiceAttachmentUrl(invoice._id);
      const url = buildAbsoluteFileUrl(signedPath);
      if (inline) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        // Content-Disposition on the server makes this a download, not a navigation.
        window.location.assign(url);
      }
    } catch (e: any) {
      showApiError(e, "Open attachment", "The attachment link could not be created — try again.");
    }
  };

  const accept = ".pdf,.png,.jpg,.jpeg,.webp";

  // normalizeUnit used to be redeclared here as a cut-down copy of the server's —
  // it knew L/ML/KG/G and nothing else, so this page and the parser disagreed
  // about CASE, LBS, PCS, BAG, BOX and TIN, and both ended in a catch-all that
  // accepted "Z" as a unit. Both now share ../utils/unitVocabulary.
  // The pack in the words is read by the one reader pricing uses (utils/packConversion, D157.12).

  const getQtyFromItem = backfillQuantity;

  // `totals` is needed as well as the lines: whether a missing tax amount can be
  // filled in is proven against the tax total the invoice itself prints.
  const backfillSplitFields = async (
    invoiceId: string,
    lineItems: any[],
    totals?: any,
    // Paths a person has already typed into. Backfill fills blanks, and a blank
    // a person MADE is not a blank to fill — see the packingNote branch below.
    editedPaths: string[] = []
  ) => {
    const ops: { path: string; value: any }[] = [];
    const nextItems = structuredClone(lineItems);
    const edited = new Set(editedPaths);
    const isManuallyEditedPath = (path: string) => edited.has(path);

    nextItems.forEach((li: any, idx: number) => {
      const base = `extracted.lineItems.${idx}`;
      // A blank nobody made (`mayBackfillCell`). Asked of the line as stored,
      // before any `set()` below: every derivation reads what the line will
      // hold, so a cleared Quantity cannot come back through Total Units either.
      const may = (field: string) => mayBackfillCell(li, field, `${base}.${field}`, edited);
      const rawDesc = String(li?.description || "");
      // Repair BEFORE parsing. "140Z" parses as 140 + "Z"; there is no way to
      // recover the real value once those two have been split apart.
      const { text: desc, repaired } = repairUnitOcr(rawDesc);
      const qtyNum = getQtyFromItem(li);

      const caseBag = repairUnitOcr(String(li?.caseBagQuantity || "").trim()).text;
      const caseBagMatch = caseBag.match(/(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/);
      const caseBagUow = caseBagMatch ? normalizeUnit(caseBagMatch[2]) : "";
      // D157.12: a count read from the words alone ("COKE 12PK") is a suggestion, never Units / Case
      // or Total Units; those come from the unit cell or a person. Mirrors the processor.
      const words = packFromWords(desc, li?.uow || li?.uom || li?.unit || caseBagUow);
      const parsed = words?.countOnly ? null : words;
      const packCount = parsed?.packCount || null;
      const sizeParsed = parseUnitSize(desc);
      const unitSize = parsed?.unitSize || sizeParsed?.unitSize || null;
      const unitUom = parsed?.unitUom || sizeParsed?.unitUom || "";

      const itemName = !isEmptyValue(li?.itemName) ? li?.itemName : cleanItemName(rawDesc);
      // The box count the supplier printed beside the product, taken back out
      // of the name by the same owner the name comes from. Falls back to a
      // case-weight grid that RECONCILES — nine weights adding up to the
      // quantity billed are nine boxes — and to nothing at all otherwise,
      // because a box count cannot be worked out from a weight when the boxes
      // weigh different amounts. Mirrors vendorInvoiceProcessor exactly.
      const packingNote =
        splitItemName(rawDesc).packingNote ||
        (li?.caseWeightCheck?.ok && Array.isArray(li?.caseWeights) && li.caseWeights.length
          ? `${li.caseWeights.length} cases`
          : "");
      // No `|| "PACKS"` on the end of that chain, and the absence is the fix:
      // a supplier who printed no unit anywhere used to have one invented for
      // them here as well as in the processor, which then read as something the
      // paper said (ClickUp 86d49xaxm). `set()` below only writes a non-empty
      // value, so a line with nothing to read keeps an empty Pack Unit — which
      // is what the grid flags and what the item memory's fill path is gated on.
      // Through the processor's own rule (`invoiceLineMath.seedPackUnit`): this
      // said it "mirrors applySplitToLineItems exactly" and wrote the printed
      // cell as typed, so a legacy "CS10PK" stayed unreadable and lost its ten.
      const { splitUow, unitWordPackCount, unitSize: cellSize } = backfillPackUnit(
        li,
        caseBagUow,
        packCount,
        may("splitUow"),
        unitSize && unitUom ? `${unitSize} ${unitUom}` : null
      );
      const splitQuantity = !isEmptyValue(li?.splitQuantity)
        ? li?.splitQuantity
        : may("splitQuantity")
          ? qtyNum ?? ""
          : "";
      // Total Units as the RECORD or the DESCRIPTION states it — or the printed
      // unit, where it stated a count ("CS10PK") and the description did not,
      // multiplied as the processor multiplies it. The loose-unit reading is
      // deliberately not folded in here — see `looseItemUnits` below for why it
      // has to come after the division that follows.
      const statedItemUnits =
        !isEmptyValue(li?.splitItemUnits) && li?.splitItemUnits !== 0
          ? li?.splitItemUnits
          : !may("splitItemUnits")
            ? ""
            : unitWordPackCount !== null
              ? packMathTotalUnits(qtyNum, unitWordPackCount) ?? ""
              : legacyBackfillTotalUnits(qtyNum, packCount);
      // Units per Case. Parsed from the description when the line has none —
      // every invoice processed before this field existed is in that state, and
      // reprocessing to recover a number already sitting in the description
      // would re-run OCR and the model for it.
      //
      // The stored Total Units is the better source when the description says
      // nothing: it was derived from a pack count on some earlier run, or from
      // one a person typed, and dividing gets it back. Only when it divides
      // exactly — a fractional pack count means these two figures never came
      // from the same reading, and inventing 8.33 items per case to paper over
      // that is how the disagreement becomes permanent.
      const derivedPackCount = (() => {
        if (packCount) return packCount;
        // The count the printed unit stated, read off the paper like the one
        // above (MC-4) — never a division.
        if (unitWordPackCount !== null) return unitWordPackCount;
        // A total the UNIT WORD supplied is not evidence of a pack. Dividing it
        // by its own quantity gives 1 every time, and recording that 1 would
        // turn "the invoice said nothing about packaging" into a stated Units /
        // Case — on the second open, after the first one filled the cell.
        if (li?.splitItemUnitsFromUnitWord === true) return null;
        // D65 rule 1 — never derive a case count onto a line the grid locks
        // the cell for; see `backfillDerivedPackCount`. `li` goes along too,
        // so a blank `splitUow` (Pack Unit emptied by a reviewer) still
        // resolves through `li.uow`'s printed fallback.
        return backfillDerivedPackCount(li, splitUow, statedItemUnits, splitQuantity);
      })();
      const splitPackCount = !isEmptyValue(li?.splitPackCount)
        ? li?.splitPackCount
        : may("splitPackCount")
          ? derivedPackCount ?? ""
          : "";
      // Total Units for goods sold LOOSE — the 38 lines of D&M Vegetables 28815,
      // billed by the LBS, that arrived with the cell empty because "Potatoes"
      // carries no pack pattern to parse. `impliedPackCount` is the rule and its
      // header is the argument: a pound holds one pound, so the total is the
      // quantity. Read from `splitQuantity` rather than `qtyNum` because that is
      // the cell the grid shows and the figure the pack-math flag checks against.
      //
      // STRICTLY AFTER `derivedPackCount`, and that ordering is load-bearing.
      // Dividing this total by its own quantity gives exactly 1, so folding it
      // in above would have the backfill write "Units / Case: 1" on every loose
      // line — a claim about the packaging that the invoice never made, stamped
      // `derived` as though it had been recovered from one, and learned by the
      // vendor item memory as a fact about the product.
      const looseItemUnits = packMathTotalUnits(toNum(splitQuantity), impliedPackCount({ splitUow }));
      const splitItemUnits = !isEmptyValue(statedItemUnits)
        ? statedItemUnits
        : looseItemUnits ?? "";
      const { splitUnitSize, impliedSize } = backfillUnitSize(
        li?.splitUnitSize,
        may("splitUnitSize"),
        unitSize,
        unitUom,
        cellSize,
        splitUow
      );
      // The size string is the better source when the description gave us
      // nothing: "500 ML" already states the unit, and the grid now edits the
      // two together, so leaving splitItemUow blank beside a perfectly good
      // size shows a red cell with nothing visibly wrong in it.
      const sizeTextUnit = canonicalUnit(unitFromSizeText(splitUnitSize)) || "";
      const splitItemUow = !isEmptyValue(li?.splitItemUow)
        ? li?.splitItemUow
        : may("splitItemUow")
          ? unitUom || sizeTextUnit || words?.pieceUom || ""
          : "";

      const set = (field: string, value: any) => {
        ops.push({ path: `${base}.${field}`, value });
        li[field] = value;
      };

      if (may("itemName") && !isEmptyValue(itemName)) set("itemName", itemName);
      if (may("splitUow") && !isEmptyValue(splitUow)) set("splitUow", splitUow);
      if (may("splitQuantity") && !isEmptyValue(splitQuantity)) {
        set("splitQuantity", splitQuantity);
      }
      if (may("splitItemUnits") && !isEmptyValue(splitItemUnits)) {
        set("splitItemUnits", splitItemUnits);
        // Say so when the unit word supplied it, exactly as the processor does
        // for an invoice extracted after this existed. Without the stamp the
        // guard in `derivedPackCount` above has nothing to read on the next
        // open, and the grid cannot explain a figure it put there itself.
        if (isEmptyValue(statedItemUnits)) set("splitItemUnitsFromUnitWord", true);
      }
      if (may("splitPackCount") && !isEmptyValue(splitPackCount)) {
        set("splitPackCount", splitPackCount);
        // Where the figure came from, so the grid can tell a pack count read off
        // the paper from one recovered by division — and so a later edit to
        // Total Units knows whether it is invalidating a real reading.
        set("splitPackCountSource", packCount ? "description" : "derived");
        // Read off the printed unit ("CS10PK"), stamped as the processor stamps
        // it: the grid explains it, and the item memory leaves it until a person
        // confirms it (MC-4).
        if (!packCount && unitWordPackCount !== null) set("splitPackCountFromUnitWord", true);
      }
      if (may("splitUnitSize") && !isEmptyValue(splitUnitSize)) {
        set("splitUnitSize", splitUnitSize);
        // Says the pipeline derived it, so `learnableFromLine` keeps a
        // restatement of the pack unit out of the item memory — where it would
        // become a remembered fact and then outrank a real size parsed off a
        // later invoice. Mirrors what the processor stamps on a fresh
        // extraction; `stampsFor` clears it when a reviewer types a real size.
        if (impliedSize) set("splitUnitSizeFromUnitWord", true);
      }
      if (may("splitItemUow") && !isEmptyValue(splitItemUow)) {
        set("splitItemUow", splitItemUow);
        // The unit half of the same derivation, and `sizeTextUnit` is how it
        // gets here: the implied "1 LBS" above is read back out for the unit
        // cell, so the two can never disagree about which reading filled them.
        const itemUowStamp = backfillItemUowStamp(li, splitItemUow, unitUom, sizeTextUnit, impliedSize);
        if (itemUowStamp) set(itemUowStamp, true);
      }
      // How many physical boxes the line arrived in. Every invoice processed
      // before this field existed has it blank with the count still sitting in
      // the description, so it heals on first open rather than needing a
      // reprocess — which would re-run OCR and the model to recover a string
      // already on the page.
      //
      // `manuallyEdited` is checked, and it is the whole difference between
      // filling a blank and overruling a person: a reviewer who DELETES the
      // note leaves a cell that looks exactly like one nobody has ever touched,
      // and re-seeding it on the next open would put their boxes back.
      if (
        isEmptyValue(li?.packingNote) &&
        !isManuallyEditedPath(`${base}.packingNote`) &&
        !isEmptyValue(packingNote)
      ) {
        set("packingNote", packingNote);
      }

      // --- Repair units stored by a pre-fix processing run --------------------
      // Backfill otherwise only fills blanks, and deliberately so: an existing
      // value may be a manual correction. A unit OUTSIDE the vocabulary is the
      // one exception — nobody types "Z" — and without this every invoice
      // processed before the fix would keep its "140 Z" until someone reprocessed
      // it. Both branches replace an unrecognised unit with a recognised one and
      // can do nothing else.
      if (repaired && unitUom) {
        const storedSizeUnit = unitFromSizeText(li?.splitUnitSize);
        if (storedSizeUnit && !isKnownUnit(storedSizeUnit) && unitSize) {
          set("splitUnitSize", `${unitSize} ${unitUom}`);
        }
        if (!isEmptyValue(li?.splitItemUow) && !isKnownUnit(li?.splitItemUow)) {
          set("splitItemUow", unitUom);
        }
      }
      // Stamped whether or not anything needed replacing: the description on this
      // line was misread, so its unit is a correction rather than a reading, and
      // the grid should say so.
      if (repaired && li?.unitRepairedFromOcr !== true) set("unitRepairedFromOcr", true);

    });

    // --- The other half of a stated VAT rate ----------------------------------
    // A line that states a rate but carries no amount beside it is half a fact:
    // one is arithmetic on the other. Done after the loop above, not inside it,
    // because whether the missing amounts can be trusted is a question about the
    // WHOLE invoice — they are kept only if they reproduce the tax total the
    // document prints. See utils/lineTax.ts.
    //
    // The quantities that loop just filled in are already on `nextItems` (set()
    // mutates the line), so a line the parser left without a qty has a base to
    // multiply by the time we get here.
    // The printed figure, not one rederived from these very lines — see
    // readTotals in src/utils/invoiceVat.js for why that distinction matters.
    // Moved by any quantity correction (D44 VR-5): statedTotals owns that reading.
    const { fills } = planZeroTaxRepairs(nextItems, statedTotals(totals).taxTotal);
    for (const fill of fills) {
      ops.push({ path: `extracted.lineItems.${fill.index}.taxAmount`, value: fill.taxAmount });
      ops.push({ path: `extracted.lineItems.${fill.index}.taxAmountSource`, value: fill.source });
      nextItems[fill.index].taxAmount = fill.taxAmount;
      nextItems[fill.index].taxAmountSource = fill.source;
    }

    if (!ops.length) return;

    const applyLocally = () =>
      setItems((prev) =>
        prev.map((it) => {
          if (it._id !== invoiceId) return it;
          const next: any = structuredClone(it);
          next.extracted = next.extracted || {};
          next.extracted.lineItems = nextItems;
          return next;
        })
      );

    try {
      // OCR unit repair / pack-field backfill — a system correction, not
      // something a person typed, so it does not count as a manual edit.
      //
      // Persisted BEFORE it is shown. This used to paint the grid first and
      // then swallow any failure with "leave UI updated", which meant a 403 or
      // a 500 left Units / Case and Total Units on screen as figures the server
      // did not have. The reviewer read a grid that reconciled and pressed
      // "Confirm & sync to Inventory" — which the server evaluates against ITS
      // copy, so stock went in against different numbers than the ones that
      // justified the click.
      await patchVendorInvoice(invoiceId, ops, { manual: false });
      applyLocally();
    } catch (e: any) {
      // Not fatal — the invoice is still perfectly usable unbackfilled — but it
      // must not be invisible either, because the missing fields are the ones
      // Inventory reads.
      setSaveError(
        e?.response?.data?.message ||
          "Could not fill in the missing pack/unit fields on this invoice. The figures shown are " +
            "the ones actually stored; re-open the invoice to try again."
      );
    }
  };

  /**
   * Fill in a due date that was never printed.
   *
   * Suppliers print an invoice date and terms ("30 DAYS TT DOLLAR"), not a due
   * date, so `extracted.invoice.dueDate` was empty on effectively every
   * invoice — and accounts payable reads exactly that field to age a payable
   * and mark it overdue. New invoices get this from the processor; this is the
   * same arithmetic for the ones already in the system, so they heal on first
   * open rather than needing a reprocess.
   *
   * Only ever fills a blank, and stamps `dueDateDerived` so the panel can say
   * the date was calculated rather than read off the document.
   */
  const backfillDueDate = async (invoiceId: string, invoiceHeader: any) => {
    if (!isEmptyValue(invoiceHeader?.dueDate)) return;
    const derived = deriveDueDate(invoiceHeader?.invoiceDate, invoiceHeader?.paymentTerms);
    if (!derived) return;

    try {
      // Calculated from the invoice date and terms, not typed by a person —
      // does not count as a manual edit.
      await savePaths(
        invoiceId,
        [
          { path: "extracted.invoice.dueDate", value: derived.dueDate },
          { path: "extracted.invoice.dueDateDerived", value: true },
        ],
        false
      );
    } catch (err) {
      // The due date stays blank, and Accounts Payable ages the bill off a blank —
      // so this failure has to be visible somewhere. It used to be a bare `catch {}`
      // whose comment claimed "the review panel still shows what it would have been";
      // the panel shows the STORED value, which is exactly the blank that was not
      // written. Nothing here interrupts the reviewer — the backfill is a heal on
      // open, not something they asked for — but a swallowed write is the pattern
      // CLAUDE.md forbids, and a silent one left no way to tell this from an invoice
      // whose terms simply could not be read.
      console.warn(
        `[VendorInvoices] due-date backfill failed for ${invoiceId}; the date stays blank ` +
          `and Accounts Payable will age this bill without one:`,
        err
      );
    }
  };

  // dd/mm/yyyy, always. `toLocaleDateString()` renders in whatever locale the
  // browser happens to be set to, so the same invoice read 07/15/2026 on one
  // machine and 15/07/2026 on the next — and on a purchase ledger where 03/04
  // is either March or April depending on who is looking, that is not a
  // cosmetic problem.
  const formatDate = (value?: string) => {
    if (!value) return "-";
    return formatDdMmYyyy(value) || value;
  };

  return (
    /* `min-h-screen` is gone: this page sits INSIDE the app shell, so a full
       viewport minimum added a second scrollbar and 64px of dead space under
       every screen. The gutter and the header band are the app's, from
       docs/DESIGN.md. */
    <div className="bg-brand-bg px-4 py-4 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold leading-7 text-slate-900">Vendor Invoices</h1>
          <ModuleInfoButton
            content={
              <div className="flex flex-col gap-2">
                <p>You can upload received vendor invoices in this section.</p>
                <p>The system scans each invoice and shows an accuracy score along with its status.</p>
                <p className="font-medium">From here, you can:</p>
                <ul className="list-disc list-inside space-y-1 pl-1">
                  <li>Edit invoice details</li>
                  <li>Delete invoices</li>
                </ul>
              </div>
            }
            ariaLabel="Vendor Invoice information"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void load().catch(() => {
                // Shown by `loadError` above the list.
              });
            }}
            title="Refresh"
            aria-label="Refresh"
            icon={<RefreshCw aria-hidden="true" className="h-4 w-4" />}
          />
          {/* The click registered and the list is being asked: a slow list is not "doing nothing". Not
              the Button's own `loading`, which would disable Refresh while a stuck request is out. */}
          {loading ? (
            <span role="status" className="text-xs text-slate-500">
              Updating…
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search here" className="w-56" />
          {/* Module 1: Vessel filter */}
          <select
            value={vesselFilter}
            onChange={(e) => { setVesselFilter(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            title="Filter by assigned vessel"
          >
            <option value="">All vessels</option>
            <option value="__unassigned__">— Unassigned —</option>
            {vesselMappings.map((m) => (
              <option key={m._id} value={m.vesselName}>{m.vesselName}</option>
            ))}
          </select>
          {/* D95: Ship Chandlery invoices, or company expenses. Pantry is on hold. */}
          <select
            value={destinationFilter}
            onChange={(e) => { setDestinationFilter(e.target.value as "" | "united" | "expense"); setPage(1); }}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
            title="Filter by destination"
            aria-label="Filter by destination"
          >
            <option value="">All destinations</option>
            <option value="united">Ship Chandlery</option>
            <option value="expense">Company expense</option>
          </select>
          <Button
            variant="primary"
            onClick={() => {
              setUploadOpen(true);
              setUploadMsg(null);
            }}
            icon={<Upload aria-hidden="true" className="h-4 w-4" />}
          >
            Upload
          </Button>
        </div>
      </div>

      {/* The invoice closes on a successful Confirm & sync, so its catalogue
          and packing result is reported here until dismissed. */}
      {confirmBanner && openId !== confirmBanner.invoiceId && (
        <div className="mb-4">
          <CatalogueSyncBanner
            key={confirmBanner.invoiceId}
            invoiceId={confirmBanner.invoiceId}
            label={(() => {
              const n = items.find((i) => i._id === confirmBanner.invoiceId)?.extracted?.invoice?.invoiceNumber;
              return n ? `Invoice ${n}` : "Last invoice sent to Inventory";
            })()}
            packingAlerts={confirmBanner.packingAlerts}
            awaitingSync
            onDismiss={() => setConfirmBanner(null)}
          />
        </div>
      )}

      {/* The list could not be loaded: the rows below are the last ones the page had, and this says why
          they did not change (lane 5, V6). Cleared by the next load that works. */}
      {loadError && (
        <div
          role="alert"
          className="mb-4 w-full rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          The vendor invoice list could not be loaded. {loadError} The rows below are the last ones loaded; use
          Refresh to try again.
        </div>
      )}

      {/* D71: a pending/processing row that vanished on the background poll —
          its first processing run failed and it was auto-deleted. Same alert
          styling as the per-invoice "Not saved" banner further down; this one
          is page-level because the row it is about no longer exists to attach
          it to. */}
      {failureNotice && (
        <div
          role="alert"
          className="mb-4 w-full rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {failureNotice.message}
          <button
            type="button"
            onClick={() => setFailureNotice(null)}
            className="ml-2 underline underline-offset-2 hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <StatCard label="Total" value={stats?.total} onClick={() => { setStatus("all"); setPage(1); }} />
        {"pending" in (stats || {}) ? (
          <StatCard label="Pending" value={stats?.pending} onClick={() => { setStatus("pending"); setPage(1); }} />
        ) : null}
        <StatCard label="Done" value={stats?.done} onClick={() => { setStatus("done"); setPage(1); }} />
        <StatCard label="Failed" value={stats?.failed} onClick={() => { setStatus("failed"); setPage(1); }} />
        <StatCard label="Processing" value={stats?.processing} onClick={() => { setStatus("processing"); setPage(1); }} />
        <StatCard label="Skipped" value={stats?.skipped} onClick={() => { setStatus("skipped"); setPage(1); }} />
      </div>

      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
        {/* Issue 5 + 1: overflow-auto needed for both sticky thead and sticky right column */}
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <table className="data-table w-full text-xs">
            {/* Issue 5: sticky thead */}
            <thead className="sticky top-0 z-20 bg-slate-50 text-slate-600">
              <tr className="text-left border-b border-slate-200">
                <SortHeader label="Invoice Number" sortKey="invoiceNumber" activeKey={loadedOrder.sort} dir={loadedOrder.dir} onSort={onSort} />
                <SortHeader label="Vendor Name" sortKey="vendor" activeKey={loadedOrder.sort} dir={loadedOrder.dir} onSort={onSort} />
                <SortHeader label="Vessel / Sticker" sortKey="vessel" activeKey={loadedOrder.sort} dir={loadedOrder.dir} onSort={onSort} />
                <SortHeader label="Created" sortKey="createdAt" activeKey={loadedOrder.sort} dir={loadedOrder.dir} onSort={onSort} />
                <SortHeader label="Total Price" sortKey="total" activeKey={loadedOrder.sort} dir={loadedOrder.dir} onSort={onSort} />
                <SortHeader label="Total Items" sortKey="items" activeKey={loadedOrder.sort} dir={loadedOrder.dir} onSort={onSort} />
                <SortHeader label="Status" sortKey="status" activeKey={loadedOrder.sort} dir={loadedOrder.dir} onSort={onSort} />
                <SortHeader label="Confidence" sortKey="confidence" activeKey={loadedOrder.sort} dir={loadedOrder.dir} onSort={onSort} />
                <th className="px-3 py-2 w-36 sticky right-0 bg-slate-50 border-l border-slate-200">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => {
                const isOpen = openId === inv._id;
                const ex = inv.extracted || {};
                const vendor = ex.vendor || {};
                const invoice = ex.invoice || {};
                const totals = ex.totals || {};
                const lineItems = Array.isArray(ex.lineItems) ? ex.lineItems : [];
                const statusLabel =
                  inv.status === "failed" && lineItems.length > 0
                    ? "Processed with issues"
                    : undefined;
                const needsBackfill =
                  isOpen &&
                  lineItems.length > 0 &&
                  lineItems.some(
                    (li: any, liIdx: number) =>
                      isEmptyValue(li?.splitQuantity) ||
                      isEmptyValue(li?.splitUow) ||
                      isEmptyValue(li?.splitUnitSize) ||
                      isEmptyValue(li?.splitItemUow) ||
                      // Every invoice processed before Units / Case existed has
                      // this empty. Backfill recovers it from the description,
                      // or from Total Units / Quantity when those divide
                      // exactly, so they heal on first open instead of needing a
                      // reprocess. Lines sold loose have no pack count to find
                      // and keep this true; that costs one parse pass per open,
                      // because backfill writes nothing when it fills nothing.
                      isEmptyValue(li?.splitPackCount) ||
                      isEmptyValue(li?.itemName) ||
                      // A stored unit outside the vocabulary is a pre-fix "140 Z".
                      // Backfill can re-derive it from the repaired description,
                      // so invoices processed before the fix heal on first open.
                      (!isEmptyValue(li?.splitItemUow) && !isKnownUnit(li?.splitItemUow)) ||
                      // A stated rate beside a 0 amount is arithmetic nobody
                      // did: on an invoice that prints VAT only at the foot the
                      // extractor has to put something in the field and returns
                      // 0, which every derivation in the pipeline then read as a
                      // figure the supplier stated. planZeroTaxRepairs decides
                      // whether it can be filled. An EMPTY amount is not a 0
                      // (the server's repairZeroLineTax leaves it alone), and a
                      // clause it could never clear would re-fire every render.
                      (!isEmptyValue(li?.taxAmount) &&
                        Number(li?.taxAmount) === 0 &&
                        !isEmptyValue(li?.taxRate) &&
                        Number(li?.taxRate) > 0 &&
                        li?.taxRateDefaulted !== true &&
                        li?.taxRateUnresolved !== true) ||
                      // A box count still glued into the description, on a line
                      // that has no packing note yet. Stated as "there is
                      // something to find", not "the field is empty": most
                      // lines print no box count at all, and a clause that can
                      // never go false is what made this whole block re-fire on
                      // every render (see the note below). This one clears
                      // itself the moment the note is written, and never fires
                      // on a note a reviewer has deleted.
                      //
                      // LAST in the chain on purpose. `.some()` short-circuits,
                      // so on a 100-line invoice the regex only runs where
                      // nothing cheaper has already answered yes.
                      (isEmptyValue(li?.packingNote) &&
                        !(inv.manuallyEditedPaths || []).includes(
                          `extracted.lineItems.${liIdx}.packingNote`
                        ) &&
                        !!splitItemName(li?.description).packingNote)
                  );
                // Both backfills are server WRITES, and both used to be
                // scheduled from inside this render body on every pass. Three
                // consequences, all real:
                //
                //   - `needsBackfill` can never become false for a line sold
                //     loose (splitPackCount stays "" when no pack pattern
                //     divides exactly), so every render scheduled another PATCH.
                //     Typing in the search box re-renders the list, so this was
                //     one structuredClone + full pack-regex pass per keystroke,
                //     doubled again by StrictMode in dev.
                //   - Several PATCHes for the same invoice were therefore in
                //     flight at once with nothing serialising them.
                //   - Neither checked `openMode`, so opening an invoice with the
                //     "Open — view only, no changes" button immediately wrote
                //     six fields to it.
                //
                // Now: edit mode only, and at most once per invoice per mount.
                if (isOpen && openMode === "edit") {
                  if (needsBackfill && !backfilledRef.current.has(`split:${inv._id}`)) {
                    backfilledRef.current.add(`split:${inv._id}`);
                    setTimeout(
                      () =>
                        backfillSplitFields(
                          inv._id,
                          lineItems,
                          totals,
                          inv.manuallyEditedPaths || []
                        ),
                      0
                    );
                  }

                  if (
                    isEmptyValue(invoice?.dueDate) &&
                    deriveDueDate(invoice?.invoiceDate, invoice?.paymentTerms) &&
                    !backfilledRef.current.has(`due:${inv._id}`)
                  ) {
                    backfilledRef.current.add(`due:${inv._id}`);
                    setTimeout(() => backfillDueDate(inv._id, invoice), 0);
                  }
                }

                const confidenceOverall = ex?.confidence?.overall ?? null;
                const accuracyPct =
                  confidenceOverall == null || Number.isNaN(Number(confidenceOverall))
                    ? "-"
                    : `${Math.round(Number(confidenceOverall) * 100)}%`;
                // Live, not `ex.validation.hasIssues`: that is a verdict on the
                // document as first extracted, so the badge stayed lit after a
                // correction and stayed dark after an edit that broke the
                // totals. Recomputed from the current values instead.
                // CR-14 — Suppress discrepancy badge once ops has marked the invoice verified.
                const { review: rowReview, lineErrors: rowLineErrors } =
                  invoiceReviews.get(inv._id) || emptyReview;

                // A human has been through this invoice and said so. Verify is
                // one way of saying it; Confirm & sync is the STRONGER way, and
                // it was not being counted.
                //
                // Confirm & sync refuses to run unless the lines reconcile with
                // the printed grand total (confirmVendorInvoiceInventory), asks
                // about every missing expiry date, and refuses outright against
                // an already-stocked twin. Someone got through all of that and
                // pressed the button, and the goods are in the warehouse. The
                // row still showed "⚠ Calc" and still offered a Verify button —
                // the grid recomputes a few checks the confirm gate does not
                // share (an empty Item Code, a blank Unit Size), so a perfectly
                // stocked invoice sat there asking to be re-verified with no
                // way to make the badge go away short of pressing Verify on an
                // invoice nobody could change any more. Reported as invoices
                // showing "CAL" after they were pushed to inventory.
                const humanSettled = !!inv.verifiedAt || !!inv.inventoryConfirmedAt;

                // D109.8: "Calc" only when the sums are off; any other open issue is "Check".
                const badge = rowBadge(rowReview.issues, rowLineErrors, humanSettled);
                const validationIssues = badge !== null;
                const isVerified = !!inv.verifiedAt;

                // Currency the processor resolved for THIS invoice (never a global default).
                const currencyCode = invoice?.currencyCode ?? null;
                const currencyNeedsConfirm =
                  !!invoice && invoice.currencyConfident === false && !humanSettled;

                // CR-13 — the backend has always flagged lines whose VAT rate it
                // defaulted rather than read; nothing ever surfaced the flag.
                const vatDefaultedCount = lineItems.filter(
                  (li: any) => li?.taxRateDefaulted === true
                ).length;
                const vendorNeedsConfirm =
                  inv?.vendorReconciliation?.state === "suggested" && !humanSettled;

                // The supplier this invoice is FILED UNDER, not the string the
                // extractor read off the letterhead. One supplier prints itself
                // differently across its own invoices — three scans of Massy
                // gave "MASSY TRADING (TRINIDAD)", "Massy Distribution" and the
                // parent "Massy Integrated Retail Ltd." — so showing the raw
                // read made one vendor look like three. Worse, it hid the
                // reverse error: those invoices were all filed against a single
                // vendor, and the column showing three names concealed it.
                // Falls back to the extracted name while a match is pending.
                const matchedVendorName = inv?.vendorReconciliation?.matchedVendorName || "";
                const displayVendorName = matchedVendorName || vendor.name || "";
                // Worth surfacing only when the two genuinely disagree.
                const extractedVendorName = String(vendor.name || "").trim();
                const vendorReadDiffers =
                  !!matchedVendorName &&
                  !!extractedVendorName &&
                  matchedVendorName.trim().toLowerCase() !== extractedVendorName.toLowerCase();

                return (
                  <React.Fragment key={inv._id}>
                    {/* Issue 1: add group class so sticky td can pick up hover bg */}
                    <tr className="group border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span>{invoice.invoiceNumber || "-"}</span>
                        {validationIssues && (
                          <span
                            title={[
                              badge === "calc"
                                ? "Calc: the invoice's figures do not add up."
                                : "Check: the figures add up, but something else needs a look (pack size, pricing, freight, stock or the document).",
                              ...rowReview.issues.map((i) => `${i.title}: ${i.message}`),
                              rowLineErrors ? `${rowLineErrors} line cell(s) missing or not adding up.` : "",
                              "Open the invoice to see which cells — click Verify once resolved.",
                            ]
                              .filter(Boolean)
                              .join("\n")}
                            className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-semibold align-middle ${
                              badge === "calc" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {badge === "calc" ? "⚠ Calc" : "Check"}
                          </span>
                        )}
                        {validationIssues && (
                          <button
                            title="Mark verified — clears the discrepancy badge"
                            onClick={async (e) => {
                              e.stopPropagation();
                              // Verifying says a human checked these figures.
                              // It stays possible over an invoice that does not
                              // reconcile — suppliers do send those — but not
                              // by accident, and not without seeing what is
                              // wrong first.
                              const outstanding = [
                                ...rowReview.issues.map((i) => `• ${i.title}: ${i.message}`),
                                rowLineErrors
                                  ? `• ${rowLineErrors} cell(s) on the line items are missing or do not add up.`
                                  : "",
                              ].filter(Boolean);
                              if (
                                outstanding.length &&
                                !window.confirm(
                                  `This invoice still has unresolved problems:\n\n${outstanding.join(
                                    "\n"
                                  )}\n\nMark it verified anyway?`
                                )
                              ) {
                                return;
                              }
                              // Through the shared client, and refreshed with
                              // the page's own loader.
                              //
                              // Was: a bare fetch against a REACT_APP_API_BASE
                              // that is defined nowhere, so the URL fell back to
                              // a relative "/api" and never reached the API in
                              // development or in a split deployment. On success
                              // it called window.refreshVendorInvoicesList,
                              // which is declared nowhere in this codebase, and
                              // so always fell through to a full page reload —
                              // destroying any staged edits without the discard
                              // prompt every other exit path honours.
                              try {
                                await markVendorInvoiceVerified(inv._id);
                                await load();
                              } catch (err: any) {
                                setSaveError(
                                  err?.response?.data?.message ||
                                    err?.message ||
                                    "Could not mark this invoice verified."
                                );
                              }
                            }}
                            className="ml-1 inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-200"
                          >
                            ✓ Verify
                          </button>
                        )}
                        {/* Stock is the stronger statement, so it is the one
                            shown when both are true — and it has to be shown at
                            all. It is the reason the ⚠ Calc badge and the
                            Verify button are absent from this row; without it
                            an invoice already in the warehouse is indis-
                            tinguishable from one nobody has opened. */}
                        {isExpenseInvoice(inv) && (
                          <span
                            title="A company bill: billed and paid, but no stock, catalogue price or purchase-order match"
                            className="ml-1.5 inline-flex items-center rounded-full bg-slate-200 px-1.5 py-0.5 text-xs font-semibold text-slate-700 align-middle"
                          >
                            Company expense
                          </span>
                        )}
                        {inv.inventoryConfirmedAt ? (
                          <span
                            title={`${destinationWording(inv).confirmedLabel} ${formatDate(inv.inventoryConfirmedAt)}${
                              inv.verifiedAt ? ` · verified ${formatDate(inv.verifiedAt)}` : ""
                            }`}
                            className="ml-1.5 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700 align-middle"
                          >
                            {destinationWording(inv).confirmedChip}
                          </span>
                        ) : (
                          isVerified &&
                          inv.verifiedAt && (
                            <span title={`Verified ${formatDate(inv.verifiedAt)}`} className="ml-1.5 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700 align-middle">
                              ✓ Verified
                            </span>
                          )
                        )}
                      </td>
                      <td className="px-3 py-2 max-w-[280px] truncate">
                        <span
                          title={
                            vendorReadDiffers
                              ? `Filed under ${matchedVendorName}. This invoice prints "${extractedVendorName}".`
                              : undefined
                          }
                        >
                          {displayVendorName || "-"}
                        </span>
                        {vendorReadDiffers && (
                          <span className="ml-1 text-xs text-slate-400 align-middle">
                            ({extractedVendorName})
                          </span>
                        )}
                        {vendorNeedsConfirm && (
                          <span
                            title={inv?.vendorReconciliation?.note || "More than one supplier matched — confirm which one issued this invoice"}
                            className="ml-1.5 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700 align-middle"
                          >
                            ⚠ Confirm vendor
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <VesselCell
                          invoice={inv}
                          onAssign={() => setAssignOpenFor(inv._id)}
                          onSplit={() => setSplitOpenFor(inv._id)}
                        />
                      </td>
                      <td className="px-3 py-2">{formatDate(inv.createdAt)}</td>
                      <td className="px-3 py-2">
                        <span>{formatInvoiceMoney(totals.grandTotal, currencyCode)}</span>
                        {currencyNeedsConfirm && (
                          <span
                            title={invoice?.currencyNote || "Currency could not be resolved from the invoice"}
                            className="ml-1.5 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700 align-middle"
                          >
                            ⚠ {currencyCode ? `${currencyCode}?` : "Currency?"}
                          </span>
                        )}
                        {vatDefaultedCount > 0 && !humanSettled && (
                          <span
                            title={`VAT was not stated on ${vatDefaultedCount} line(s); ${
                              typeof vatRatePercent === "number" && Number.isFinite(vatRatePercent)
                                ? `${vatRatePercent}%`
                                : "the Settings VAT rate"
                            } is suggested for confirmation and is NOT included in these amounts`}
                            className="ml-1.5 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700 align-middle"
                          >
                            ⚠ Confirm VAT
                          </span>
                        )}
                        {/* Visible without opening the invoice: an overcharge
                            nobody looks for is an overcharge that gets paid. */}
                        {rowReview.overcharge.total > 0 && (
                          <span
                            title={rowReview.overcharge.lines
                              .filter((l) => l.difference > 0)
                              .map(
                                (l) =>
                                  `Line ${l.lineNo} ${l.itemName}: charged ${l.charged}, should be ${l.expected}`
                              )
                              .join("\n")}
                            className="ml-1.5 inline-flex items-center rounded-full bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-700 align-middle"
                          >
                            ⚠ Overcharged {formatInvoiceMoney(rowReview.overcharge.total, currencyCode)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">{lineItems.length || 0}</td>
                      <td className="px-3 py-2">
                        <StatusPill status={inv.status} label={statusLabel} />
                        {/* B031: a failed (re)process says why on the row, not only once it is opened. */}
                        <ProcessingIssueNote status={inv.status} lastError={inv.lastError} />
                        {/* Same invoice number + supplier as another invoice.
                            Sits beside the status because that is where a
                            reviewer scanning the list is already looking, and
                            this changes whether the row should be opened at all.
                            Red once a twin is in Inventory — from that point
                            confirming this one double-counts the goods. */}
                        {(inv.duplicateCount ?? 0) > 0 && (
                          <span
                            title={(inv.duplicateInvoices || [])
                              .map(
                                (t) =>
                                  `${t.invoiceNumber || "(no number)"}${t.vendorName ? ` — ${t.vendorName}` : ""}` +
                                  `${t.fileName ? ` · ${t.fileName}` : ""}` +
                                  `${t.inventoryConfirmedAt ? " · already in Inventory" : ""}`
                              )
                              .join("\n")}
                            className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-semibold align-middle ${
                              (inv.duplicateConfirmedCount ?? 0) > 0
                                ? "bg-rose-100 text-rose-700"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            ⧉ Duplicate{(inv.duplicateConfirmedCount ?? 0) > 0 ? " — already stocked" : ""}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-semibold ${(() => {
                          if (accuracyPct === "-") return "text-slate-400";
                          const n = parseFloat(accuracyPct); // strips the "%"
                          if (n >= 90) return "text-cyan-600";
                          if (n >= 70) return "text-amber-500";
                          return "text-rose-500";
                        })()}`}>
                          {accuracyPct}
                        </span>
                      </td>
                      {/* Issue 1: sticky actions cell — always visible on right */}
                      <td className="px-3 py-2 sticky right-0 bg-white group-hover:bg-slate-50 border-l border-slate-100 transition-colors">
                        {/* D109.4: a refusal for a row that is not the open invoice
                            shows here, where the click happened (the open panel
                            shows its own). Same reprocessNotice state. */}
                        {reprocessNotice && reprocessNotice.invoiceId === inv._id && !isOpen && (
                          <div
                            role="alert"
                            className="mb-1 max-w-[16rem] whitespace-normal rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-800"
                          >
                            {reprocessNotice.message}
                            <button
                              onClick={() => setReprocessNotice(null)}
                              className="ml-2 underline underline-offset-2 hover:no-underline"
                            >
                              Dismiss
                            </button>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-slate-500">
                          <button
                            onClick={() => doReprocess(inv._id)}
                            className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-200 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            title="Reprocess"
                            aria-label="Reprocess"
                            disabled={reprocessId === inv._id}
                          >
                            <svg viewBox="0 0 24 24" className={`h-4 w-4 ${reprocessId === inv._id ? "animate-spin motion-reduce:animate-none" : ""}`} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                              <path d="M21 3v6h-6" />
                            </svg>
                          </button>
                          <button
                            onClick={() => requestOpen(inv._id, "edit")}
                            className={`h-7 w-7 inline-flex items-center justify-center rounded border hover:text-slate-700 hover:bg-slate-50 ${
                              isOpen && openMode === "edit" ? "border-primary-500 text-primary-500" : "border-slate-200"
                            }`}
                            title="Edit — change the extracted values"
                            aria-label="Edit"
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteId(inv._id)}
                            className="h-7 w-7 inline-flex items-center justify-center rounded border border-rose-200 text-rose-600 hover:bg-rose-50"
                            title="Delete"
                            aria-label="Delete"
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18" />
                              <path d="M8 6V4h8v2" />
                              <path d="M6 6l1 14h10l1-14" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* The inline detail row that used to live here was replaced by the
                        detail Modal further down this file. It was left in place behind
                        a `false &&` rather than deleted, which quietly took "Split by
                        vessel" out of the UI with it — the only trigger was inside. */}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {loading && items.length === 0 && (
          <div className="px-4 py-10">
            <LoadingState text="Loading vendor invoices..." />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="p-4">
            <EmptyState title="No invoices found" detail="No vendor invoice matches this filter." />
          </div>
        )}

        <Pagination page={page} pageSize={limit} total={total} onPageChange={setPage} noun="invoices" />
      </div>

      {/* ── Invoice Detail Modal ── */}
      {openId && (() => {
        // Falls back to the last version of this invoice we held.
        //
        // The modal body was `items.find(...)` with `if (!inv) return null`, so
        // a background poll returning a page that no longer contains the open
        // invoice — a filter change, a sort, or the stale-closure poll that used
        // to repaint page 1 — unmounted the whole modal mid-edit. `openId` and
        // `pendingEdits` stayed set, so the staged corrections were still in
        // state but invisible and unreachable, and re-opening the row offered to
        // discard them. Keeping the last known copy means the editor survives a
        // refetch that drops the row from the current page.
        // Id-checked, so the retained copy can only ever stand in for the
        // invoice it belongs to — never for the previously open one.
        const retained =
          openInvoiceRef.current?._id === openId ? openInvoiceRef.current : null;
        const inv = items.find((i) => i._id === openId) || retained;
        if (!inv) return null;
        openInvoiceRef.current = inv;
        // One decision for the stock wording on this screen: see destinationWording.
        const wording = destinationWording(inv);
        const isReadOnly = openMode === "view";
        // Staged-but-unsaved edits are laid over the persisted invoice so the
        // grid, totals and discrepancy panel all show what the user just
        // typed, exactly as before the switch to a single top-level Save —
        // nothing here has reached the server yet.
        const previewInv = pendingOps.length ? applyOpsToInvoice(inv, pendingOps) : inv;
        const ex = previewInv.extracted || {};
        const vendor = ex.vendor || {};
        const invoice = ex.invoice || {};
        const totals = ex.totals || {};
        const lineItems = Array.isArray(ex.lineItems) ? ex.lineItems : [];
        // The same lines WITHOUT the staged-edit overlay — what each cell's
        // "nothing changed" check is measured against (USC-BUG-0045).
        const storedLineItems = Array.isArray(inv.extracted?.lineItems) ? inv.extracted.lineItems : [];
        const statusLabel = inv.status === "failed" && lineItems.length > 0 ? "Processed with issues" : undefined;
        const confidenceOverall = ex?.confidence?.overall ?? null;
        const accuracyPct = confidenceOverall == null || Number.isNaN(Number(confidenceOverall))
          ? "-" : `${Math.round(Number(confidenceOverall) * 100)}%`;
        // Recomputed from the values on screen — see utils/lineItemFlags.ts for
        // why the server's stored verdict is not enough. This is what makes a
        // hand-typed quantity that breaks the totals visible the moment it is
        // saved. Pending (unsaved) edits bypass the whole-list `invoiceReviews`
        // memo — cheap to redo for one invoice — so they show up immediately too.
        const review = pendingOps.length
          ? reviewInvoice(ex, { uploadedAt: inv.createdAt, documentNote: textOnlyReadNote(inv), ...pageContextOf(inv), stocks: !isExpenseInvoice(inv) })
          : (invoiceReviews.get(inv._id) || emptyReview).review;
        // Does this invoice add up? Shared by the note under the Totals card
        // (reconciled) and the full-width discrepancy panel (not reconciled),
        // so the two can never disagree about which state we are in.
        // The one verdict on this screen that is READ rather than recomputed.
        // Its two inputs are the invoice's printed terms and the vendor record's
        // credit days, and the second of those is not on this screen at all, so
        // vendorInvoicePaymentTerms.service.js owns the sentence and re-derives
        // it on every path that can change either input — the edit save, a
        // confirmed vendor match, and an edit to the vendor's credit days. Do not
        // add a second derivation here: it would be a copy of a rule that has an
        // owner, and the copy is what let this flag go stale in the first place.
        const paymentTermsFlag: string | null = ex?.validation?.paymentTermsFlag || null;
        const lineMismatches = lineItems
          .map((li: any, i: number) => ({
            li,
            i,
            flags: getLineItemFlags(li, ex?.validation, {
              convention: review.overcharge.convention,
              index: i,
              stocks: !isExpenseInvoice(inv),
            }),
          }))
          .filter((r: any) => r.flags.lineTotal?.level === "error");
        // Two questions, and they used to share one answer. `reconciled` is a
        // verdict on the ARITHMETIC — it is what the green "Adds up, matching
        // the Grand Total" note under the Totals card asserts. A payment-terms
        // disagreement is not arithmetic and cannot make an invoice stop adding
        // up, but it was in this expression, so HADCO 4461490 (8,735.84 + 387.50
        // = 9,123.34, cent-exact against the paper) lost its green tick and was
        // shown the amber panel headed "Check these figures against the
        // document" over figures nothing disagreed with. Reported from the floor
        // as the grand total not matching; nothing was mismatched, the
        // confirmation was just absent.
        //
        // ARITHMETIC ONLY, by kind — see the IssueKind block in
        // utils/lineItemFlags. Payment terms were the first thing pulled out of
        // this expression, and they were not the only passenger: a freight figure
        // worth a second look, a line that never said how big its pack was, and a
        // supplier billing above its own quantity x price are all real things to
        // deal with, and none of them is the question "do the lines come to the
        // grand total". Each one used to silently withhold the green tick.
        const arithmeticProblems = arithmeticIssues(review.issues);
        const reconciled =
          lineItems.length > 0 && !arithmeticProblems.length && !lineMismatches.length;
        // Everything the arithmetic has no opinion about, kept apart so the panel
        // below can put each kind under its own heading instead of pouring them
        // all into one list of sentences.
        const otherIssues = nonArithmeticIssues(review.issues);
        const freightIssues = otherIssues.filter((i) => i.kind === "freight");
        const stockIssues = otherIssues.filter((i) => i.kind === "stock");
        const pricingIssues = otherIssues.filter((i) => i.kind === "pricing");
        const documentIssues = otherIssues.filter((i) => i.kind === "document");
        // The panel appears for ANY of them, alongside the green note rather than
        // instead of it: "this invoice adds up" and "its payment terms are still
        // in question" are both true at once, and the screen has to be able to
        // say so. It is not the negation of `reconciled` any more.
        const showReviewPanel =
          lineItems.length > 0 &&
          !!(!reconciled || otherIssues.length || paymentTermsFlag);
        const manuallyEditedPaths = inv.manuallyEditedPaths || [];
        const isManuallyEdited = (path: string) => path in pendingEdits || manuallyEditedPaths.includes(path);
        // Cells a person has vouched for without changing. A staged (unsaved)
        // edit to a cell retracts its confirmation on screen straight away —
        // the server does the same on Save — because a confirmation describes
        // the figure that was showing when it was given, not the cell.
        const reviewConfirmedPaths = (inv.reviewConfirmedPaths || []).filter((p) => !(p in pendingEdits));
        const confirmedSet = new Set(reviewConfirmedPaths);
        const attachmentName = String(inv?.source?.fileName || inv?.source?.gcsObjectPath || inv?.source?.filePath || "").split("/").pop() || "invoice attachment";
        const inline = opensInBrowser(attachmentName, inv?.source?.mimeType || undefined);
        const currentVendorMatch = inv.vendorReconciliation as any;

        return (
          <Modal
            isOpen={true}
            onClose={() => {
              if (!confirmDiscardPending()) return;
              setOpenId(null);
              setPendingEdits({});
            }}
            title={`Invoice: ${invoice.invoiceNumber || "—"}${isReadOnly ? " (view only)" : ""}`}
            asPage
            bodyClass="flex-1 min-h-0 overflow-y-auto px-3 sm:px-5 pb-4"
          >
            <div className="flex flex-col gap-3 text-xs">
              {/* Metadata + actions bar — sticky so vendor/status/accuracy stay
                  visible while the panels below are reviewed. */}
              <div className="sticky top-0 z-40 -mx-3 sm:-mx-5 px-3 sm:px-5 pt-3 pb-2 bg-white border-b border-slate-100 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-500 min-w-0">
                  {/* The supplier this invoice is filed under, matching the list
                      column. The letterhead's own wording stays editable in the
                      Vendor field below — this bar states where it was filed. */}
                  <span className="truncate max-w-[280px]" title={vendor.name ? `Read from the invoice: "${vendor.name}"` : undefined}>
                    Vendor: <span className="text-slate-700 font-medium">{currentVendorMatch?.matchedVendorName || vendor.name || "—"}</span>
                  </span>
                  <span className="text-slate-300">•</span>
                  <span>Created: {formatDate(inv.createdAt)}</span>
                  <span className="text-slate-300">•</span>
                  <span>Status: <StatusPill status={inv.status} label={statusLabel} /></span>
                  <span className="text-slate-300">•</span>
                  <span title={ex?.confidence?.reasoning || undefined} className="cursor-help">
                    Accuracy Score:{" "}
                    <span
                      className={`font-semibold ${
                        Number(confidenceOverall) >= 0.9
                          ? "text-cyan-600"
                          : Number(confidenceOverall) >= 0.7
                          ? "text-amber-500"
                          : "text-rose-500"
                      }`}
                    >
                      {accuracyPct}
                    </span>
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {/* Vendor match.

                      `unmatched` belongs here as much as `suggested` does, and
                      leaving it out was the bug: candidates are filtered at
                      score >= 0.55, so a supplier we have never seen produces
                      ZERO candidates and state `unmatched` — and this was the
                      only route to the modal that can link or create one. The
                      invoice with no supplier at all, the one that most needs a
                      vendor created, was the one with no way to create it.
                      (Lawrence Marketing Limited, 27036: closest row "Lawrence
                      Provisions" scores 0.433, so it never became a candidate.)

                      Consequence beyond the vendor list: no vendorId means
                      `resolveInvoiceVendorId` returns null, so the item memory
                      declines to read AND to learn for every such invoice,
                      permanently. */}
                  {/* "Change vendor" (D35, VI-1): an invoice already filed under a
                      supplier can be moved to another. Refused once the invoice is
                      in Inventory, because its catalogue rows, stock movements and
                      item memory are already filed under the current vendor — the
                      server says VENDOR_CHANGE_AFTER_STOCK_IN. */}
                  {currentVendorMatch &&
                    currentVendorMatch.state !== "suggested" &&
                    currentVendorMatch.state !== "unmatched" && (
                    <button
                      disabled={!!inv.inventoryConfirmedAt}
                      title={
                        inv.inventoryConfirmedAt
                          ? wording.vendorLockTitle
                          : "File this invoice under a different registered supplier"
                      }
                      onClick={() => openVendorMatch(inv)}
                      className="rounded-md border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Change vendor
                    </button>
                  )}
                  {(currentVendorMatch?.state === "suggested" ||
                    currentVendorMatch?.state === "unmatched") && (
                    <button
                      onClick={() => openVendorMatch(inv)}
                      className="rounded-md border border-primary-500 px-3 py-1.5 font-semibold text-primary-600 hover:bg-cyan-50"
                    >
                      {/* "Add" alone understated what this opens once the modal
                          grew a register search (TECH_DEBT #281): an `unmatched`
                          invoice can now be LINKED to a supplier already on file
                          under a name the matcher scored too low to suggest, and
                          a reviewer who reads the button as "create" will make a
                          duplicate rather than click it. */}
                      {currentVendorMatch.state === "unmatched"
                        ? "Find or add supplier"
                        : "Review vendor match"}
                    </button>
                  )}

                  {/* Attachment — minted as a short-lived signed URL on click */}
                  <button
                    type="button"
                    onClick={() => openInvoiceAttachment(inv, inline)}
                    title={attachmentName}
                    className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 font-semibold text-primary-500 hover:bg-slate-50"
                  >
                    {inline ? "View attachment" : "Download attachment"}
                  </button>

                  {/* One Save for the whole invoice — replaces the old per-cell
                      Save buttons. Disabled until a cell is actually changed. */}
                  {!isReadOnly && (
                    <button
                      onClick={async () => {
                        // VI-6 (D30): allowed after stock-in, but said first.
                        // Asked before "Saving…" shows; Cancel keeps the edits staged.
                        if (
                          stockedMoneyEditPaths(inv, pendingOps).length &&
                          !(await confirm({
                            title: wording.alreadyTitle,
                            summary: wording.moneyEditSummary,
                            hint: "Stock on hand is corrected in Inventory, not on the invoice.",
                            confirmLabel: "Save anyway",
                            cancelLabel: "Keep editing",
                          }))
                        ) {
                          return;
                        }
                        setSavingPending(true);
                        try {
                          await commitPendingEdits(inv._id);
                        } catch {
                          // savePaths has already rolled the grid back and set
                          // saveError, which renders below. Swallowed here only
                          // so the click does not become an unhandled rejection.
                        } finally {
                          setSavingPending(false);
                        }
                      }}
                      disabled={!pendingOps.length || savingPending}
                      className="inline-flex items-center gap-2 rounded-md border border-primary-500 bg-primary-500 px-3 py-1.5 font-semibold text-white hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {savingPending && <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />}
                      {savingPending ? "Saving…" : pendingOps.length ? `Save (${pendingOps.length})` : "Save"}
                    </button>
                  )}

                  {/* The whole legend, not just the blue one. "What does the
                      yellow mean again?" is the question this screen gets
                      asked most, and the strip above the line items only has
                      room for three counts. */}
                  <ModuleInfoButton
                    ariaLabel="What do the highlighted cells mean?"
                    content={
                      <div className="space-y-2">
                        <p>
                          A cell is highlighted only when it needs a look. Everything else is what the AI read
                          from the invoice.
                        </p>
                        {/* Same component, same swatches, as the quotation grid.
                            The wording stays this page's: pink means "missing or
                            wrong" on both screens, but only here does it stop a
                            sync from running. */}
                        <CellHighlightLegend
                          items={[
                            {
                              level: 'error',
                              meaning: (
                                <>
                                  missing or wrong. Fix these; some stop "Confirm &amp; sync to Inventory"
                                  from running.
                                </>
                              ),
                            },
                            {
                              level: 'warn',
                              meaning: (
                                <>
                                  the AI guessed it. Check it against the invoice and edit anything wrong;
                                  Confirm &amp; sync records the rest as checked.
                                </>
                              ),
                            },
                            {
                              level: 'manual',
                              meaning: <>a person already typed or confirmed it.</>,
                            },
                          ]}
                          footer={
                            <>
                              Hover any highlighted cell to see why.
                              {!isReadOnly &&
                                " Edits save only when you click Save, and nothing reaches Inventory until you press Confirm & sync."}
                            </>
                          }
                        />
                      </div>
                    }
                  />

                  {/* UC5 — the only action that pushes this invoice's lines into
                      Inventory and the Master Catalogue. Gated server-side on the
                      lines reconciling with the invoice's own grand total, and
                      refuses to run a second time — see confirmVendorInvoiceInventory. */}
                  {inv.inventoryConfirmedAt ? (
                    <span
                      title={`${wording.confirmedLabel} ${formatDate(inv.inventoryConfirmedAt)}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"
                    >
                      {wording.confirmedChip}
                    </span>
                  ) : (
                    <button
                      onClick={async () => {
                        // Two refusals before the server is ever asked, each
                        // with its own tone: nothing is wrong with the invoice
                        // when the only problem is that Save has not been
                        // pressed, and saying "does not reconcile" in that case
                        // sent people hunting for a figure that was already right.
                        if (pendingOps.length) {
                          // Short on purpose. A reviewer meets these dialogs
                          // several times an hour, and a paragraph explaining
                          // the mechanism is a paragraph they stop reading —
                          // after which the next one, which DOES matter, is
                          // skipped too. Title = what happened. Summary = the
                          // one thing to do. Nothing else.
                          showDialog({
                            tone: "blocked",
                            title: `Save your ${pendingOps.length} unsaved edit${
                              pendingOps.length === 1 ? "" : "s"
                            } first`,
                            summary: "Confirm & sync reads the saved invoice.",
                            confirmLabel: "Got it",
                          });
                          return;
                        }

                        // D86: no vendor linked, no confirm (the server refuses
                        // it too, VENDOR_NOT_LINKED). Asked before the change
                        // cards, so nobody reads a preview of a sync that will
                        // be refused. The banner at the top says so as soon as
                        // the invoice opens; this is the backstop.
                        if (!hasLinkedVendor(inv)) {
                          showDialog(vendorNotLinkedDialog(inv));
                          return;
                        }

                        const outstanding = review.issues.filter((i) => i.level === "error");
                        if (outstanding.length) {
                          showDialog({
                            tone: "error",
                            title: `${outstanding.length} thing${
                              outstanding.length === 1 ? "" : "s"
                            } to fix before this can be sent`,
                            // The list below IS the message. A summary saying
                            // "its lines do not come to its own grand total"
                            // restates the items in worse detail, and the
                            // reassurance that nothing was changed belongs on
                            // the screen once, not on every refusal.
                            items: outstanding.map((i) => ({ label: i.title, text: i.message })),
                            confirmLabel: "Got it",
                          });
                          return;
                        }

                        // THE CHANGE CARD FIRST: what this will do to the
                        // Master Catalogue, shown before it does it, from a dry
                        // run of the sync itself. The plain confirmation below
                        // still stands for anyone the preview is not available
                        // to — it needs Products view, because the cards show
                        // catalogue costs (Preet, 2026-09-10).
                        // A company expense changes no catalogue row, so it has no change card.
                        if (wording.stocks && (await openSyncPreview(inv._id))) return;

                        const lineCount = lineItems.length;
                        showDialog({
                          tone: "confirm",
                          title: wording.confirmQuestionTitle(lineCount),
                          // The three-item breakdown said what "sync to
                          // Inventory" means to someone who had never pressed
                          // it — which is nobody, by the second week. What has
                          // to survive is the one fact that makes this button
                          // different from every other: it happens once.
                          summary: wording.confirmQuestionBody,
                          confirmLabel: wording.confirmQuestionButton,
                          cancelLabel: "Cancel",
                          busyLabel: "Syncing…",
                          // The amber cells this invoice still carries. Pressing
                          // Confirm & sync records them as checked, which is
                          // what teaches the item memory at reviewer weight —
                          // see the comment in runInventorySync. Computed here
                          // rather than in the handler because the flag rules
                          // are client-side and this is where the lines are.
                          onConfirm: () =>
                            runInventorySync(
                              inv._id,
                              false,
                              collectFlaggedCells(lineItems, ex?.validation, {
                                convention: review.overcharge.convention,
                                confirmed: reviewConfirmedPaths,
                                stocks: wording.stocks,
                              }).confirmable.map((c) => c.path)
                            ),
                        });
                      }}
                      // Also off while the change cards load or are open: the
                      // panel carries its own Confirm, and a second click here
                      // only re-fetched the preview under the reviewer.
                      // D94 (owner, 2026-10-06): off while the split question is open, so
                      // nobody stocks invoice 1 and forgets the rest. The server refuses too.
                      // D86 (QA retest item 30, INV-PSS-1004): the banner above already SAYS
                      // this invoice "cannot be confirmed" with no vendor linked, and the
                      // onClick below already has a backstop dialog for it — but the button
                      // itself stayed a solid, clickable green button, disagreeing with its
                      // own banner. Disabled here too, same source of truth (`hasLinkedVendor`)
                      // as the banner and the Item memory button already use.
                      disabled={confirmingInventory || syncPreviewFor === inv._id || splitQuestionOpen(inv.splitProposal) || !hasLinkedVendor(inv)}
                      title={
                        !hasLinkedVendor(inv)
                          ? "Link a vendor first — this invoice is not linked to a vendor in the vendor list."
                          : splitQuestionOpen(inv.splitProposal)
                          ? "Answer the split question first"
                          : wording.confirmButtonTitle
                      }
                      className="inline-flex items-center gap-2 rounded-md border border-primary-500 bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {(confirmingInventory || (syncPreviewFor === inv._id && syncPreviewLoading)) && (
                        <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                      )}
                      {confirmingInventory
                        ? "Syncing…"
                        : syncPreviewFor === inv._id && syncPreviewLoading
                        ? "Checking…"
                        : wording.confirmButton}
                    </button>
                  )}

                  {/* One ship for the whole invoice: the same dialog as the list row's "+ Assign vessel" (client
                      order, vessel, sticker colour). Once set, the vessel tag shows here, and clicking it changes
                      it. A split invoice names its vessels in "Vessels" below instead, so this stays out of the way. */}
                  {!inv?.vesselAllocations?.length &&
                    (inv?.assignment?.vesselName ? (
                      <VesselPill
                        name={inv.assignment.vesselName}
                        color={inv.assignment.stickerColor}
                        title={`Vessel: ${inv.assignment.vesselName} · Sticker: ${inv.assignment.stickerColor || "none"} (click to change)`}
                        onClick={() => setAssignOpenFor(inv._id)}
                      />
                    ) : (
                      <button
                        onClick={() => setAssignOpenFor(inv._id)}
                        title="Assign this whole invoice to one vessel: client order, vessel name and sticker colour"
                        className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Assign vessel
                      </button>
                    ))}

                  {/* D1 — Record which vessels this invoice's goods go to, and
                      release each vessel's share to it. The invoice stays one
                      record; only its allocations change. Disabled on the child
                      invoices the old cloning split left behind — their goods
                      belong to the original. */}
                  {!isExpenseInvoice(inv) && (
                  <button
                    onClick={() => setSplitOpenFor(inv._id)}
                    disabled={!!inv?.splitFromInvoiceId}
                    title={
                      inv?.splitFromInvoiceId
                        ? "This is a leftover split child from the old behaviour — split the original instead"
                        : "Choose which vessels this invoice's goods go to, and send each vessel its share"
                    }
                    className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-800 hover:bg-purple-100 disabled:opacity-50"
                  >
                    ⇢ {inv?.vesselAllocations?.length ? "Vessels" : "Split by vessel"}
                  </button>
                  )}

                  {/* What this supplier's past invoices taught the system —
                      the Pack Unit and Units / Case that arrive already filled
                      in, and the item names a reviewer cleaned up. Reachable
                      from here because this is where a wrong one is noticed.
                      Needs a RESOLVED vendor: the memory is keyed on the
                      supplier, and an unmatched invoice has none.

                      BOTH ids, not just the root stamp. The server resolves the
                      supplier through `itemGroup.resolveInvoiceVendorId`, which
                      reads `vendorId` OR `vendorReconciliation.matchedVendorId`
                      — the same pair accountsPayable and poInvoiceReconciliation
                      read. Asking only for `vendorId` here meant an invoice the
                      server was already learning from and replaying onto had its
                      memory button greyed out, and this panel is the only way to
                      reach a wrong answer without hunting for another invoice
                      that happens to carry the line. (ClickUp 86d478wx5.) */}
                  <button
                    onClick={() =>
                      setMemoryFor({
                        id: String(inv.vendorId || inv?.vendorReconciliation?.matchedVendorId),
                        name: String(ex?.vendor?.name || "this supplier"),
                      })
                    }
                    disabled={!hasLinkedVendor(inv)}
                    title={
                      hasLinkedVendor(inv)
                        ? "See and correct what past invoices from this supplier taught the system"
                        : "This invoice is not matched to a supplier yet, so there is no memory to show"
                    }
                    className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Item memory
                  </button>

                  <button
                    onClick={() => doReprocess(inv._id)}
                    disabled={reprocessId === inv._id}
                    className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {reprocessId === inv._id ? "Reprocessing…" : "Reprocess"}
                    <svg viewBox="0 0 24 24" className={`h-3.5 w-3.5 ${reprocessId === inv._id ? "animate-spin motion-reduce:animate-none" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* D86, said FIRST (QA 2026-10-08): an invoice with no vendor linked cannot be
                  confirmed and synced. It used to be said only by the Confirm & sync click,
                  after the reviewer had already checked every line. */}
              {!inv.inventoryConfirmedAt && !hasLinkedVendor(inv) && (
                <div
                  role="alert"
                  data-testid="vendor-not-linked-banner"
                  className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  <span>
                    <span className="font-semibold">No vendor linked.</span> This invoice cannot be confirmed and
                    synced until it is linked to a vendor in the vendor list.
                  </span>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => openVendorMatch(inv)}
                      className="rounded-md border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                    >
                      Link a vendor now
                    </button>
                  )}
                </div>
              )}

              {/* QA USC-BUG-0061: what the last Confirm & sync did, every time.
                  Full width UNDER the action bar, not inside it: as a w-full
                  child of the button row it wrapped every button onto a second
                  line and left a blank band beside the metadata (2026-09-25). */}
              {wording.stocks && (
                <CatalogueSyncBanner
                  key={inv._id}
                  invoiceId={inv._id}
                  packingAlerts={confirmBanner?.invoiceId === inv._id ? confirmBanner.packingAlerts : undefined}
                  awaitingSync={confirmBanner?.invoiceId === inv._id}
                  onDismiss={() => setConfirmBanner(null)}
                />
              )}

              {reprocessNotice && reprocessNotice.invoiceId === inv._id && (
                <div
                  role="alert"
                  className="w-full whitespace-pre-line rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                >
                  {reprocessNotice.message}
                  <button
                    onClick={() => setReprocessNotice(null)}
                    className="ml-2 underline underline-offset-2 hover:no-underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {saveError && (
                // whitespace-pre-line, QA USC-BUG-0061: assertRequiredLineCells
                // refuses with one "Line 3 (Rice) — Unit Size is empty" per bad
                // row. Without this the newlines collapsed and every row ran
                // together into one unreadable sentence.
                <div
                  role="alert"
                  className="w-full whitespace-pre-line rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                >
                  <span className="font-semibold">Not saved.</span> {saveError}
                  {/* D44 #53: the refusal explains, this is the way forward. */}
                  {saveErrorReturn?.message === saveError && invoicePerms.canCreate && (
                    <button
                      type="button"
                      onClick={() => raiseVendorReturn(saveErrorReturn.invoiceId)}
                      className="ml-2 rounded border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs font-semibold text-purple-700 hover:bg-purple-100"
                    >
                      Raise a vendor return
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSaveError(null);
                      setSaveErrorReturn(null);
                    }}
                    className="ml-2 underline underline-offset-2 hover:no-underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {ex?.confidence?.reasoning && (
                <div className="text-xs text-slate-500 line-clamp-2" title={ex.confidence.reasoning}>
                  <span className="font-medium text-slate-600">Why this score: </span>
                  {ex.confidence.reasoning}
                </div>
              )}

              {/* Error */}
              {/* The sentences live in features/vendorInvoices/processingIssue (the list row reads them too). */}
              {processingIssueText(inv.lastError) && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700">
                  <span className="font-semibold">Issue:</span> {processingIssueText(inv.lastError)}
                </div>
              )}

              {/* All extracted header panels tile into whatever width is available:
                  1 column on a phone, up to 5-6 on a wide desktop — no vertical
                  stack of half-empty rows to scroll past. */}
              {(() => {
                // Falls back to a blue "manually edited" flag when nothing else
                // is wrong with the cell — an error/warning about the value
                // itself always outranks the note about who typed it.
                const fieldFlagOrManual = (p: string): CellFlag | undefined =>
                  review.fieldFlags[p] ||
                  (isManuallyEdited(p)
                    ? { level: "manual", reason: "This value was entered manually — it is not the AI-extracted figure." }
                    : undefined);
                return (
              <div className="grid gap-3 items-start [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
                <KVTable title="Vendor" readOnly={isReadOnly} fieldFlag={fieldFlagOrManual} onSave={(path, value) => stageEdit(path, value)} rows={[
                  { label: "Name", path: "extracted.vendor.name", value: vendor.name },
                  { label: "Address", path: "extracted.vendor.address", value: vendor.address },
                  { label: "VAT", path: "extracted.vendor.vat", value: vendor.vat },
                  { label: "Phone", path: "extracted.vendor.phone", value: vendor.phone },
                  { label: "Email", path: "extracted.vendor.email", value: vendor.email },
                ]} />
                {/* No Buyer panel. We are the buyer on every vendor invoice in
                    this system, so the three fields only ever restated our own
                    name, address and VAT reg back at the reviewer, and took a
                    column away from the panels that do carry a decision.
                    extracted.buyer is still parsed and stored — it is what the
                    house-company guard compares against to catch an invoice
                    that read US as the vendor (see utils/houseCompany). */}
                <KVTable title="Invoice Header" readOnly={isReadOnly} fieldFlag={fieldFlagOrManual} onSave={(path, value) => stageEdit(path, value)} rows={[
                  { label: "Invoice Number", path: "extracted.invoice.invoiceNumber", value: invoice.invoiceNumber, validate: validateHeaderTextLength },
                  // "credit_note" (or Notes citing an ORIGINAL INVOICE) is refused
                  // at Confirm & sync (D33). Editable so a misread invoice can be
                  // put right rather than dead-ending (NEW-4).
                  { label: "Document type", path: "extracted.docType", value: ex.docType },
                  {
                    label: "Invoice Date",
                    path: "extracted.invoice.invoiceDate",
                    value: invoice.invoiceDate,
                    validate: validateHeaderDateText,
                    display: invoiceHeaderDateDisplay(invoice.invoiceDate),
                  },
                  {
                    label: "Due Date",
                    path: "extracted.invoice.dueDate",
                    value: invoice.dueDate,
                    validate: validateHeaderDateText,
                    display: invoiceHeaderDateDisplay(invoice.dueDate),
                    // Suppliers print terms, not a due date. When it was
                    // calculated from the date and the terms, say so — a
                    // calculated date and a printed one must not look identical.
                    hint: invoice.dueDateDerived
                      ? `Calculated from the invoice date and payment terms${
                          invoice.paymentTerms ? ` (${invoice.paymentTerms})` : ""
                        }`
                      : undefined,
                  },
                  { label: "PO number", path: "extracted.invoice.poNumber", value: invoice.poNumber, validate: validateHeaderTextLength },
                  // "as printed" is what the OCR literally read (often a bare "$");
                  // "code" is the resolved ISO code that drives all amount formatting.
                  { label: "Currency (as printed)", path: "extracted.invoice.currency", value: invoice.currency },
                  { label: "Currency code", path: "extracted.invoice.currencyCode", value: invoice.currencyCode },
                  { label: "Payment Terms", path: "extracted.invoice.paymentTerms", value: invoice.paymentTerms },
                  // Incoterms (EXW/FOB/CIF) are a shipping allocation term on
                  // international trade documents. Nothing in this system reads
                  // the field, these are domestic T&T supplier invoices, and it
                  // was empty on every one of them — so it was a row of "empty"
                  // for ops to scroll past on the panel that matters most.
                  { label: "Notes", path: "extracted.invoice.notes", value: invoice.notes },
                ]} />
                <div className="min-w-0 space-y-2">
                {/* Under each figure: read from the document, not found on it, or
                    edited by hand (VI-3). The verdict comes from the server; a
                    staged or saved edit outranks it. And, once a quantity
                    correction has moved it, what the vendor printed (D44 #55). */}
                <KVTable title="Totals" readOnly={isReadOnly} fieldFlag={fieldFlagOrManual} onSave={(path, value) => stageEdit(path, value)} rows={Object.entries(TOTAL_LABELS).map(([key, label]) => ({
                  label,
                  path: `extracted.totals.${key}`,
                  value: totals[key],
                  type: "number" as const,
                  ...totalsEvidenceNote(ex?.validation?.totalsEvidence?.[key], isManuallyEdited(`extracted.totals.${key}`)),
                  note: printedTotalOf(totals, key),
                }))} />
                {/* Was it already paid? (D49.) Asked at upload; corrected here, and a
                    part payment's amount is typed here, until Confirm & sync raises
                    the bill. */}
                <DestinationCard invoice={inv} onSaved={(destination) => applySavedDestination(inv._id, destination)} readOnly={isReadOnly} />
                <PaymentOnReceiptCard invoice={inv} onSaved={() => { void load(); }} readOnly={isReadOnly} />
                {/* D94: a file holding 2+ invoices asks to be split. Anyone who can
                    edit vendor invoices answers; NO deletes the record. */}
                <SplitInvoiceQuestion
                  invoice={inv}
                  canEdit={vendorRights.canEdit && !isReadOnly}
                  onSplit={async () => { await load(); }}
                  onDeleted={async (message) => {
                    setOpenId(null);
                    setPendingEdits({});
                    // Tell the person FIRST: the record is already gone, so a failing list refresh
                    // must not hide that (the panel unmounts on setOpenId, so nothing after an
                    // awaited load() could be relied on to show).
                    showDialog({ tone: "success", title: "Record deleted", summary: message, confirmLabel: "Done" });
                    try {
                      await load();
                    } catch {
                      showDialog({ tone: "success", title: "Record deleted", summary: `${message} The list could not be refreshed: reload the page.`, confirmLabel: "Done" });
                    }
                  }}
                />
                {/* Does this invoice add up? — the reconciled answer.
                    Sits directly under the figures it vouches for, so the eye
                    goes Grand Total → tick without crossing the screen. The
                    printed total is two rows up, so it is not repeated here;
                    the note shows what the LINES come to and that it matches.
                    Its claim is about the ARITHMETIC and nothing else, so it can
                    and does appear alongside the amber panel below: "the lines
                    come to the grand total" and "the payment terms are still in
                    question" are both true at once on plenty of invoices, and
                    withholding this note until every last question was closed
                    was read as the totals check having quietly failed.

                    Three tones, decided by `totalsNote` (utils/lineItemFlags):
                    grey when there is no Grand Total to check against (prod
                    USC-BUG-0023, a cut-off scan shown "Adds up"), amber while a
                    cell is still pink (USC-BUG-0005, 0016), green otherwise.
                    The grey note does not quote what the lines total: typed
                    into the empty Grand Total, that figure would make the check
                    agree with itself. */}
                {(() => {
                  const note = totalsNote(ex, review, reconciled);
                  if (!note) return null;
                  const cur = invoice?.currencyCode;
                  // VI-3 (USC-BUG-0023): a stated total the document does not
                  // show was not read off the paper; on a cut-off scan the model
                  // summed it from these very lines. "The lines match it" then
                  // proves nothing, so the note stops saying "Adds up" and names
                  // what could not be checked. Display only: nothing is refused.
                  // It joins totalsNote's tones: the grey note stands, and a
                  // pink cell still turns the note amber.
                  if (note.tone === "no-grand-total") {
                    return (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 space-y-0.5">
                        <div className="flex items-center gap-1.5 text-slate-700 font-semibold text-xs">
                          <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                          </svg>
                          {"Can't check — no Grand Total on this invoice"}
                        </div>
                        <div className="text-xs text-slate-600 pl-[22px]">
                          There is nothing to compare the lines against. Type the Grand Total from the paper
                          invoice; Confirm &amp; sync waits for it.
                        </div>
                      </div>
                    );
                  }
                  // D32 (PROVISIONAL): amber while a cell is pink OR when the
                  // lines are off by more than rounding explains; red when the
                  // arithmetic check fails. `totalsNote` decides; this paints.
                  // VI-3: a stated total not found on the document also keeps
                  // the note off green, and is named under the heading.
                  const red = note.tone === "does-not-add-up";
                  const amber = note.tone === "pink-remains" || note.tone === "close";
                  const unchecked = uncheckedTotals(ex?.validation, isManuallyEdited);
                  const warn = amber || unchecked.length > 0;
                  const linesTotal = formatInvoiceMoney(review.computed.grandTotal, cur);
                  const offBy = note.gap !== undefined ? `off by ${formatInvoiceMoney(note.gap, cur)} on ${note.gapOn}` : "";
                  const pinkNote = note.pinkCells
                    ? ` — ${note.pinkCells} pink cell${note.pinkCells === 1 ? "" : "s"} still to check`
                    : "";
                  return (
                    <div className={`rounded-xl border px-3 py-2 space-y-0.5 ${red ? "border-rose-200 bg-rose-50" : warn ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
                      <div className={`flex items-center gap-1.5 font-semibold text-xs ${red ? "text-rose-800" : warn ? "text-amber-800" : "text-emerald-800"}`}>
                        <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          {warn || red ? (
                            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                          ) : (
                            <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.415l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 111.414-1.414l2.793 2.793 6.793-6.793a1 1 0 011.414 0z" clipRule="evenodd" />
                          )}
                        </svg>
                        {red
                          ? `Does not add up — lines total ${linesTotal}${offBy ? `, ${offBy}` : ""}`
                          : note.tone === "close"
                          ? `Close — lines total ${linesTotal}, ${offBy}${pinkNote}`
                          : unchecked.length > 0
                          ? `Lines total ${linesTotal}, but the printed totals could not be checked${pinkNote}`
                          : amber
                          ? `Lines total ${linesTotal}, matching the Grand Total${pinkNote}`
                          : `Adds up — lines total ${linesTotal}, matching the Grand Total`}
                      </div>
                      {unchecked.length > 0 && (
                        <div className="text-xs text-amber-800 pl-[22px]">
                          Not found on the document: {unchecked.join(", ")}. Part of the invoice may be cut off; compare with the paper or re-scan.
                        </div>
                      )}
                      <div className={`text-xs pl-[22px] ${red ? "text-rose-700" : warn ? "text-amber-700" : "text-emerald-700"}`}>
                        {review.computed.lines} line{review.computed.lines === 1 ? "" : "s"}:{" "}
                        {formatInvoiceMoney(review.computed.subTotal, cur)} + {formatInvoiceMoney(review.computed.taxTotal, cur)} tax
                        {/* Named, not silently folded in: a reader checking the
                            sum by eye needs the term that closes the gap. */}
                        {review.addenda.net
                          ? ` ${review.addenda.net > 0 ? "+" : "−"} ${formatInvoiceMoney(Math.abs(review.addenda.net), cur)} freight/discount`
                          : ""}
                        {note.tone === "close"
                          ? ". Rounding explains a cent or two, not this — check the tax and totals against the paper before confirming."
                          : amber
                          ? ". Totals can match while one figure is misread, so check the pink cells before confirming."
                          : ""}
                      </div>
                      {/* QA 2026-10-08 (owner: detect it, and say so): the VAT was read as already
                          inside the printed prices, so it was not added on top of them. */}
                      {review.computed.vatInLineTotals && (
                        <div data-testid="vat-in-line-totals-note" className={`text-xs pl-[22px] ${red ? "text-rose-700" : warn ? "text-amber-700" : "text-emerald-700"}`}>
                          Read as VAT-inclusive: the line prices already include the{" "}
                          {formatInvoiceMoney(review.computed.taxTotal, cur)} VAT, so it is not added again.
                          Stock is costed at the printed line totals.
                        </div>
                      )}
                    </div>
                  );
                })()}
                </div>
                {/* No Taxes panel. It restated the supplier's tax SUMMARY block
                    ("VAT / 0% / 0.00") next to a Totals panel that already shows
                    Tax Total, and on the single-rate Trinidad invoices that make
                    up this corpus the two say the same thing. When the extractor
                    only half-read the summary block the panel actively misled: a
                    "0%" row sat beside a real tax total and read as a
                    contradiction — when in fact nothing on this screen was
                    disagreeing with it. The red Tax Total / Grand Total cells
                    come from reviewInvoice (utils/lineItemFlags) comparing
                    extracted.totals against the LINES; they never look at
                    extracted.taxes at all.

                    extracted.taxes is still extracted and stored, and is not
                    dead data: statedStandardRate (utils/invoiceVat.js) reads it
                    to recover the printed rate on a MIXED-rate invoice, where
                    the subtotal-implied rate belongs to no line, and
                    masterCatalogueVendorSync falls back to it. Dropping the
                    panel therefore drops the only place a reviewer could
                    CORRECT that rate — filed as TECH_DEBT #79. */}
              </div>
                );
              })()}

              {/* Overcharge banner.
                  The reported problem, in the client's words: "they bought 10
                  biscuit packs at 10 and the invoice says 120, and they end up
                  paying more". That is not a subtle arithmetic note buried in a
                  panel — it is money leaving the business, so it gets the top of
                  the screen and a figure, before anyone reaches for the totals. */}
              {review.overcharge.total > 0 && (
                <div className="rounded-xl border-2 border-rose-300 bg-rose-50 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-rose-800 font-semibold">
                    <svg className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    This invoice charges {formatInvoiceMoney(review.overcharge.total, invoice?.currencyCode)} more
                    than its own quantities and prices come to
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-rose-700">
                          <th className="py-1 pr-3 font-semibold">Line</th>
                          <th className="py-1 pr-3 font-semibold">Item</th>
                          <th className="py-1 pr-3 font-semibold text-right">Should be</th>
                          <th className="py-1 pr-3 font-semibold text-right">Charged</th>
                          <th className="py-1 font-semibold text-right">Difference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {review.overcharge.lines
                          .filter((l) => l.difference > 0)
                          .map((l, i) => (
                            <tr key={i} className="border-t border-rose-200 text-rose-900">
                              <td className="py-1 pr-3">{l.lineNo}</td>
                              <td className="py-1 pr-3 max-w-[280px] truncate">{l.itemName}</td>
                              <td className="py-1 pr-3 text-right">{formatInvoiceMoney(l.expected, invoice?.currencyCode)}</td>
                              <td className="py-1 pr-3 text-right">{formatInvoiceMoney(l.charged, invoice?.currencyCode)}</td>
                              <td className="py-1 text-right font-semibold">
                                +{formatInvoiceMoney(l.difference, invoice?.currencyCode)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-xs text-rose-700">
                    Checked against each line's own quantity x unit price
                    {review.overcharge.convention === "inclusive" ? ", VAT included" : ", before VAT"} —
                    the form this supplier's other lines are printed in. Query it before paying.
                  </div>
                </div>
              )}

              {/* Have we already got this invoice?
                  Upload dedupe is a hash of the file's bytes, so it only ever
                  catches the identical file being sent twice. The same invoice
                  re-scanned or re-exported hashes differently and arrives as a
                  brand-new one — and the moment it is confirmed, the same goods
                  are stocked in twice with nothing downstream to say so. This is
                  the one screen where a person can still tell.
                  Rose rather than amber when a twin is already in Inventory:
                  that one is not a tidy-up, it is a refusal waiting to happen. */}
              {(inv.duplicateInvoices?.length ?? 0) > 0 && (() => {
                const twins = inv.duplicateInvoices || [];
                const confirmed = twins.filter((t) => t.inventoryConfirmedAt);
                const severe = confirmed.length > 0;
                const tone = severe
                  ? { box: "border-rose-200 bg-rose-50", head: "text-rose-800", foot: "text-rose-700" }
                  : { box: "border-amber-200 bg-amber-50", head: "text-amber-800", foot: "text-amber-700" };
                return (
                  <div className={`rounded-xl border p-4 space-y-2 ${tone.box}`}>
                    <div className={`flex items-center gap-2 font-semibold text-sm ${tone.head}`}>
                      <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      {/* The twin is what is in Inventory, not this invoice: the
                          old wording told the reviewer the opposite. */}
                      {severe
                        ? "An invoice with this number and supplier is already in Inventory"
                        : `Same invoice number and supplier as ${twins.length} other invoice${twins.length === 1 ? "" : "s"}`}
                    </div>

                    {/* VI-5: this invoice beside each match, so a re-scan and a
                        reused number can be told apart (dates, totals, lines,
                        the document itself). */}
                    <DuplicateInvoiceComparison
                      current={{
                        _id: inv._id,
                        invoiceNumber: invoice?.invoiceNumber || "",
                        vendorName: vendor?.name || "",
                        invoiceDate: invoice?.invoiceDate || null,
                        dueDate: invoice?.dueDate || null,
                        currency: invoice?.currencyCode || undefined,
                        subTotal: totals?.subTotal ?? null,
                        taxTotal: totals?.taxTotal ?? null,
                        grandTotal: totals?.grandTotal ?? null,
                        lineCount: lineItems.length,
                        fileName: inv.source?.fileName || "",
                        uploadedAt: inv.createdAt || null,
                        inventoryConfirmedAt: inv.inventoryConfirmedAt || null,
                      }}
                      twins={twins}
                      formatDate={formatDate}
                      formatMoney={formatInvoiceMoney}
                      onOpenDocument={(id) => openInvoiceAttachment({ _id: id }, true)}
                    />

                    <div className={`text-xs ${tone.foot}`}>
                      {severe
                        ? 'Confirm & sync will refuse this invoice, because sending it would count the same goods twice. If it genuinely is a different invoice that happens to share a number, the refusal offers "Confirm anyway".'
                        : "Matched on the invoice number and supplier name, not on the file — so this catches a re-scan or a re-export that the upload check cannot. Only one of these should be sent to Inventory."}
                    </div>
                  </div>
                );
              })()}

              {/* Everything on this invoice that wants a person, one section per
                  KIND of thing — see IssueKind in utils/lineItemFlags.
                  Recomputed from the values on screen every time anything is
                  edited — the previous panel read `extracted.validation`, which
                  is written once at processing time, so it neither cleared when
                  a discrepancy was fixed nor appeared when an edit created one.
                  Full width because it lists lines and needs the room.

                  This is NOT the negation of the green note under the Totals
                  card. That note answers one question, the arithmetic; this panel
                  raises all the others, and the two appear together whenever an
                  invoice adds up but still has something outstanding. */}
              {showReviewPanel && (() => {
                return (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                      <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      {/* "Does not add up" is a claim about the arithmetic, and
                          it is not always the claim being made. A suspect
                          freight sits on an invoice whose figures reconcile
                          perfectly — the number is just in the wrong box — and
                          heading that panel "does not add up" sends a reviewer
                          hunting for a discrepancy that is not there. */}
                      {/* The heading names the WORST kind present, and only the
                          arithmetic kind may claim the invoice does not add up.
                          A suspect freight sits on an invoice whose figures
                          reconcile perfectly — the number is just in the wrong
                          box — and heading that panel "does not add up" sends a
                          reviewer hunting for a discrepancy that is not there. */}
                      {!reconciled
                        ? "This invoice does not add up"
                        : stockIssues.length
                        // Blocking, but not an arithmetic complaint: the goods
                        // cannot be received until the pack is described.
                        ? "This invoice cannot be sent to Inventory yet"
                        : otherIssues.length || paymentTermsFlag
                        // The arithmetic is settled and the green note two cards
                        // up says so. Heading this "check these figures" sent a
                        // reviewer hunting through the totals for a discrepancy
                        // that is not there — the open questions are named in
                        // their own sections below. Unless there is no Grand
                        // Total: then nothing was settled, and "adds up" is the
                        // very claim prod USC-BUG-0023 reported.
                        ? review.stated.grandTotal === null
                          ? "Check the following"
                          : "This invoice adds up, but check the following"
                        : "Check these figures against the document"}
                    </div>

                    {/* One section per kind of problem, each under its own
                        heading. They used to be a single undifferentiated list of
                        sentences, which is why a terms question and a grand total
                        that genuinely did not reconcile looked like the same
                        event to the person reading it. */}

                    {(arithmeticProblems.length > 0 || lineMismatches.length > 0) && (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold text-amber-700">Totals and lines</div>
                        {arithmeticProblems.map((issue, i) => (
                          // whitespace-pre-line: a multi-line issue (one line per
                          // offending invoice line) keeps its line breaks instead
                          // of running together into one sentence.
                          <div key={i} className="text-xs text-amber-800 bg-amber-100 rounded-md px-3 py-1.5 whitespace-pre-line">
                            <span className="font-semibold">{issue.title}:</span> {issue.message}
                          </div>
                        ))}
                        {lineMismatches.map(({ li, i, flags }: any) => (
                          <div key={`m${i}`} className="text-xs text-amber-800 bg-amber-100 rounded-md px-3 py-1.5 flex items-start gap-2">
                            <span className="shrink-0 font-semibold">Line {li?.lineNo ?? i + 1}:</span>
                            <span className="truncate max-w-[220px] text-slate-600">
                              {li?.itemName || li?.description}
                            </span>
                            <span className="ml-auto">{flags.lineTotal.reason}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {pricingIssues.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold text-amber-700">What the supplier charged</div>
                        {pricingIssues.map((issue, i) => (
                          <div key={i} className="text-xs text-amber-800 bg-amber-100 rounded-md px-3 py-1.5 whitespace-pre-line">
                            <span className="font-semibold">{issue.title}:</span> {issue.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {freightIssues.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold text-amber-700">Freight and discounts</div>
                        {freightIssues.map((issue, i) => (
                          <div key={i} className="text-xs text-amber-800 bg-amber-100 rounded-md px-3 py-1.5 whitespace-pre-line">
                            <span className="font-semibold">{issue.title}:</span> {issue.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* VI-2: a vessel name read as a line, a dropped product, a
                        nameless zero line. Warn only; Confirm & sync still runs. */}
                    {documentIssues.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold text-amber-700">Lines against the paper</div>
                        {documentIssues.map((issue, i) => (
                          <div key={i} className="text-xs text-amber-800 bg-amber-100 rounded-md px-3 py-1.5 whitespace-pre-line">
                            <span className="font-semibold">{issue.title}:</span> {issue.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {stockIssues.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-semibold text-amber-700">Before this can go to Inventory</div>
                        {stockIssues.map((issue, i) => (
                          <div key={i} className="text-xs text-amber-800 bg-amber-100 rounded-md px-3 py-1.5 whitespace-pre-line">
                            <span className="font-semibold">{issue.title}:</span> {issue.message}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* "Use vendor's terms" saves the invoice's Payment Terms the
                        way the cell's Save does — the same PATCH, so the due date
                        and the server's re-check follow — rather than staging it. */}
                    <PaymentTermsFlag
                      flag={paymentTermsFlag}
                      vendorReconciliation={inv.vendorReconciliation}
                      readOnly={isReadOnly}
                      // Typed-but-unsaved terms are the reviewer's own answer;
                      // the button must not overwrite them behind their back.
                      blockedReason={
                        savingPending
                          ? "Wait for the save to finish."
                          : "extracted.invoice.paymentTerms" in pendingEdits
                          ? "Save or discard your typed payment terms first."
                          : null
                      }
                      onUseVendorTerms={async (terms) => {
                        // The ref, not `savingPending`: state reaches the button a
                        // render late (USC-BUG-0045).
                        if (savingPendingRef.current || "extracted.invoice.paymentTerms" in pendingEdits) return;
                        setSaveError(null);
                        setSaveErrorReturn(null);
                        try {
                          await savePaths(inv._id, [{ path: "extracted.invoice.paymentTerms", value: terms }]);
                        } catch {
                          // savePaths has already rolled the grid back and set
                          // saveError, whose "Not saved." banner shows above.
                        }
                      }}
                    />

                    {/* What the lines come to, for a reviewer checking the
                        totals by eye. Suppressed once the arithmetic is clean:
                        the green note under the Grand Total already states these
                        same three figures AND that they match, and repeating
                        them inside an amber box is what made a settled invoice
                        read as an accusation about its money. */}
                    {/* D32: the red note under the Grand Total now states the
                        lines' figures when the invoice does not add up, so this
                        panel no longer repeats them. The exception is a missing
                        Grand Total, whose grey note deliberately quotes none. */}
                    {!reconciled && review.stated.grandTotal === null && (
                      <div className="text-xs text-amber-700">
                        The lines come to {formatInvoiceMoney(review.computed.subTotal, invoice?.currencyCode)} before tax,
                        {" "}{formatInvoiceMoney(review.computed.taxTotal, invoice?.currencyCode)} tax,
                        {" "}{formatInvoiceMoney(review.computed.grandTotal, invoice?.currencyCode)} in total.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* WHAT CONFIRM & SYNC WILL CHANGE IN THE MASTER CATALOGUE.
                  Opened by that button, and rendered here — inside this
                  full-screen review, above the lines it is about — rather than
                  as a second dialog over it. */}
              {syncPreviewFor === inv._id && (
                <SyncPreviewPanel
                  preview={syncPreview}
                  loading={syncPreviewLoading}
                  error={syncPreviewError}
                  stale={syncPreviewStale}
                  excluded={syncExcluded}
                  categoryChoices={syncCategories}
                  onCategoryChange={(lineIndex, key) =>
                    setSyncCategories((prev) => ({ ...prev, [lineIndex]: key }))
                  }
                  canCreateCategory={settingsPermission.canEdit}
                  busy={confirmingInventory}
                  onToggle={(lineIndex) =>
                    setSyncExcluded((prev) =>
                      prev.includes(lineIndex)
                        ? prev.filter((n) => n !== lineIndex)
                        : [...prev, lineIndex]
                    )
                  }
                  onCancel={closeSyncPreview}
                  onConfirm={() =>
                    runInventorySync(
                      inv._id,
                      false,
                      // The same amber cells the plain dialog would have
                      // recorded — see the comment in runInventorySync.
                      collectFlaggedCells(lineItems, ex?.validation, {
                        convention: review.overcharge.convention,
                        confirmed: reviewConfirmedPaths,
                        stocks: !isExpenseInvoice(inv),
                      }).confirmable.map((c) => c.path)
                    )
                  }
                />
              )}

              {/* Line Items — Line# and Description stay pinned when the remaining
                  columns have to scroll sideways on a narrow screen.
                  Hidden while the catalogue-change cards above are open: two
                  tables of the same lines, one above the other, is the reason
                  the cards render inside this review rather than as a second
                  dialog over it. "Back to invoice" (closeSyncPreview) brings
                  it back. Nothing the Confirm & sync button needs — the amber
                  cells `collectFlaggedCells` reports above — reads this
                  table's DOM; it reads `lineItems` and `ex?.validation`, both
                  plain data, so hiding the table changes nothing it collects.

                  EXCEPT two moments (the owner, 2026-09-25): the panel's own
                  blocking refusals ("This invoice cannot be sent yet — Line 3
                  — Unit Size…", read the SAME way the panel's own header
                  reads them, `refusals.filter(r => !r.overridable)`) and a
                  confirm run FROM the panel that the server refused (the
                  catch in runInventorySync sets `gateIssues` against this
                  invoice's id). Both name cells that live in THIS table, so
                  hiding it strands the reviewer with no way to fix them. */}
              {(syncPreviewFor !== inv._id ||
                (syncPreview?.invoiceId === inv._id &&
                  syncPreview.refusals.some((refusal) => !refusal.overridable)) ||
                gateIssues?.invoiceId === inv._id) && (
              <SimpleTable title="Line Items" basePath="extracted.lineItems" rows={lineItems}
                storedRows={storedLineItems}
                readOnly={isReadOnly}
                // MC-6: what the item memory holds for THIS product, one click
                // from its line: the supplier's memory, already searched by the
                // key the memory matched the line on (see memorySearchForLine).
                // Only on an invoice matched to a supplier, because the memory is
                // keyed on one (same test as the header's Item memory button).
                rowAction={
                  inv?.vendorId || inv?.vendorReconciliation?.matchedVendorId || (inv?.inventoryConfirmedAt && !isExpenseInvoice(inv))
                    ? (row: any, idx: number) => (
                        <span className="inline-flex items-center gap-1">
                        {inv?.vendorId || inv?.vendorReconciliation?.matchedVendorId ? (
                        <button
                          type="button"
                          onClick={() =>
                            setMemoryFor({
                              id: String(inv.vendorId || inv?.vendorReconciliation?.matchedVendorId),
                              name: String(ex?.vendor?.name || "this supplier"),
                              search: memorySearchForLine(row),
                            })
                          }
                          aria-label={`Item memory for ${row?.itemName || row?.description || "this line"}`}
                          title="What past invoices from this supplier taught the system about this product"
                          className="whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                        >
                          Memory
                        </button>
                        ) : null}
                        {/* VI-7 (D29): on a stocked invoice, packaging is corrected
                            through a previewed action that also restates item
                            memory and the catalogue row. Master Catalogue edit only. */}
                        {inv?.inventoryConfirmedAt && !isExpenseInvoice(inv) ? (
                          <button
                            type="button"
                            disabled={!catalogueRights.canEdit || Object.keys(pendingEdits).length > 0}
                            onClick={() =>
                              setPackagingFor({
                                invoiceId: String(inv._id),
                                invoiceNumber: ex?.invoice?.invoiceNumber || null,
                                lineIndex: idx,
                                line: row,
                              })
                            }
                            aria-label={`Correct packaging for ${row?.itemName || row?.description || "this line"}`}
                            title={
                              !catalogueRights.canEdit
                                ? "Correcting packaging needs Master Catalogue edit rights"
                                : Object.keys(pendingEdits).length > 0
                                  ? "Save your edits first"
                                  : "Correct Pack Unit, Units / Case or Unit Size, and update item memory and the Master Catalogue"
                            }
                            className="whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Packaging
                          </button>
                        ) : null}
                        </span>
                      )
                    : undefined
                }
                // A question for the line, on the line, answered through the same
                // staged edit as any cell and saved with the rest.
                rowNote={(row: any, idx: number) => {
                  // "Check the packing" (D29 / MC-5): the server's alerts for this
                  // saved line, shown beside whichever question the line asks.
                  const packingAlerts = previewInv.packingAlerts?.[idx];
                  const packing = packingAlerts?.length ? (
                    <PackingAlertNote
                      alerts={packingAlerts}
                      lineNo={row?.lineNo ?? idx + 1}
                      canEdit={catalogueRights.canEdit}
                      // "The name says 12": one click stages Units / Case, saved
                      // with the other edits and checked like a typed one.
                      onUsePackCount={
                        isReadOnly ? undefined : (count) => stageEdit(`extracted.lineItems.${idx}.splitPackCount`, count)
                      }
                    />
                  ) : null;
                  // D44 #55, #58: what the vendor printed on a line a person
                  // corrected, and the printed total that did not add up. First,
                  // above whichever question the line asks.
                  const printedFigures = printedFiguresOf(row);
                  const printedFlag = printedLineFlagFor(ex?.validation, row, idx);
                  const corrected =
                    printedFigures || printedFlag ? (
                      <CorrectedLineNote
                        lineNo={row?.lineNo ?? idx + 1}
                        printed={printedFigures}
                        flag={printedFlag}
                        // D77 "No": the flag, like the stored validation, describes the saved line.
                        billedAtEnteredPrice={storedLineItems[idx]?.supplierPrintedCorrectedPrice === false}
                      />
                    ) : null;
                  const withPacking = (question: React.ReactNode) => {
                    const rest =
                      question && packing ? (
                        <>
                          {question}
                          {packing}
                        </>
                      ) : (
                        question || packing
                      );
                    return corrected && rest ? (
                      <>
                        {corrected}
                        {rest}
                      </>
                    ) : (
                      corrected || rest
                    );
                  };
                  // How to read a Unit Size like "20ct*1.5oz" (prod USC-BUG-0030).
                  if (lineUnitSize(row).needsAnswer) {
                    return withPacking(
                      <UnitSizeReadingPrompt
                        line={row}
                        lineNo={row?.lineNo ?? idx + 1}
                        readOnly={isReadOnly}
                        onAnswer={(answer) => stageEdit(`extracted.lineItems.${idx}.splitUnitSizeReading`, answer)}
                      />
                    );
                  }
                  // "Are these fixed 2 KG packs?" on a catch-weight line (prod
                  // USC-BUG-0028). The server asks it, and only of a Unit Size
                  // that is already read, so the two questions take turns.
                  const fixedPack = pendingFixedPackWeightQuestion(row, idx, previewInv.fixedPackWeightQuestions);
                  // D65 — Units / Case still needs proving: a pack of one with
                  // no size stated, or a count that matches a size printed on
                  // the same line. Live off the row, same as the Unit Size
                  // question above — no server round trip, so it clears itself
                  // the moment `packCountConfirmed` is staged or the line is
                  // fixed another way (a real size typed in, the count edited).
                  const packCountIssue = packCountGuardIssue(row);
                  return withPacking(
                    fixedPack ? (
                      <FixedPackWeightPrompt
                        question={fixedPack}
                        lineNo={row?.lineNo ?? idx + 1}
                        readOnly={isReadOnly}
                        onAnswer={() => stageEdit(`extracted.lineItems.${idx}.splitUnitSizeFixed`, true)}
                      />
                    ) : packCountIssue ? (
                      <PackCountConfirmPrompt
                        issue={packCountIssue}
                        // `num`, not `toNum`: `onAnswer` stages this figure as
                        // `packCountConfirmed`, and it has to equal what
                        // `packCountConfirmedMatches` (server-side rule) reads
                        // off `splitPackCount` or the confirmation never
                        // matches and the refusal never clears.
                        packCount={num(row?.splitPackCount) ?? 0}
                        lineNo={row?.lineNo ?? idx + 1}
                        readOnly={isReadOnly}
                        onAnswer={(count) =>
                          stageEdit(`extracted.lineItems.${idx}.packCountConfirmed`, count)
                        }
                      />
                    ) : null
                  );
                }}
                // Straight from the flag list, so the star and the red cell can
                // never disagree about which columns are compulsory.
                requiredKeys={isExpenseInvoice(inv) ? MONEY_REQUIRED_CELL_KEYS : REQUIRED_LINE_CELL_KEYS}
                // D77: a Unit Price that makes a printed line add up asks whether the
                // supplier printed it, and the answer goes in the same save.
                onSave={async (path, value) =>
                  stageEdits(
                    await lineEditOps(path, value, {
                      storedRows: storedLineItems,
                      rows: lineItems,
                      inStock: !!inv.inventoryConfirmedAt,
                      ask: confirm,
                    })
                  )
                }
                onSaveMany={(ops) => stageEdits(ops)}
                // Before Confirm & sync only, and only for someone who may delete
                // a vendor invoice (prod USC-BUG-0021; TECH_DEBT #74): VENDORS delete,
                // the right the remove route checks, so nobody is offered a 403.
                onRemoveRow={
                  !isReadOnly && !inv.inventoryConfirmedAt && vendorRights.canDelete
                    ? (row, idx) => removeLine(inv._id, row, idx)
                    : undefined
                }
                confirmCell={(row, key, value) => {
                  // D72: before stock-in a price edit recalculates the line.
                  const conflict = describeLineTotalConflict(row, key, value, {
                    inStock: !!inv.inventoryConfirmedAt,
                  });
                  return conflict
                    ? {
                        title: "This line adds up now, but not with that change",
                        summary: conflict,
                        confirmLabel: "Use this value",
                        cancelLabel: "Keep the old value",
                      }
                    : null;
                }}
                scrollClass="max-h-[min(58vh,620px)] max-w-full"
                cellFlag={(() => {
                  // One pass per line, not one per cell: the checks look at the
                  // whole line anyway, and the grid asks about a dozen columns.
                  const cache = new Map<any, ReturnType<typeof getLineItemFlags>>();
                  const ctx = { convention: review.overcharge.convention, stocks: !isExpenseInvoice(inv) };
                  // The server's verdict on THIS invoice, if the last confirm
                  // refused it. Matched on id so a refusal never paints another
                  // invoice's rows.
                  const gate =
                    gateIssues && gateIssues.invoiceId === inv._id ? gateIssues.byLine : null;
                  const manualFlag: CellFlag = {
                    level: "manual",
                    reason: "This value was entered manually — it is not the AI-extracted figure.",
                  };
                  return (row: any, key: string, idx: number) => {
                    let flags = cache.get(row);
                    if (!flags) {
                      flags = getLineItemFlags(row, ex?.validation, {
                        ...ctx,
                        gateIssue: gate?.get(idx) || null,
                        index: idx,
                      });
                      cache.set(row, flags);
                    }
                    // Unit Size is one column standing for two stored fields, so
                    // it has to show whichever of them is in a worse state —
                    // otherwise a bad stored unit has nowhere left to appear.
                    const flag =
                      key === "splitUnitSize" ? worstFlag(flags.splitUnitSize, flags.splitItemUow) : flags[key];
                    // Both stored fields where the column stands for two, for
                    // the same reason `worstFlag` reads both above: Unit Size
                    // can be flagged through either of them, so it can be
                    // edited or confirmed through either of them.
                    const cellPaths =
                      key === "splitUnitSize"
                        ? [`extracted.lineItems.${idx}.splitUnitSize`, `extracted.lineItems.${idx}.splitItemUow`]
                        : [`extracted.lineItems.${idx}.${key}`];

                    // An amber cell a person has already vouched for is not
                    // still asking to be confirmed. It goes blue like a manual
                    // edit — both mean "a person has dealt with this" — and
                    // keeps the original reason on hover, so why it was ever
                    // flagged is still answerable months later.
                    //
                    // Only warnings. An error is a figure that is WRONG, not
                    // one that is merely unproven, and no amount of vouching
                    // makes a line that does not add up add up.
                    if (flag?.level === "warn" && cellPaths.some((p) => confirmedSet.has(p))) {
                      return {
                        level: "confirmed",
                        reason: `Checked against the document and confirmed by a reviewer.\n\nIt was flagged because: ${flag.reason}`,
                      } as CellFlag;
                    }
                    if (flag) return flag;

                    // Case weights that do not add up to the quantity billed.
                    // A warning, never an error: the money is fine, and the
                    // invoice can still be confirmed — what is lost is the
                    // ability to pick this line by case.
                    if (key === "caseWeights" && row?.caseWeightCheck?.ok === false) {
                      return {
                        level: "warn",
                        reason:
                          `These ${row.caseWeightCheck.count} case weights add up to ` +
                          `${row.caseWeightCheck.sum}, but the line bills ` +
                          `${row?.splitQuantity ?? row?.qty}. The goods will be stocked in as a ` +
                          `plain quantity and cannot be picked by case until this is corrected.`,
                      } as CellFlag;
                    }

                    // Nothing wrong with the value — but was it typed by hand?
                    return cellPaths.some((p) => isManuallyEdited(p)) ? manualFlag : undefined;
                  };
                })()}
                subheader={<>
                {/* D44 #20, #53: said once above a stocked invoice's grid, with
                    the way forward, so nobody meets the lock as a refusal. */}
                {inv.inventoryConfirmedAt ? (
                  <StockedQuantityLockNote
                    onRaiseReturn={invoicePerms.canCreate ? () => raiseVendorReturn(inv._id) : undefined}
                  />
                ) : null}
                {(() => {
                  const { errors, warnings } = countFlags(lineItems, ex?.validation, {
                    confirmed: reviewConfirmedPaths,
                    stocks: !isExpenseInvoice(inv),
                  });
                  // Confirmed cells are blue, so they belong with the manual
                  // ones in the count as well as in the colour: to a reviewer
                  // both say "a person has dealt with this".
                  const touched = new Set([
                    ...manuallyEditedPaths.filter((p) => p.startsWith("extracted.lineItems.")),
                    ...Object.keys(pendingEdits).filter((p) => p.startsWith("extracted.lineItems.")),
                    ...reviewConfirmedPaths,
                  ]).size;
                  if (!errors && !warnings && !touched) return null;
                  return (
                    <div className="px-3 py-1.5 border-b border-slate-200 bg-white flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                      {errors > 0 && (
                        <span className="inline-flex items-center gap-1.5">
                          <HighlightSwatch level="error" className="h-2.5 w-2.5" />
                          {errors} missing or unreadable
                          {/* Beside the count rather than only in the header
                              legend: this is the colour that blocks Confirm &
                              sync, and "why is this one pink?" is asked at the
                              row, not at the top of the screen. */}
                          <ModuleInfoButton
                            ariaLabel="What does a pink cell mean?"
                            content={
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <HighlightSwatch level="error" />
                                  <span className="font-semibold">Pink: missing or unreadable</span>
                                </div>
                                <p>
                                  Either the AI could not read this value at all, or what it read is not a
                                  value that can stand: a pack unit like <span className="font-semibold">C-24</span>{" "}
                                  that is not a unit, a figure that cannot be a number, or a line whose
                                  quantity, price and total do not agree.
                                </p>
                                <p>
                                  Pink is also how an <span className="font-semibold">autocorrection</span>{" "}
                                  shows. When the invoice printed something unusable and this supplier&apos;s
                                  item memory already holds the answer a reviewer settled for that product, the
                                  cell is filled in for you and stays pink so you can see it happened. Nothing
                                  else on this screen overrules the document, so it is deliberately the
                                  loudest colour.
                                </p>
                                <p>
                                  Hover the cell. The tooltip says what was printed and what it was corrected
                                  to. If the correction is right, leave it: pressing{" "}
                                  <span className="font-semibold">Confirm &amp; sync to Inventory</span> is all
                                  that is needed. If this delivery really is different, type over it, and the
                                  memory learns what you enter for next time.
                                </p>
                                <p>
                                  A pink cell with no correction offered has to be typed in: the memory has
                                  nothing settled for that product yet, and several of these stop{" "}
                                  <span className="font-semibold">Confirm &amp; sync to Inventory</span> from
                                  running at all.
                                </p>
                              </div>
                            }
                          />
                        </span>
                      )}
                      {warnings > 0 && (
                        <span className="inline-flex items-center gap-1.5">
                          <HighlightSwatch level="warn" className="h-2.5 w-2.5" />
                          {warnings} needs confirming
                        </span>
                      )}
                      {touched > 0 && (
                        <span className="inline-flex items-center gap-1.5">
                          <HighlightSwatch level="manual" className="h-2.5 w-2.5" />
                          {touched} changed or confirmed
                        </span>
                      )}

                      <span className="text-slate-400">
                        Hover a highlighted cell for the reason. Amber cells are settled for you when you
                        press Confirm &amp; sync to Inventory.
                      </span>
                    </div>
                  );
                })()}
                </>}
                columns={[
                  { header: "Line#", key: "lineNo", type: "number", widthClass: "w-14 min-w-[56px]", stickyClass: "left-0" },
                  // Item Name, not Description: the raw description bundles pack
                  // config, unit size, size grade and expiry into one cell. Those
                  // all have their own columns now, so showing the description
                  // again just makes the row unreadable. `description` is still on
                  // the line — it is the audit trail and the inventory matcher's
                  // input — it is reachable on hover rather than rendered, so a
                  // reviewer can check what the cleaner took out (vendor part
                  // numbers among it) and type it back if it was part of the name.
                  {
                    header: "Item Name",
                    key: "itemName",
                    widthClass: "min-w-[220px] max-w-[320px]",
                    stickyClass: "left-14",
                    cellTitle: (row: any) =>
                      row?.description && row.description !== row?.itemName
                        ? `Invoice prints: ${row.description}`
                        : undefined,
                  },
                  // Beside the item name because that is where it was asked
                  // for. Deborah, 2026-09-04: "It should be next to your
                  // description name, it's your item name. Next to it."
                  //
                  // Free text, and no `type` and no `validate` on purpose:
                  // "I can put anything inside here. I can put 9 cases, I can
                  // put the actual weights if I want." Meat is billed by WEIGHT
                  // and picked by BOX and the boxes weigh different amounts, so
                  // there is no number to validate against — "2 boxes @ 20/15lb"
                  // has to be as acceptable as "7 cases". Nothing computes with
                  // this cell; the whole point is that no arithmetic can produce
                  // what it holds.
                  {
                    header: "Boxes / Cases",
                    key: "packingNote",
                    widthClass: "min-w-[130px] max-w-[180px]",
                    headerNote: "free text — not counted",
                    help:
                      "How many physical boxes or cases this line arrived in, for the " +
                      "warehouse to pull.\n\n" +
                      "Filled in for you when the invoice prints it beside the product " +
                      "(\"CHICKEN BREWER (2 bxs)\") or when the case weights add up. " +
                      "Type anything: \"7 cases\", \"2 boxes @ 20/15lb\".\n\n" +
                      "It is never used in a calculation — meat is billed by weight and " +
                      "picked by box, and the boxes are not the same weight. It is copied " +
                      "onto the client invoice and the packing list.",
                  },
                  { header: "Item Code", key: "itemCode", widthClass: "min-w-[100px]" },
                  {
                    header: "Pack Unit",
                    key: "splitUow",
                    widthClass: "min-w-[85px]",
                    help: "How the goods were SOLD — case, box, bag. One row of the invoice is this many packs.",
                    validate: validatePackUnit,
                  },
                  {
                    header: "Quantity",
                    key: "splitQuantity",
                    type: "number",
                    widthClass: "min-w-[80px]",
                    help:
                      "Number of packs charged on this line. Before stock-in, correcting it recalculates " +
                      "the line and the invoice totals; 0 takes the line off the totals. Locked once in stock.",
                    validate: nonNegativeNumber("Quantity"),
                    // D44 #20: in stock, a quantity cannot change (vendor return).
                    locked: isQuantityLocked(inv, "splitQuantity") ? STOCKED_QUANTITY_LOCK_TITLE : undefined,
                    // Quantity x Units per Case = Total Units, so changing the
                    // number of packs has to carry the total with it. The server
                    // does this too (linkPackMath) for edits that never pass
                    // through this grid; writing it here as well is what stops
                    // the cell showing a stale total until the next refetch.
                    writes: (value: any, row: any) =>
                      packMathWrites({ ...row, splitQuantity: value }, "splitQuantity", value),
                  },
                  // Units per Case — the multiplier between the two columns
                  // either side of it. It sat parsed-but-unstored until now, so
                  // a row read "1 case -> 24 units" with the 24-per-case that
                  // produced it nowhere on screen, and the only way to correct a
                  // misread pack size was to overwrite Total Units by hand.
                  {
                    header: "Units / Case",
                    key: "splitPackCount",
                    type: "number",
                    widthClass: "min-w-[100px]",
                    // Paired with Unit Price, which is why both are tinted. The
                    // master catalogue divides the one by the other to get a
                    // cost per item, so a pack size that does not describe the
                    // price beside it prices every future quotation wrong.
                    headerAccent: true,
                    headerNote: "items in one case",
                    help:
                      "How many individual items are inside ONE case — the 24 in " +
                      "\"PENNE PASTA 24X300G\". Quantity x this = Total Units.\n\n" +
                      "It must describe the same pack the Unit Price is for: the Master " +
                      "Catalogue divides one by the other to cost a single item.\n\n" +
                      // D65: the three rules a stored count now has to clear.
                      "LOCKED at 1 for a PCS, BTL, TIN or any other unit that is one item by " +
                      "definition, and for a measure (KG, LBS, L...: one pound is one pound) — " +
                      "change the Pack Unit to CASE or BOX first if the invoice sold these in " +
                      "packs.\n\n" +
                      "REQUIRED for a CASE, BOX, CARTON, PACK, BUNDLE, TRAY, BAG or SACK. A value " +
                      "of exactly 1 needs either a real size in Unit Size (\"3 KG\") or a click on " +
                      "\"Sold as one item\" below the row — a box of 10 ice-cream bars is 10, not 1.\n\n" +
                      "Never the SIZE: a \"5kg Bag\" is Units / Case 1 with Unit Size 5 KG, not " +
                      "Units / Case 5 — Units / Case counts separate items, and a typed count that " +
                      "matches the Unit Size is refused unless confirmed.",
                    validate: nonNegativeNumber("Units / Case"),
                    writes: (value: any, row: any) =>
                      packMathWrites({ ...row, splitPackCount: value }, "splitPackCount", value),
                    // D65: a one-item unit's Units / Case is never typed, only
                    // ever displayed as 1 — the stored cell stays blank so a
                    // fabricated 1 can never be learned by vendor item memory
                    // as a fact about the product (D18, TECH_DEBT #72). Falls
                    // through to the real stored value the moment the row is
                    // NOT locked, which is also true of an error state a
                    // stored count > 1 puts the row in (`lockedFor` below).
                    display: (row: any) => (packCountLockTitle(row) ? 1 : row?.splitPackCount),
                    lockedFor: (row: any) => packCountLockTitle(row),
                  },
                  {
                    header: "Total Units",
                    key: "splitItemUnits",
                    type: "number",
                    widthClass: "min-w-[95px]",
                    help:
                      "Individual items across all packs — this is what goes into stock. " +
                      "Normally Quantity x Units / Case; type over it to state what actually arrived.",
                    validate: nonNegativeNumber("Total Units"),
                    // D44 #20, #52: locked in stock. A pack edit still re-derives
                    // it (Units / Case writes it alongside), so only typing is barred.
                    locked: isQuantityLocked(inv, "splitItemUnits") ? STOCKED_QUANTITY_LOCK_TITLE : undefined,
                    // Deliberately writes nothing else. A person overriding the
                    // total is describing the delivery, not correcting the pack
                    // size — back-solving a per-case figure from it would invent
                    // a fact about the packaging out of a fact about the pallet.
                    writes: (value: any, row: any) => [
                      { key: "splitItemUnits", value },
                      // Mirrors linkPackMath: anything not typed by a person is
                      // no longer what produced this total. A pack count
                      // somebody typed stays as their statement, and the grid
                      // flags the disagreement instead of demoting it.
                      ...(toNum(row?.splitPackCount) !== null &&
                      row?.splitPackCountSource !== "manual" &&
                      row?.splitPackCountSource !== "stale"
                        ? [{ key: "splitPackCountSource", value: "stale" }]
                        : []),
                    ],
                  },
                  // Was two columns, "Unit Size" (500 ML) beside "Unit" (ML) —
                  // the same fact printed twice, and two places to edit one
                  // value. Now one column that writes both fields, so the
                  // stored unit cannot drift from the size it belongs to.
                  {
                    header: "Unit Size",
                    key: "splitUnitSize",
                    widthClass: "min-w-[110px]",
                    help: "Size of ONE item, number and unit together: 500 ML, 14 OZ, 1.5 KG.",
                    validate: validateUnitSize,
                    writes: (value: any, row: any) => {
                      const size = String(value ?? "").trim();
                      // Blanking the size blanks the unit with it: a stored "ML"
                      // with no number is what the two-column layout used to
                      // leave behind, and it reads as data when it is a leftover.
                      const unit = size ? canonicalUnit(unitFromSizeText(size)) || "" : "";
                      return [
                        { key: "splitUnitSize", value: size || null },
                        { key: "splitItemUow", value: unit },
                        // A new Unit Size is a new question (prod USC-BUG-0030):
                        // the answer to how "20ct*1.5oz" was read does not carry
                        // over to other text. Withdrawn here so the question comes
                        // back before Save; the server withdraws it too, for edits
                        // that never pass through this grid (`linkUnitSizeReading`).
                        ...(row?.splitUnitSizeReading ? [{ key: "splitUnitSizeReading", value: null }] : []),
                        // Same for "yes, fixed 2 KG packs" (prod USC-BUG-0028): it
                        // was said about the old size.
                        ...(row?.splitUnitSizeFixed ? [{ key: "splitUnitSizeFixed", value: null }] : []),
                      ];
                    },
                  },
                  // Catch weight. Meat is billed by weight but delivered as
                  // boxes of unequal weight, and the invoice prints every one of
                  // them under the description. Until they are captured, a
                  // vessel asking for 50 kg cannot be told which boxes to pull.
                  // Hidden for now — see SHOW_CASE_WEIGHTS_COLUMN.
                  ...(SHOW_CASE_WEIGHTS_COLUMN
                    ? [
                  {
                    header: "Case Weights",
                    key: "caseWeights",
                    widthClass: "min-w-[190px]",
                    help:
                      "Catch-weight lines only: the weight of EACH case, separated by spaces or " +
                      "commas. They must add up to the quantity billed. Leave blank if the " +
                      "invoice prints no such grid.",
                    display: (row: any) =>
                      Array.isArray(row?.caseWeights) ? row.caseWeights.join(", ") : "",
                    validate: (_value: any, raw: string) => {
                      const text = String(raw ?? "").trim();
                      if (!text) return null;
                      const parts = text.split(/[\s,;]+/).filter(Boolean);
                      if (parts.some((p) => !Number.isFinite(Number(p)) || Number(p) <= 0)) {
                        return "Case weights must be a list of positive numbers.";
                      }
                      if (parts.length < 2) {
                        return "A weight grid is at least two cases.";
                      }
                      return null;
                    },
                    writes: (value: any, row: any) => {
                      const parts = String(value ?? "")
                        .split(/[\s,;]+/)
                        .filter(Boolean)
                        .map(Number)
                        .filter((n) => Number.isFinite(n) && n > 0);
                      if (!parts.length) {
                        return [
                          { key: "caseWeights", value: null },
                          { key: "caseWeightCheck", value: null },
                          { key: "caseWeightSource", value: null },
                        ];
                      }
                      const sum = Math.round(parts.reduce((s, n) => s + n, 0) * 1000) / 1000;
                      const qty = Number(row?.splitQuantity ?? row?.qty ?? 0);
                      const tolerance = Math.max(0.005 * Math.abs(qty), 0.02);
                      const ok = qty > 0 && Math.abs(sum - qty) <= tolerance;
                      return [
                        { key: "caseWeights", value: parts },
                        { key: "caseWeightSource", value: "manual" },
                        {
                          key: "caseWeightCheck",
                          value: {
                            ok,
                            sum,
                            diff: Math.round((sum - qty) * 1000) / 1000,
                            tolerance: Math.round(tolerance * 1000) / 1000,
                            count: parts.length,
                            reason: ok ? "ok" : "sum_mismatch",
                          },
                        },
                      ];
                    },
                  },
                      ]
                    : []),
                  {
                    header: "Unit Price",
                    key: "unitPrice",
                    type: "number",
                    widthClass: "min-w-[90px]",
                    // The price of a CASE — the thing the Quantity column
                    // counts — because the Master Catalogue divides it by
                    // Units / Case to cost one item.
                    //
                    // A supplier who prints per item (West Indian Traders) is
                    // now restated automatically at extraction, so this note no
                    // longer tells anyone to reach for a calculator: the cell
                    // turns amber and says what the paper printed instead.
                    headerAccent: true,
                    headerNote: "price of one case",
                    help:
                      "Price of one case — the same case the Quantity column counts.\n\n" +
                      "The Master Catalogue costs a single item by dividing this by Units / " +
                      "Case, so the two must describe the same case. A supplier who prints a " +
                      "price per item is converted for you; the cell turns amber and shows " +
                      "what the invoice actually printed.",
                    validate: nonNegativeNumber("Unit Price"),
                  },
                  {
                    header: "Discount",
                    key: "discount",
                    type: "number",
                    widthClass: "min-w-[85px]",
                    help:
                      "This line's total discount, in money — 24 cases less 6.50 A CASE shows 156.00, " +
                      "not the 6.50 the invoice prints. Editing the cell still edits the figure as " +
                      "printed (hover a changed cell to see it); a line the total does not prove shows " +
                      "the printed figure unchanged.",
                    // D83 (prod vendor-invoice 3723225809, Blue Waters): `discount`
                    // is stored exactly as printed — a per-case amount, a net price,
                    // a percent — because a quantity correction rescales it as a
                    // rate (vendorLineCorrection.correctLineExtension, D44 #48).
                    // Shown here is the line's TOTAL discount instead, whenever the
                    // printed lineTotal proves one; storage, and what typing into
                    // the cell writes, is untouched. No `stored` override: the
                    // table's own `storedRows` already reads `discount` (this
                    // column's `key`) off the RECORD, exactly the printed figure a
                    // same-value edit must compare against.
                    display: discountCellDisplay,
                    cellTitle: (row: any) => discountCellTitle(row, invoice?.currencyCode),
                  },
                  // Numeric so an edited rate persists as 12.5, not "12.5" — a string
                  // rate reads differently in every downstream consumer.
                  //
                  // The "%" is in the header AND after every value, because the
                  // two tax columns sit side by side and looked alike: a bare
                  // 12.5 in one is a rate, the same 12.5 in the other is money.
                  // A rate typed into the amount column is an expensive silent
                  // mistake — it back-derives a rate nobody stated — and the
                  // only thing separating them was a word in the header.
                  {
                    header: "Tax Rate %",
                    key: "taxRate",
                    type: "number",
                    suffix: "%",
                    widthClass: "min-w-[90px]",
                    help: `VAT percentage — ${
                      typeof vatRatePercent === "number" && Number.isFinite(vatRatePercent)
                        ? vatRatePercent
                        : "the Settings VAT rate"
                    }, not the money. Enter it and the Tax Amount is calculated for you.`,
                    validate: validateTaxRate,
                  },
                  {
                    header: "Tax Amount",
                    key: "taxAmount",
                    type: "number",
                    widthClass: "min-w-[95px]",
                    help: "VAT in money, not the percentage. Enter it and the Tax Rate is calculated for you.",
                    validate: nonNegativeNumber("Tax Amount"),
                  },
                  {
                    header: "Line Total",
                    key: "lineTotal",
                    type: "number",
                    widthClass: "min-w-[95px]",
                    help: "The extended amount as printed. Checked against quantity x unit price.",
                    validate: nonNegativeNumber("Line Total"),
                  },
                  // Expiry, asked for here because this is the last screen where
                  // somebody is holding the paperwork and looking at the goods.
                  // Confirm & sync is refused while a line that will stock
                  // something in has neither a date nor the tick beside it.
                  //
                  // Blank is not an answer: "nobody looked" and "somebody looked
                  // and the box carries no date" lead to opposite decisions when
                  // the stock comes to be picked.
                  {
                    header: "Expiry",
                    key: "expiryDate",
                    type: "date",
                    widthClass: "min-w-[140px]",
                    help:
                      "Best-before on the packaging. Filled in automatically when the invoice " +
                      "prints one; type over it if the box says otherwise.",
                    // Shows the date read off the invoice until somebody types
                    // one. Typing still writes `expiryDate`, so the two never
                    // merge — what a person stated stays separable from what was
                    // read out of a description.
                    //
                    // Except once "No date" is ticked. A reviewer who has said
                    // the goods carry no date should not be left looking at one
                    // in the cell beside the tick; the parsed value stays on the
                    // record as the audit trail, and resolveLineExpiry ignores it
                    // on a ticked line for the same reason.
                    display: (row: any) =>
                      row?.expiryDate || (row?.expiryNotPrinted === true ? "" : row?.expiryDateParsed) || "",
                    // The shown date may be the parsed one, which the gate does
                    // not take as an answer (it re-reads the description). Typing
                    // that same date must still write `expiryDate`, or the line
                    // looks dated and Confirm & sync keeps refusing it. `editSeed`
                    // restates `display` (D83 redesign) so a click-then-blur still
                    // CONFIRMS the parsed date shown, exactly as before that
                    // redesign — Expiry is the one column that needs the fallback
                    // to seed editing, not just view it.
                    editSeed: (row: any) =>
                      row?.expiryDate || (row?.expiryNotPrinted === true ? "" : row?.expiryDateParsed) || "",
                    stored: (row: any) => row?.expiryDate ?? null,
                    cellTitle: (row: any) =>
                      !row?.expiryDate && row?.expiryDateParsed && row?.expiryNotPrinted !== true
                        ? `Read off the invoice${row?.expiryDateParsedMarked ? "" : " (not marked EXP/BB)"} — confirm against the packaging`
                        : undefined,
                    // A date and "no date printed" are two answers to one
                    // question. Typing a date is the later answer, so it takes
                    // the tick off — the pair used to be independently editable
                    // and a line could assert both at once. The server does this
                    // too (linkExpiryAnswer), for the writers that never come
                    // through this grid; here as well so the tick clears on
                    // screen instead of at the next refetch.
                    writes: (value: any, row: any) => [
                      { key: "expiryDate", value },
                      ...(String(value ?? "").trim() !== "" && row?.expiryNotPrinted === true
                        ? [{ key: "expiryNotPrinted", value: false }]
                        : []),
                    ],
                  },
                  {
                    header: "No date",
                    key: "expiryNotPrinted",
                    type: "check",
                    widthClass: "min-w-[70px]",
                    help:
                      "Tick when the goods genuinely carry no expiry date. Records that somebody " +
                      "checked, rather than leaving the column silently blank.",
                    // The other half of the exclusion. Ticking withdraws a date
                    // the line was carrying; UNticking invents nothing — that
                    // leaves the line owing an answer, which is what it does.
                    writes: (value: any, row: any) => [
                      { key: "expiryNotPrinted", value },
                      ...(value === true && String(row?.expiryDate ?? "").trim() !== ""
                        ? [{ key: "expiryDate", value: null }]
                        : []),
                    ],
                  },
                ]}
              />
              )}

            </div>
          </Modal>
        );
      })()}

      {/* What past invoices from this supplier taught the system. Correcting a
          value here changes what arrives filled in on the NEXT invoice, so it is
          the one place a wrong memory can be reached without waiting for an
          invoice that happens to carry the line. */}
      <VendorItemMemoryPanel
        isOpen={!!memoryFor}
        // Reloaded on close: the panel can change the vendor's Payment Terms,
        // which the server pushes onto this vendor's invoices (flag + credit-days
        // snapshot), and the "Use vendor's terms" button reads that snapshot.
        onClose={() => {
          setMemoryFor(null);
          void load();
        }}
        vendorId={memoryFor?.id ?? null}
        vendorName={memoryFor?.name}
        // Set when opened from a line's Memory button, so the panel lands on
        // that product rather than the supplier's whole list.
        initialSearch={memoryFor?.search}
        // The routes gate on PRODUCTS edit/delete and the server refuses either
        // way; passing them here is so a user is not offered a Forget button
        // that answers 403.
        canEdit={catalogueRights.canEdit}
        canDelete={catalogueRights.canDelete}
        // USC-BUG-0031: the supplier's Payment Terms, stored on the vendor and
        // saved through the VENDORS routes, so gated on VENDORS rights.
        termsRights={vendorRights}
      />

      {/* VI-7 (D29) — correct a stocked line's packaging, previewed first. */}
      {packagingFor ? (
        <PackagingCorrectionDialog
          isOpen
          onClose={() => setPackagingFor(null)}
          invoiceId={packagingFor.invoiceId}
          invoiceNumber={packagingFor.invoiceNumber}
          lineIndex={packagingFor.lineIndex}
          line={packagingFor.line}
          formatMoney={formatInvoiceMoney}
          // The line, its Total Units and the memory stamps changed on the server.
          onApplied={() => {
            void load();
          }}
        />
      ) : null}

      {/* D1 — Send one invoice's goods to several vessels, whole lines or by quantity */}
      <Modal
        isOpen={!!splitOpenFor}
        title="Split by Vessel"
        onClose={() => { if (!splitBusy && !allocBusyRow) setSplitOpenFor(null); }}
        maxWidthClass="max-w-5xl"
      >
        {(() => {
          const inv = items.find((i) => i._id === splitOpenFor) as any;
          if (!inv) return <div className="text-sm text-slate-500">Loading...</div>;
          const lineItems: any[] = Array.isArray(inv?.extracted?.lineItems) ? inv.extracted.lineItems : [];
          if (lineItems.length === 0) {
            return <div className="text-sm text-slate-500">This invoice has no extracted line items to split.</div>;
          }
          if (splitLoading) {
            return <div className="text-sm text-slate-500">Loading this invoice's split…</div>;
          }

          const basisFor = (idx: number): SplitBasis => splitBasis[idx] || 'whole';
          const qtyFor = (idx: number, rowId: string) => splitQty[`${idx}:${rowId}`] ?? '';
          const allocatedFor = (idx: number) =>
            splitRows.reduce((sum, r) => sum + (toNum(qtyFor(idx, r.id)) ?? 0), 0);
          const rowLabel = (r: SplitRow, i: number) => r.vessel.trim() || `Vessel ${i + 1}`;

          // What happens to this line. Only 'over' and 'error' block saving —
          // goods left in the warehouse are a normal outcome, not a mistake.
          const lineState = (idx: number): { tone: 'ok' | 'left' | 'idle' | 'error'; text: string } => {
            const basis = basisFor(idx);
            if (basis === 'whole') {
              const target = splitWhole[idx];
              const at = splitRows.findIndex((r) => r.id === target);
              // A row that has since been removed leaves the line unassigned.
              return at >= 0
                ? { tone: 'ok', text: `all → ${rowLabel(splitRows[at], at)}` }
                : { tone: 'idle', text: 'stays in stock' };
            }
            const allocated = allocatedFor(idx);
            const total = lineTotalForBasis(lineItems[idx], basis);
            if (allocated === 0) return { tone: 'idle', text: 'stays in stock' };
            if (total === null || total <= 0) {
              return { tone: 'error', text: `no ${basis === 'units' ? 'Total Units' : 'Quantity'}` };
            }
            const left = total - allocated;
            if (left < -1e-6) return { tone: 'error', text: `${+(-left).toFixed(3)} over` };
            if (left > 1e-6) return { tone: 'left', text: `${+left.toFixed(3)} stays` };
            return { tone: 'ok', text: 'all sent' };
          };

          const states = lineItems.map((_, idx) => lineState(idx));
          const overOrBroken = states.filter((s) => s.tone === 'error').length;
          const staying = states.filter((s) => s.tone === 'idle' || s.tone === 'left').length;

          const linesForRow = (rowId: string) =>
            lineItems.filter((_, idx) =>
              basisFor(idx) === 'whole' ? splitWhole[idx] === rowId : (toNum(qtyFor(idx, rowId)) ?? 0) > 0
            ).length;

          const unnamedRows = splitRows.filter((r) => !r.vessel.trim()).length;
          const emptyRows = splitRows.filter((r) => linesForRow(r.id) === 0).length;

          // The same value is sent to the server and compared against the last
          // save, so "saved" can never mean something different from "what
          // would be sent".
          const splits = buildSplitSpecs(lineItems, splitRows, splitBasis, splitWhole, splitQty);
          const specKey = JSON.stringify(splits);
          const isSaved = splitSavedSpec !== null && splitSavedSpec === specKey;
          const hasSavedSplit = splitRows.some((r) => r.allocationId);

          const canSave =
            !splitBusy &&
            !allocBusyRow &&
            splitRows.length >= 1 &&
            unnamedRows === 0 &&
            emptyRows === 0 &&
            overOrBroken === 0;

          const blockReason = splitBusy
            ? ''
            : unnamedRows > 0
              ? `${unnamedRows} vessel${unnamedRows === 1 ? ' needs' : 's need'} a name`
              : overOrBroken > 0
                ? `${overOrBroken} line${overOrBroken === 1 ? '' : 's'} allocated beyond what the invoice has`
                : emptyRows > 0
                  ? `${emptyRows} vessel${emptyRows === 1 ? ' has' : 's have'} nothing assigned`
                  : '';

          const doSave = async () => {
            setSplitError(null);
            setSplitBusy(true);
            try {
              const res = await splitVendorInvoiceByVessel(String(splitOpenFor), splits);
              const saved = res?.data?.allocations || [];
              // Same order in as out, so the ids land on the rows that produced them.
              setSplitRows(splitRows.map((r, i) => ({
                ...r,
                allocationId: saved[i]?._id ?? r.allocationId,
                sentAt: saved[i]?.inventorySentAt ?? null,
                sentLines: saved[i]?.inventorySentLines ?? null,
                linesToSend: saved[i]?.inventoryLinesToSend ?? null,
              })));
              setSplitSavedSpec(JSON.stringify(splits));
              setSplitStale(null);
              await load();
            } catch (e: any) {
              // unwrapErrorMessage falls back to a delete-specific sentence when
              // handed nothing, so only call it when there is something to unwrap.
              setSplitError(e?.message ? unwrapErrorMessage(e.message) : 'Could not save the split');
            } finally {
              setSplitBusy(false);
            }
          };

          const doClear = async () => {
            if (!window.confirm(
              'Remove the vessel split from this invoice?\n\n' +
              'This only forgets the plan; no stock moves. A vessel already sent must have its send cancelled first.'
            )) return;
            setSplitError(null);
            setSplitBusy(true);
            try {
              await clearVendorInvoiceVesselSplit(String(splitOpenFor));
              await load();
              setSplitOpenFor(null);
            } catch (e: any) {
              setSplitError(e?.message ? unwrapErrorMessage(e.message) : 'Could not clear the split');
            } finally {
              setSplitBusy(false);
            }
          };

          const doAllocate = async (row: SplitRow) => {
            if (!row.allocationId) return;
            setAllocErrByRow({ ...allocErrByRow, [row.id]: '' });
            setAllocBusyRow(row.id);
            try {
              const res = await allocateVendorInvoiceToVessel(String(splitOpenFor), row.allocationId);
              setAllocByRow({ ...allocByRow, [row.id]: res.data });

              // Only call it sent if something actually moved. This flipped the
              // button to "✓ Sent to inventory" — and counted the vessel in the
              // list's "n/m sent" badge — on any 2xx, including the run where
              // every line came back `skipped` for want of stock or a matching
              // item. The skip reasons did render lower down, but the two
              // signals a person actually reads said the opposite. D112.3: read
              // `applied`, not allocated + alreadyAllocated — a line that met a
              // stuck (never applied) movement is already_allocated but moved nothing.
              // D113.1 — the counts the server just stored, so the row says
              // "Partly sent (9 of 10)" rather than "Sent" when a line stayed behind.
              if (vesselSendMovedStock(res.data)) {
                setSplitRows((rows) =>
                  rows.map((x) =>
                    x.id === row.id
                      ? {
                          ...x,
                          sentAt: new Date().toISOString(),
                          sentLines: res.data.applied,
                          linesToSend: res.data.toSend,
                        }
                      : x
                  )
                );
              } else {
                setAllocErrByRow({
                  ...allocErrByRow,
                  [row.id]:
                    'Nothing was sent — every line for this vessel was skipped. See the reasons below.',
                });
              }
              await load();
            } catch (e: any) {
              setAllocErrByRow({
                ...allocErrByRow,
                [row.id]: e?.message ? unwrapErrorMessage(e.message) : 'Could not send to inventory',
              });
            } finally {
              setAllocBusyRow(null);
            }
          };

          return (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">
                This stays <span className="font-semibold">one invoice and one payable</span> — the vessels
                are recorded on it, the way they are written on the paper copy. Send a whole line to one
                vessel, or share it by quantity in packs (Pack Unit) or units (Unit). Anything you do not
                allocate stays in the warehouse.
              </div>

              {splitStale && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  The saved split no longer matches this invoice: {splitStale} Correct it below and save again.
                </div>
              )}

              {/* Destination vessels */}
              <div className="space-y-2">
                {splitRows.map((r, i) => {
                  const tint = splitRowTint(r, i);
                  const cnt = linesForRow(r.id);
                  const setRow = (patch: Partial<SplitRow>) =>
                    setSplitRows(splitRows.map((x) => (x.id === r.id ? { ...x, ...patch } : x)));
                  const result = allocByRow[r.id];
                  const err = allocErrByRow[r.id];
                  const sendState = rowSendState(r);
                  const sendTitle = !r.allocationId
                    ? 'Save the split first — there is nothing recorded to send yet'
                    : !isSaved
                      ? 'You have unsaved changes. Save the split before sending.'
                      : sendState.kind === 'partly'
                        ? `${vesselSendLabel(sendState)}. Pressing again sends only the lines that have not gone yet.`
                        : sendState.kind === 'sent'
                          ? `Already sent. Pressing again allocates only what has not gone yet.`
                          : `Release ${rowLabel(r, i)}'s ${cnt} line${cnt === 1 ? '' : 's'} from the warehouse to the ship`;
                  return (
                    <div
                      key={r.id}
                      className="rounded-lg border-2 p-2 space-y-1.5"
                      style={{ borderColor: tint, backgroundColor: `${tint}14` }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 w-16 shrink-0">
                          Vessel {i + 1}
                        </span>
                        <input
                          type="text"
                          list="usc-split-vessel-list"
                          value={r.vessel}
                          onChange={(e) => setRow({ vessel: e.target.value })}
                          placeholder="Vessel name (required)"
                          className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-slate-300 rounded bg-white"
                        />
                        <select
                          value={r.color}
                          onChange={(e) => setRow({ color: e.target.value })}
                          className="w-32 shrink-0 text-xs px-2 py-1.5 border border-slate-300 rounded bg-white"
                        >
                          <option value="">Sticker colour</option>
                          {allowedColors.map((c) => (<option key={c} value={c}>{c}</option>))}
                        </select>
                        <input
                          type="text"
                          value={r.label}
                          onChange={(e) => setRow({ label: e.target.value })}
                          placeholder="Label (optional)"
                          className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-slate-300 rounded bg-white"
                        />
                        <span className="text-xs text-slate-500 w-14 text-right shrink-0">
                          {cnt} line{cnt === 1 ? '' : 's'}
                        </span>
                        <button
                          type="button"
                          onClick={() => doAllocate(r)}
                          disabled={!r.allocationId || !isSaved || !!allocBusyRow || cnt === 0}
                          title={sendTitle}
                          className="shrink-0 px-2 py-1.5 text-xs font-semibold rounded bg-primary-500 text-white hover:bg-[#08568b] disabled:opacity-40 whitespace-nowrap"
                        >
                          {allocBusyRow === r.id
                            ? 'Sending…'
                            : sendState.kind === 'sent'
                              ? '✓ Sent to inventory'
                              : sendState.kind === 'partly'
                                ? vesselSendLabel(sendState)
                                : 'Send to inventory'}
                        </button>
                        {/* D113.11 — a sent line cannot be re-split; this is the way back (D113.10). */}
                        {r.allocationId && sendState.kind !== 'unsent' && (
                          <VesselCancelSendButton
                            invoiceId={String(splitOpenFor)}
                            allocationId={r.allocationId}
                            vesselName={rowLabel(r, i)}
                            lineCount={sendState.kind === 'partly' ? sendState.sent : r.sentLines ?? cnt}
                            locationLabel={destinationLabel(destinationOf(inv))}
                            disabled={!!allocBusyRow || splitBusy}
                            onBusy={(busy) => setAllocBusyRow(busy ? r.id : null)}
                            onError={(message) => setAllocErrByRow({ ...allocErrByRow, [r.id]: message })}
                            onCancelled={() => {
                              setAllocErrByRow({ ...allocErrByRow, [r.id]: '' });
                              // The last send's line results describe goods that are back in stock now.
                              const { [r.id]: _gone, ...keep } = allocByRow;
                              setAllocByRow(keep);
                              setSplitRows((rows) =>
                                rows.map((x) => (x.id === r.id ? { ...x, sentAt: null, sentLines: null, linesToSend: null } : x))
                              );
                              void load();
                            }}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => setSplitRows(splitRows.filter((x) => x.id !== r.id))}
                          disabled={splitRows.length <= 1 || !!allocBusyRow}
                          title={splitRows.length <= 1 ? 'Keep at least one vessel' : 'Remove this vessel'}
                          className="text-slate-400 hover:text-rose-600 disabled:opacity-30 text-xs px-1 shrink-0"
                        >
                          ×
                        </button>
                      </div>

                      {result && (
                        <div className="text-xs text-slate-700 pl-16">
                          {result.allocated} sent
                          {result.alreadyAllocated > 0 && `, ${result.alreadyAllocated} already gone`}
                          {result.skipped > 0 && (
                            <span className="text-amber-700">, {result.skipped} skipped</span>
                          )}
                          {result.lines
                            .filter((l) => l.status === 'skipped')
                            .map((l) => (
                              <div key={l.lineNumber} className="pl-3 text-amber-700">
                                • line {l.lineNumber} ({l.itemName}): {l.reason}
                              </div>
                            ))}
                        </div>
                      )}
                      {err && <div className="text-xs text-rose-700 pl-16">{err}</div>}
                    </div>
                  );
                })}
                <datalist id="usc-split-vessel-list">
                  {vesselMappings.map((m) => (<option key={m._id} value={m.vesselName} />))}
                </datalist>
                <button
                  type="button"
                  onClick={() => setSplitRows([...splitRows, blankSplitRow()])}
                  className="text-xs text-primary-500 hover:underline"
                >
                  + Add vessel
                </button>
              </div>

              {/* Line-item allocation */}
              <div className="rounded-lg border border-slate-200 max-h-80 overflow-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8">#</th>
                      <th className="px-2 py-1.5 text-left min-w-[170px]">Item</th>
                      <th className="px-2 py-1.5 text-right w-24">Quantity</th>
                      <th className="px-2 py-1.5 text-right w-24">Total Units</th>
                      <th className="px-2 py-1.5 text-center w-28">Split by</th>
                      {splitRows.map((r, i) => (
                        <th key={r.id} className="px-2 py-1.5 text-center w-24">
                          <span className="inline-flex items-center gap-1 justify-center max-w-full">
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: splitRowTint(r, i) }}
                            />
                            <span className="truncate max-w-[70px]">{rowLabel(r, i)}</span>
                          </span>
                        </th>
                      ))}
                      <th className="px-2 py-1.5 text-left w-28">Left in stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((li: any, idx: number) => {
                      const basis = basisFor(idx);
                      const state = states[idx];
                      const wholeRowIdx = splitRows.findIndex((r) => r.id === splitWhole[idx]);
                      const rowTint =
                        basis === 'whole' && wholeRowIdx >= 0
                          ? splitRowTint(splitRows[wholeRowIdx], wholeRowIdx)
                          : null;
                      return (
                        <tr
                          key={idx}
                          className="border-t border-slate-100"
                          style={rowTint ? { backgroundColor: `${rowTint}12` } : undefined}
                        >
                          <td className="px-2 py-1 text-slate-500">{idx + 1}</td>
                          <td className="px-2 py-1">
                            <div className="truncate max-w-[220px]" title={li?.description || ''}>
                              {li?.itemName || li?.description || li?.sku || '—'}
                            </div>
                          </td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">
                            {li?.splitQuantity ?? li?.qty ?? li?.quantity ?? '—'}{' '}
                            <span className="text-slate-400">{li?.splitUow || ''}</span>
                          </td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">
                            {li?.splitItemUnits ?? '—'}{' '}
                            <span className="text-slate-400">{li?.splitItemUow || ''}</span>
                          </td>
                          <td className="px-2 py-1 text-center">
                            <select
                              value={basis}
                              onChange={(e) => setSplitBasis({ ...splitBasis, [idx]: e.target.value as SplitBasis })}
                              className="text-xs px-1 py-0.5 border border-slate-300 rounded bg-white"
                            >
                              <option value="whole">Whole line</option>
                              <option value="packs">Packs</option>
                              <option value="units">Units</option>
                            </select>
                          </td>
                          {splitRows.map((r, i) => (
                            <td key={r.id} className="px-2 py-1 text-center">
                              {basis === 'whole' ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSplitWhole(
                                      splitWhole[idx] === r.id
                                        ? { ...splitWhole, [idx]: '' }
                                        : { ...splitWhole, [idx]: r.id }
                                    )
                                  }
                                  title={
                                    splitWhole[idx] === r.id
                                      ? 'Click again to keep this line in the warehouse'
                                      : `Send this whole line to ${rowLabel(r, i)}`
                                  }
                                  className="h-5 w-5 rounded border text-xs font-bold leading-none text-white"
                                  style={
                                    splitWhole[idx] === r.id
                                      ? { backgroundColor: splitRowTint(r, i), borderColor: splitRowTint(r, i) }
                                      : { backgroundColor: '#fff', borderColor: '#cbd5e1', color: '#cbd5e1' }
                                  }
                                >
                                  {splitWhole[idx] === r.id ? '✓' : ''}
                                </button>
                              ) : (
                                <input
                                  type="number"
                                  min={0}
                                  step="any"
                                  value={qtyFor(idx, r.id)}
                                  onChange={(e) =>
                                    setSplitQty({ ...splitQty, [`${idx}:${r.id}`]: e.target.value })
                                  }
                                  className="w-16 text-xs px-1 py-0.5 border border-slate-300 rounded text-right"
                                />
                              )}
                            </td>
                          ))}
                          <td className="px-2 py-1 text-xs whitespace-nowrap">
                            <span
                              className={
                                state.tone === 'error'
                                  ? 'text-rose-600'
                                  : state.tone === 'ok'
                                    ? 'text-emerald-600'
                                    : 'text-slate-500'
                              }
                            >
                              {state.text}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {splitError && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">{splitError}</div>
              )}

              <div className="flex items-start justify-between gap-4">
                <div className="text-xs text-slate-500 max-w-lg">
                  {blockReason || (
                    <>
                      {staying > 0
                        ? `${staying} line${staying === 1 ? '' : 's'} stay wholly or partly in the warehouse.`
                        : `Every line is fully allocated.`}{' '}
                      Sending to inventory moves stock this invoice already added — it does not receive it
                      again, so what is left over stays on hand. Pressing it twice sends once.
                    </>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {hasSavedSplit && (
                    <button
                      type="button"
                      onClick={doClear}
                      disabled={splitBusy || !!allocBusyRow}
                      className="px-3 py-1.5 text-xs font-semibold rounded border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                    >
                      Clear split
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setSplitOpenFor(null)}
                    disabled={splitBusy || !!allocBusyRow}
                    className="px-3 py-1.5 text-xs font-semibold rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {isSaved ? 'Done' : 'Cancel'}
                  </button>
                  <button
                    type="button"
                    onClick={doSave}
                    disabled={!canSave || isSaved}
                    title={blockReason || (isSaved ? 'Nothing has changed since the last save' : '')}
                    className="px-3 py-1.5 text-xs font-semibold rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                  >
                    {splitBusy ? 'Saving…' : isSaved ? 'Saved' : 'Save split'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Module 1 + B6 — link the client order, then the vessel sticker */}
      <Modal
        isOpen={!!assignOpenFor}
        title="Client Order, Vessel & Sticker Colour"
        onClose={() => {
          if (assignBusy) return;
          setAssignOpenFor(null);
          setAssignVesselName("");
          setAssignColor("");
          setAssignOrder(null);
        }}
        maxWidthClass="max-w-lg"
      >
        {(() => {
          const inv = items.find((i) => i._id === assignOpenFor);
          // Read-only: gates the "Remove assignment" button below. Pre-filling the
          // form now happens in a useEffect keyed on `assignOpenFor` (above) —
          // doing it here re-fired on every render and overwrote live typing.
          const currentVessel = inv?.assignment?.vesselName || "";
          return (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">
                Invoice: <span className="font-medium text-slate-700">{inv?.extracted?.invoice?.invoiceNumber || inv?.source?.fileName || "—"}</span>
              </div>
              {/* B6 — which client order these goods were bought for.
                  Labelled by vessel, because that is what Deborah recognises;
                  stored as the order, because a vessel cannot identify one. */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Client Order <span className="font-normal text-slate-400">(optional)</span>
                </label>
                {assignOrder ? (
                  <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-semibold text-slate-800">
                        {assignOrder.vesselName || "No vessel on this order yet"}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          // Item 49 (QA retest): this cleared the linked ORDER only —
                          // `assignVesselName` is a separate piece of state (typed
                          // directly, or seeded from the order on pick, line below)
                          // that this never touched, so the search box that replaces
                          // the order summary came back empty while the vessel field
                          // right under it still showed the old ship's name.
                          setAssignOrder(null);
                          setAssignVesselName("");
                        }}
                        className="text-xs text-slate-500 hover:text-rose-600"
                      >
                        Change
                      </button>
                    </div>
                    <div className="text-xs text-slate-500">
                      {clientDisplayName(assignOrder.clientName) || "--"}
                      {orderRef(assignOrder) ? ` • ${orderRef(assignOrder)}` : ""}
                    </div>
                    <div className="text-xs mt-1">
                      {assignOrder.clientPoNumber ? (
                        <span className="text-slate-700">
                          Client PO <span className="font-semibold">{assignOrder.clientPoNumber}</span>
                          <span className="text-slate-400"> — this is what goes on our invoice to them</span>
                        </span>
                      ) : (
                        <span className="text-amber-700">
                          No client PO number on this order yet. Add it on the order so it reaches the
                          client invoice.
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {assignOrderLoading ? (
                      <div className="h-9 flex items-center text-xs text-slate-500">Loading the linked order…</div>
                    ) : (
                      <ConfirmedOrderSearchSelect
                        autoFocus
                        onSelect={(order) => {
                          setAssignOrder(order);
                          // The vessel follows the order — nobody types it twice.
                          setAssignVesselName(order.vesselName || "");
                        }}
                      />
                    )}
                    <div className="text-xs text-slate-400 mt-1">
                      Search by vessel, client, RFQ or the client’s PO number. Leave empty to tag the
                      vessel only.
                    </div>
                  </>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Vessel Name</label>
                <input
                  list="usc-vessel-list"
                  value={assignVesselName}
                  onChange={(e) => setAssignVesselName(e.target.value)}
                  placeholder="e.g. Stena Promise"
                  className="w-full h-9 rounded-md border border-slate-200 px-3 text-xs outline-none focus:border-primary-500"
                />
                <datalist id="usc-vessel-list">
                  {vesselMappings.map((m) => (
                    <option key={m._id} value={m.vesselName} />
                  ))}
                </datalist>
                <div className="text-xs text-slate-400 mt-1">
                  {assignOrder
                    ? "This is the order’s vessel. Changing it here corrects the ORDER — the RFQ, the quotation and the purchase orders all follow."
                    : "Tip: existing vessels will auto-suggest their previously-assigned colour."}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Sticker Colour</label>
                <div className="grid grid-cols-6 gap-2">
                  {(allowedColors.length ? allowedColors : Object.keys(COLOR_SWATCH)).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setAssignColor(c)}
                      className={`h-9 rounded-md border-2 flex items-center justify-center text-xs font-semibold capitalize transition-all ${
                        assignColor === c
                          ? "border-primary-500 shadow-sm scale-105"
                          : "border-slate-200 hover:border-slate-400"
                      }`}
                      style={{
                        backgroundColor: COLOR_SWATCH[c] || "#ccc",
                        color: ["yellow", "white", "pink"].includes(c) ? "#374151" : "#fff",
                      }}
                      title={c}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                <div>
                  {currentVessel && (
                    <button
                      onClick={async () => {
                        if (!assignOpenFor || assignBusy) return;
                        if (!window.confirm("Remove vessel assignment from this invoice?")) return;
                        setAssignBusy(true);
                        try {
                          await clearVendorInvoiceAssignment(assignOpenFor);
                          await load();
                          setAssignOpenFor(null);
                          setAssignVesselName("");
                          setAssignColor("");
                          setAssignOrder(null);
                        } catch (e: any) {
                          showApiError(e, "Clear vessel assignment");
                        } finally {
                          setAssignBusy(false);
                        }
                      }}
                      className="text-xs text-rose-600 hover:text-rose-700 font-semibold"
                    >
                      Clear assignment
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { if (!assignBusy) { setAssignOpenFor(null); setAssignVesselName(""); setAssignColor(""); setAssignOrder(null); } }}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!assignOpenFor || assignBusy) return;
                      if (!assignVesselName.trim() || !assignColor) {
                        showDialog({
                          tone: "blocked",
                          title: "Assignment is incomplete",
                          summary: "A vessel needs both a name and a sticker colour before it can be saved.",
                          itemsHeading: "Still missing",
                          items: [
                            ...(!assignVesselName.trim()
                              ? [{ label: "Vessel", text: "Enter the vessel name" }]
                              : []),
                            ...(!assignColor ? [{ label: "Colour", text: "Pick a sticker colour" }] : []),
                          ],
                          confirmLabel: "Got it",
                        });
                        return;
                      }
                      setAssignBusy(true);
                      try {
                        const saved = await assignVendorInvoiceVessel(assignOpenFor, {
                          vesselName: assignVesselName.trim(),
                          stickerColor: assignColor,
                          // B6 — always sent, so clearing the picker really
                          // unlinks. Omitting the key means "leave it alone",
                          // which would make Change/Clear silently do nothing.
                          confirmedOrderId: assignOrder?._id || null,
                        });
                        // D171.3: tagged to an order with one PO from this supplier, so it attached itself;
                        // D185.5: or why it did not, when that is the person's to settle.
                        const attachedPo = saved?.data?.attachedPo;
                        const poNote = attachedPo ? `Attached to ${attachedPo.poNumber}` : notAttachedMessage(saved?.data?.notAttached);
                        if (poNote) {
                          showDialog({ tone: "success", title: "Assignment saved", summary: poNote, confirmLabel: "Done" });
                        }
                        // Refresh data + mappings (vessel might be new)
                        await load();
                        const m = await listVesselStickerMappings();
                        const data = (m as any)?.data || {};
                        setVesselMappings(Array.isArray(data.mappings) ? data.mappings : []);
                        setAssignOpenFor(null);
                        setAssignVesselName("");
                        setAssignColor("");
                        setAssignOrder(null);
                      } catch (e: any) {
                        // Keyed on the envelope's `code`, not the server's prose.
                        showDialog({
                          tone: "blocked",
                          title: "Assignment not saved",
                          summary: vesselAssignmentErrorMessage(e),
                          confirmLabel: "Got it",
                        });
                      } finally {
                        setAssignBusy(false);
                      }
                    }}
                    disabled={assignBusy || !assignVesselName.trim() || !assignColor}
                    className="px-3 py-1.5 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-40"
                  >
                    {assignBusy ? "Saving..." : "Save Assignment"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ? Upload Modal */}
      <Modal
        isOpen={uploadOpen}
        title="Upload Invoice — PDF or Photos"
        onClose={() => {
          if (!uploadBusy) {
            setUploadOpen(false);
            resetUploadModal();
          }
        }}
        maxWidthClass="max-w-2xl"
      >
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            Upload ONE vendor invoice: a <span className="font-medium">PDF</span> or{" "}
            <span className="font-medium">image</span> (jpg/png/webp), or several photos or files that are the
            pages of that one invoice. Put the pages in order below; they are joined into one PDF on our own
            server. We will create an invoice record and trigger parsing.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-slate-500 mb-1">folderTag (optional)</div>
              <input
                value={uploadFolderTag}
                onChange={(e) => setUploadFolderTag(e.target.value)}
                placeholder="e.g. Jan2026-Westco"
                className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm outline-none focus:ring-2 focus:ring-primary-500/25"
              />
            </div>

            <div>
              <div className="text-xs text-slate-500 mb-1">Capture Mode (mobile)</div>
              <select
                value={uploadCaptureMode}
                onChange={(e) => setUploadCaptureMode(e.target.value as any)}
                className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm outline-none"
              >
                <option value="auto">Auto (file picker)</option>
                <option value="camera">Camera (prefer capture)</option>
              </select>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <label htmlFor="upload-destination" className="block text-xs text-slate-500 mb-1">
              Destination
            </label>
            <DestinationPicker
              id="upload-destination"
              value={uploadDestination}
              onChange={setUploadDestination}
              disabled={uploadBusy}
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <PaymentOnReceiptFields
              draft={uploadPayment}
              onChange={setUploadPayment}
              onDateValidityChange={setUploadPaidDateValid}
              showAmount={false}
              disabled={uploadBusy}
              idPrefix="upload"
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs text-slate-500 mb-2">
              Select file(s). You can pick several at once, or add one page at a time (camera: take a page, then
              choose again for the next).
            </div>

            <input
              type="file"
              multiple
              accept={accept}
              disabled={uploadBusy}
              {...(uploadCaptureMode === "camera" ? ({ capture: "environment" } as any) : {})}
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                // Cleared so the same input can add the next page, including a repeat camera capture.
                e.target.value = "";
                const { pages, refused, repeated } = addPickedPages(uploadPages, picked, isAcceptedInvoiceUploadFile);
                setUploadPages(pages);
                const notes = [
                  ...refused.map(
                    (f) =>
                      `"${f.name}" is not a PDF or an image (${f.type || "unrecognised type"}). Choose a PDF, JPG, PNG or WEBP file.`
                  ),
                  ...(repeated.length
                    ? [`Already in the list, not added again: ${repeated.map((f) => f.name).join(", ")}.`]
                    : []),
                ];
                setUploadMsg(notes.length ? notes.join("\n") : null);
              }}
              className="block w-full text-sm text-slate-700"
            />

            {uploadPages.length > 1 && (
              <div className="mt-2 text-xs text-slate-600">
                These {uploadPages.length} files will be joined into ONE invoice, in this order.
              </div>
            )}
            <UploadPageList
              pages={uploadPages}
              disabled={uploadBusy}
              onMove={(index, delta) => setUploadPages((pages) => movePage(pages, index, delta))}
              onRemove={(index) => setUploadPages((pages) => pages.filter((_, i) => i !== index))}
            />
          </div>

          {uploadMsg ? (
            <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 whitespace-pre-line">
              {uploadMsg}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => {
                // While uploading, Cancel stops the upload and keeps the modal open to say so.
                if (uploadBusy) {
                  uploadAbortRef.current?.abort();
                  return;
                }
                setUploadOpen(false);
                resetUploadModal();
              }}
              className="px-3 py-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-sm disabled:opacity-50"
            >
              {uploadBusy ? "Cancel upload" : "Cancel"}
            </button>

            <button
              disabled={uploadBusy || !uploadPages.length}
              onClick={doUploadSingle}
              className="px-3 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm disabled:opacity-50 inline-flex items-center gap-2"
            >
              {uploadBusy && (
                <span className="w-3.5 h-3.5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
              )}
              {uploadBusy ? "Uploading…" : "Upload & Parse"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={vendorMatchOpen && !!openInvoice}
        title={
          currentVendorMatch?.state === "suggested" || currentVendorMatch?.state === "unmatched"
            ? "Review vendor match"
            : "Change vendor"
        }
        onClose={closeVendorMatch}
        maxWidthClass="max-w-4xl"
      >
        {openInvoice && currentVendorMatch ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-800">Invoice vendor details</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-600">
                {currentVendorMatch.matchedVendorName ? (
                  <div className="md:col-span-2">
                    <span className="font-semibold text-slate-700">Filed under:</span> {currentVendorMatch.matchedVendorName}
                  </div>
                ) : null}
                <div><span className="font-semibold text-slate-700">Read from the invoice:</span> {currentVendorMatch.extractedVendor?.name || "-"}</div>
                <div><span className="font-semibold text-slate-700">Email:</span> {currentVendorMatch.extractedVendor?.email || "-"}</div>
                <div><span className="font-semibold text-slate-700">Phone:</span> {currentVendorMatch.extractedVendor?.phone || "-"}</div>
                <div><span className="font-semibold text-slate-700">State:</span> {currentVendorMatch.state || "-"}</div>
              </div>
              <div className="mt-2 text-xs text-slate-500">{currentVendorMatch.note || "Review the suggested vendor match."}</div>
            </div>

            {Array.isArray(currentVendorMatch.candidates) && currentVendorMatch.candidates.length > 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Suggested existing vendors</div>
                    <div className="text-xs text-slate-500">Pick the one that matches this invoice.</div>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {currentVendorMatch.candidates.map((candidate: any) => (
                    <div key={candidate.vendorId} className="rounded-lg border border-slate-200 px-3 py-2 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800">{candidate.vendorName}</div>
                        <div className="text-xs text-slate-500">
                          Score {Math.round((candidate.score || 0) * 100)}% • {candidate.reason}
                          {candidate.email ? ` • ${candidate.email}` : ""}
                          {candidate.phone ? ` • ${candidate.phone}` : ""}
                        </div>
                      </div>
                      <button
                        disabled={vendorMatchSaving}
                        onClick={() => resolveVendorMatch("same_existing", candidate.vendorId || undefined)}
                        className="shrink-0 rounded-md border border-primary-500 px-3 py-1.5 text-xs font-semibold text-primary-600 hover:bg-cyan-50 disabled:opacity-50"
                      >
                        Same vendor
                      </button>
                    </div>
                  ))}
                </div>

                <label className="mt-3 flex items-start gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={vendorSyncDetails}
                    onChange={(e) => setVendorSyncDetails(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Also update this vendor&apos;s phone, address, and credit terms from this invoice,
                    if they differ from what&apos;s on file.
                  </span>
                </label>
              </div>
            ) : null}

            {/* Pick any supplier on the vendor list (D107.3).

                Rendered for every state, not only `unmatched`: it is also how a
                reviewer links an invoice to a supplier the matcher scored below
                the 0.55 candidate floor, and how they correct a suggestion that
                names the wrong company entirely. A supplier that is not on the list
                is created from the dropdown, pre-filled with the printed name, and
                comes back picked; `Link this vendor` then files the invoice under it. */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-sm font-semibold text-slate-800">Link this invoice to a vendor</div>
              <div className="text-xs text-slate-500">
                Pick the supplier from the vendor list. Check here first, so the same company does
                not end up on two rows. A supplier that is not on the list yet can be created from
                the list itself.
              </div>

              <div className="mt-3 flex items-start gap-2">
                <VendorSelect
                  className="min-w-0 flex-1"
                  ariaLabel="Supplier to link this invoice to"
                  placeholder="Select the supplier…"
                  allowClear={false}
                  value={vendorPick}
                  onChange={setVendorPick}
                  // item 31: the name alone used to reach the create-new-vendor popup; the
                  // phone/address/email the panel above already reads off this same invoice
                  // (`currentVendorMatch.extractedVendor`, the same source "Read from the
                  // invoice" above uses) never did, so a reviewer creating a brand-new supplier
                  // retyped three fields they could already see on screen.
                  createSuggestion={{
                    name: vendorMatchName,
                    phone: currentVendorMatch?.extractedVendor?.phone || undefined,
                    address: currentVendorMatch?.extractedVendor?.address || undefined,
                    email: currentVendorMatch?.extractedVendor?.email || undefined,
                  }}
                />
                <button
                  disabled={vendorMatchSaving || !vendorPick.vendorId}
                  onClick={() => resolveVendorMatch("same_existing", vendorPick.vendorId || undefined)}
                  className="h-9 shrink-0 rounded-md bg-primary-500 px-3 text-xs font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
                >
                  Link this vendor
                </button>
              </div>

              {/* The same opt-in as the candidates block, rendered here only when
                  that block is absent — otherwise the one checkbox would appear
                  twice and read as two separate settings. */}
              {!(Array.isArray(currentVendorMatch.candidates) && currentVendorMatch.candidates.length > 0) ? (
                <label className="mt-3 flex items-start gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={vendorSyncDetails}
                    onChange={(e) => setVendorSyncDetails(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Also update this vendor&apos;s phone, address, and credit terms from this invoice,
                    if they differ from what&apos;s on file.
                  </span>
                </label>
              ) : null}

              <div className="mt-4 flex items-center justify-end">
                <button
                  disabled={vendorMatchSaving}
                  onClick={closeVendorMatch}
                  className="rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={!!deleteId}
        title="Are you sure?"
        onClose={() => { setDeleteId(null); setDeleteError(null); setDeleteBlockedByStock(false); }}
        maxWidthClass="max-w-md"
      >
        <div className="space-y-4">
          {/* What this delete will actually do, which depends on whether the
              invoice ever reached Inventory.

              The old sentence was unconditional and pointed the wrong way: "any
              stock it added is returned to inventory" reads as stock going IN,
              when a delete takes back OUT what the invoice put there. On an
              invoice uploaded in error and never confirmed it was worse than
              unclear, it was untrue — nothing has been added to stock before
              Confirm & sync, so `reverseStockInMovements` finds no applied
              PURCHASE_RECEIPT and touches no balance. A reviewer deleting a
              mis-scan was being warned about an inventory correction that was
              never going to happen. */}
          {(() => {
            const target = items.find((i) => i._id === deleteId);
            const confirmedAt = target?.inventoryConfirmedAt || null;
            return (
              <div className="text-sm text-slate-600">
                Deleting this Vendor Invoice is permanent, and the uploaded file is removed.{" "}
                {confirmedAt && isExpenseInvoice(target) ? (
                  <>
                    This invoice was confirmed on {formatDate(confirmedAt)}. It is a company expense, so it
                    added no stock and none is taken back.
                  </>
                ) : confirmedAt ? (
                  <>
                    This invoice was sent to Inventory on{" "}
                    {formatDate(confirmedAt)}, so the stock it added will be taken
                    back out again. If any of those goods have already been issued or transferred,
                    the delete is refused and says which.
                  </>
                ) : (
                  <>
                    It was never sent to Inventory, so no stock levels change.
                  </>
                )}
              </div>
            );
          })()}
          {deleteError && (
            // whitespace-pre-line: the server lists one failing item per line.
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 whitespace-pre-line">
              {deleteError}
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => { setDeleteId(null); setDeleteError(null); setDeleteBlockedByStock(false); }}
              className="px-3 py-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              CANCEL
            </button>
            {deleteBlockedByStock && (
              <button
                onClick={() => deleteId && doDelete(deleteId, { force: true })}
                disabled={deleteBusy}
                title="Remove the invoice and leave its stock in inventory — correct the stock separately"
                className="px-4 py-2 rounded-md border border-[#d24b1c] text-[#d24b1c] text-xs font-semibold hover:bg-orange-50 disabled:opacity-50"
              >
                {deleteBusy ? "Deleting..." : "DELETE ANYWAY"}
              </button>
            )}
            <button
              onClick={() => deleteId && doDelete(deleteId)}
              disabled={deleteBusy || deleteBlockedByStock}
              className="px-4 py-2 rounded-md bg-[#d24b1c] text-white text-xs font-semibold hover:bg-[#bf3f14] disabled:opacity-50"
            >
              {deleteBusy ? "Deleting..." : "DELETE"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Every refusal, failure and one-way confirmation this page raises, in
          one place — see the `dialog` state for why none of them are alerts. */}
      {dialog && (
        <ActionDialog
          key={dialog.id}
          isOpen
          tone={dialog.tone}
          title={dialog.title}
          summary={dialog.summary}
          itemsHeading={dialog.itemsHeading}
          items={dialog.items}
          hint={dialog.hint}
          details={dialog.details}
          onClose={() => setDialog(null)}
          onConfirm={dialog.onConfirm}
          confirmLabel={dialog.confirmLabel}
          cancelLabel={dialog.cancelLabel}
          busyLabel={dialog.busyLabel}
        />
      )}
    </div>
  );
}
